// AUTO-GENERATED from content/items/ — do not edit by hand
import { InventoryItem, ItemRarity, ItemType, CharacterClass } from '../../CharacterTypes';
import type { ItemEquipmentVisualDescriptor } from '../../equipmentVisuals';
import type { ItemTrailStyle } from '../../trailStyle';
import type { ItemLanternStyle } from '../../lanternStyle';
import type { ItemBinding } from '../../inventory/itemBinding';

export interface ItemDefinition extends InventoryItem {
  category: string;
  tags?: string[];
  rarity: ItemRarity;
  requiredLevel: number;
  requiredClass?: CharacterClass;
  sellPrice: number;
  price?: number;
  stock?: number;
  tradeable: boolean;
  /** Plan 23 row 42: whether a bag copy may be dropped (src/shared/inventory/itemBinding.ts). */
  binding: ItemBinding;
  stats: {
    attack?: number; magicAttack?: number; defense?: number;
    hp?: number; mp?: number; strength?: number;
    dexterity?: number; intelligence?: number; luck?: number;
  };
  equipmentSlot?: 'weapon' | 'hat' | 'top' | 'bottom' | 'shoes' | 'gloves' | 'cape' | 'accessory' | 'lantern' | 'flame';
  setName?: string;
  /**
   * Authoritative equipment visual descriptor (BIG-EQUIP-VIS-01).
   * Present only on items with an authored `[<item>.visual]` TOML sub-table.
   * Derived fields (itemSlot/schemaSlot/layer/itemId) are generated from the
   * item's equipment_slot so the descriptor and item slot cannot drift.
   * Consumed by resolveItemEquipmentVisual() in equipmentVisuals.ts.
   */
  visual?: ItemEquipmentVisualDescriptor;
  /**
   * Trail look (lane BD, D46) — present exactly on `shoes` items, which the
   * game presents as the Trail slot. Consumed by trailStyleForItemId().
   */
  trail?: ItemTrailStyle;
  /** Lantern look (lane BK, B26) — present exactly on `lantern` items. Consumed by lanternStyleForItemId(). */
  lantern?: ItemLanternStyle;
  /** Flame look (DJ 2026-10-23) — present exactly on `flame` items. Consumed by flameStyleForItemId(). */
  flame?: ItemLanternStyle;
}

export const ITEM_DATABASE: Record<string, ItemDefinition> = {
  'gold': {
    id: 'gold',
    name: 'Gold',
    type: 'currency' as ItemType,
    category: 'currency',
    quantity: 1,
    maxStack: 999999,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: '💰',
    description: 'The currency of the realm.',
    stats: {},
  },
  'red_potion': {
    id: 'red_potion',
    name: 'Red Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: '🧪',
    description: 'A basic potion that restores 50 HP.',
    stats: { hp: 50 },
  },
  'orange_potion': {
    id: 'orange_potion',
    name: 'Orange Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: '🍊',
    description: 'A stronger potion that restores 150 HP.',
    stats: { hp: 150 },
  },
  'power_elixir': {
    id: 'power_elixir',
    name: 'Power Elixir',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: '⚡',
    description: 'Fully restores HP and MP.',
    stats: { hp: 9999, mp: 9999 },
  },
  'blue_potion': {
    id: 'blue_potion',
    name: 'Blue Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: '💙',
    description: 'Restores 50 MP.',
    stats: { mp: 50 },
  },
  'return_scroll': {
    id: 'return_scroll',
    name: 'Return Scroll',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: '📜',
    description: 'Returns you to the last town you visited.',
    stats: {},
  },
  'elixir_of_speed': {
    id: 'elixir_of_speed',
    name: 'Elixir of Speed',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'potion_speed',
    description: 'Increases movement speed by 20 for 60 seconds.',
    stats: {},
  },
  'elixir_of_fortitude': {
    id: 'elixir_of_fortitude',
    name: 'Elixir of Fortitude',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 8,
    sellPrice: 40,
    tradeable: true,
    binding: 'none',
    icon: 'potion_fortitude',
    description: 'Temporarily increases maximum health by 100.',
    stats: {},
  },
  'flask_of_the_titans': {
    id: 'flask_of_the_titans',
    name: 'Flask of the Titans',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 5,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 12,
    sellPrice: 200,
    tradeable: true,
    binding: 'none',
    icon: 'flask_titans',
    description: 'Increases Strength, Dexterity, Intelligence, and Luck by 5 for 30 minutes.',
    stats: {},
  },
  'flask_of_the_speed_runner': {
    id: 'flask_of_the_speed_runner',
    name: 'Flask of the Speed Runner',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'flask_titans',
    description: 'A quicksilver spiral sealed for the Hollow Coil challenge. Returns you to the Waking Tomb and begins an official timed journey.',
    stats: {},
  },
  'flask_of_the_slow_runner': {
    id: 'flask_of_the_slow_runner',
    name: 'Flask of the Slow Runner',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'flask_titans',
    description: 'A heavy dusk-colored draught for measured combat testing. Returns you to the Waking Tomb and tempers your outgoing damage.',
    stats: {},
  },
  'bandage': {
    id: 'bandage',
    name: 'Linen Bandage',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'bandage_linen',
    description: 'Restores 100 HP over 8 seconds. Can be interrupted.',
    stats: {},
  },
  'antidote': {
    id: 'antidote',
    name: 'Antidote',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 3,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'potion_antidote',
    description: 'Removes poison effects.',
    stats: {},
  },
  'mana_candy': {
    id: 'mana_candy',
    name: 'Mana Candy',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 50,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'candy_mana',
    description: 'A sweet candy that restores 30 MP.',
    stats: {},
  },
  'attack_scroll': {
    id: 'attack_scroll',
    name: 'Attack Scroll',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 10,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'scroll_attack',
    description: 'Temporarily increases attack power by 10 for 5 minutes.',
    stats: {},
  },
  'defense_scroll': {
    id: 'defense_scroll',
    name: 'Defense Scroll',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 10,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'scroll_defense',
    description: 'Temporarily increases defense by 10 for 5 minutes.',
    stats: {},
  },
  'experience_scroll': {
    id: 'experience_scroll',
    name: 'Experience Scroll',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 5,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 8,
    sellPrice: 200,
    tradeable: true,
    binding: 'none',
    icon: 'scroll_exp',
    description: 'Doubles experience gain for 30 minutes.',
    stats: {},
  },
  'guild_summon_stone': {
    id: 'guild_summon_stone',
    name: 'Guild Summon Stone',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 5,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 500,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'stone_summon',
    description: 'Summon all online guild members to your location.',
    stats: {},
  },
  'gm_red_potion': {
    id: 'gm_red_potion',
    name: 'GM Red Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: '🧪',
    description: 'A level-1 GM specimen that restores 50 HP.',
    stats: { hp: 50 },
  },
  'gm_blue_potion': {
    id: 'gm_blue_potion',
    name: 'GM Blue Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: '💙',
    description: 'A level-1 GM specimen that restores 50 MP.',
    stats: { mp: 50 },
  },
  'gm_orange_potion': {
    id: 'gm_orange_potion',
    name: 'GM Orange Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: '🍊',
    description: 'A level-1 GM specimen that restores 150 HP.',
    stats: { hp: 150 },
  },
  'gm_antidote': {
    id: 'gm_antidote',
    name: 'GM Antidote',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 20,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'potion_antidote',
    description: 'A level-1 GM specimen that removes poison effects.',
    stats: {},
  },
  'gm_elixir_of_speed': {
    id: 'gm_elixir_of_speed',
    name: 'GM Elixir of Speed',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 20,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'potion_speed',
    description: 'A level-1 GM specimen that increases movement speed by 20 for 60 seconds.',
    stats: {},
  },
  'gm_elixir_of_fortitude': {
    id: 'gm_elixir_of_fortitude',
    name: 'GM Elixir of Fortitude',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 20,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'potion_fortitude',
    description: 'A level-1 GM specimen that increases maximum health by 100 for 60 seconds.',
    stats: {},
  },
  'gm_flask_of_the_titans': {
    id: 'gm_flask_of_the_titans',
    name: 'GM Flask of the Titans',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 5,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'flask_titans',
    description: 'A level-1 GM specimen that grants 5 Strength, Dexterity, Intelligence, and Luck for 30 minutes.',
    stats: {},
  },
  'gm_power_elixir': {
    id: 'gm_power_elixir',
    name: 'GM Power Elixir',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: '⚡',
    description: 'A level-1 GM specimen that fully restores HP and MP.',
    stats: {},
  },
  'training_sword': {
    id: 'training_sword',
    name: 'Training Sword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: '⚔️',
    description: 'A sturdy training sword for beginners.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'training_sword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 0, primaryColor: '#9aa0a6', accentColor: '#5c4033' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'bronze_helm': {
    id: 'bronze_helm',
    name: 'Bronze Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: '⛑️',
    description: 'A basic bronze helmet.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'bronze_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 0, primaryColor: '#b87333', accentColor: '#5a3420' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 10, hp: 50 },
  },
  'iron_platemail': {
    id: 'iron_platemail',
    name: 'Iron Platemail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 10,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 250,
    tradeable: true,
    binding: 'none',
    icon: '🦺',
    description: 'Heavy iron armor for strong warriors.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'iron_platemail', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 0, primaryColor: '#7f8790', accentColor: '#3d434a' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 25, hp: 100 },
  },
  'apprentice_wand': {
    id: 'apprentice_wand',
    name: 'Apprentice Wand',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 60,
    tradeable: true,
    binding: 'none',
    icon: '🪄',
    description: 'A basic wand for apprentice mages.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'apprentice_wand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 0, primaryColor: '#7b68ee', accentColor: '#d8c9ff' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 20 },
  },
  'apprentice_hat': {
    id: 'apprentice_hat',
    name: 'Apprentice Hat',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 40,
    tradeable: true,
    binding: 'none',
    icon: '🎓',
    description: 'A pointed hat for magic users.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'apprentice_hat', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hat', variant: 0, primaryColor: '#4f46a5', accentColor: '#c4b5fd' } as ItemEquipmentVisualDescriptor,
    stats: { mp: 50, defense: 5 },
  },
  'mage_robe': {
    id: 'mage_robe',
    name: 'Mage Robe',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 8,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 200,
    tradeable: true,
    binding: 'none',
    icon: '🥻',
    description: 'A magical robe that enhances spell power.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'mage_robe', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 0, primaryColor: '#4f46a5', accentColor: '#c4b5fd' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 15, defense: 15, mp: 100 },
  },
  'training_bow': {
    id: 'training_bow',
    name: 'Training Bow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 55,
    tradeable: true,
    binding: 'none',
    icon: '🏹',
    description: 'A simple bow for trainee archers.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'training_bow', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'bow', variant: 0, primaryColor: '#9a6b3f', accentColor: '#d2b48c' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 18, dexterity: 5 },
  },
  'archer_tunic': {
    id: 'archer_tunic',
    name: 'Archer Tunic',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 6,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 120,
    tradeable: true,
    binding: 'none',
    icon: '👕',
    description: 'Light leather tunic for mobility.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'archer_tunic', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'tunic', variant: 0, primaryColor: '#667a3e', accentColor: '#a58b5f' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 18, dexterity: 8 },
  },
  'waking_blade': {
    id: 'waking_blade',
    name: 'Waking Blade',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: '⚔️',
    description: 'A weathered vault blade, balanced for any newly awakened hero.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'waking_blade', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 0, primaryColor: '#b8b8c0', accentColor: '#5a5a66' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 4 },
  },
  'waking_hood': {
    id: 'waking_hood',
    name: 'Waking Hood',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: '🎓',
    description: 'A soft hood left beside the waking slab.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'waking_hood', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 0, primaryColor: '#8a7a5c', accentColor: '#3d3528' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 1 },
  },
  'waking_tunic': {
    id: 'waking_tunic',
    name: 'Waking Tunic',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: '👕',
    description: 'A simple tunic preserved in the dry reaches of the vault.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'waking_tunic', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'tunic', variant: 0, primaryColor: '#9a8a6a', accentColor: '#4a3f2e' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 1, hp: 5 },
  },
  'waking_leggings': {
    id: 'waking_leggings',
    name: 'Waking Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'armor_legs_leather',
    description: 'Flexible leggings made for the climb out of the vault.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'waking_leggings', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 0, primaryColor: '#7a6a4c', accentColor: '#3a3022' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 1 },
  },
  'waking_boots': {
    id: 'waking_boots',
    name: 'Waking Wisp Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'trail_waking',
    description: 'The first faint light a waking ghost sheds as it moves. Barely there, but it proves you are.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'waking_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'boots', variant: 0, primaryColor: '#6a5a44', accentColor: '#2e2618' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#e8f0ff', glow: 0.55, size: 14, count: 3, rise: 2 } as ItemTrailStyle,
    stats: { defense: 1 },
  },
  'waking_gloves': {
    id: 'waking_gloves',
    name: 'Waking Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'armor_gloves_cloth',
    description: 'Plain gloves that still carry a trace of candle warmth.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'waking_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 0, primaryColor: '#9a8a6a', accentColor: '#4a3f2e' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 1 },
  },
  'waking_cloak': {
    id: 'waking_cloak',
    name: 'Waking Cloak',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'armor_cape_travelers',
    description: 'A short cloak that smells faintly of rain and old incense.',
    equipmentSlot: 'cape' as const,
    visual: { itemId: 'waking_cloak', itemSlot: 'cape', schemaSlot: 'back', layer: 'back', family: 'cloak', variant: 0, primaryColor: '#5a4a3a', accentColor: '#2a2218' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 1 },
  },
  'waking_charm': {
    id: 'waking_charm',
    name: 'Waking Charm',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'accessory_ring_copper',
    description: 'A small Coil-marked charm found among the vault offerings.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'waking_charm', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'pendant', variant: 0, primaryColor: '#c8a04a', accentColor: '#6a4a1a' } as ItemEquipmentVisualDescriptor,
    stats: { luck: 1 },
  },
  'leather_leggings': {
    id: 'leather_leggings',
    name: 'Leather Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 3,
    sellPrice: 60,
    tradeable: true,
    binding: 'none',
    icon: 'armor_legs_leather',
    description: 'Simple leather leggings.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'leather_leggings', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 0, primaryColor: '#6b4b32', accentColor: '#a97852' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 8, hp: 30 },
  },
  'iron_boots': {
    id: 'iron_boots',
    name: 'Forge Spark Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 75,
    tradeable: true,
    binding: 'none',
    icon: 'trail_forge',
    description: 'Sparks that leapt off an anvil and never quite came down. They climb behind you, bright and short-lived.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'iron_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 1, primaryColor: '#7f8790', accentColor: '#3d434a' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ff8a3d', glow: 0.8, size: 10, count: 5, rise: 6 } as ItemTrailStyle,
    stats: { defense: 6, dexterity: 3 },
  },
  'cloth_gloves': {
    id: 'cloth_gloves',
    name: 'Cloth Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 30,
    tradeable: true,
    binding: 'none',
    icon: 'armor_gloves_cloth',
    description: 'Basic cloth gloves.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'cloth_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 0, primaryColor: '#d6c7aa', accentColor: '#8a7964' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 4, luck: 2 },
  },
  'travelers_cloak': {
    id: 'travelers_cloak',
    name: 'Traveler\'s Cloak',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 8,
    sellPrice: 150,
    tradeable: true,
    binding: 'none',
    icon: 'armor_cape_travelers',
    description: 'A warm cloak for long journeys.',
    equipmentSlot: 'cape' as const,
    visual: { itemId: 'travelers_cloak', itemSlot: 'cape', schemaSlot: 'back', layer: 'back', family: 'cloak', variant: 1, primaryColor: '#5a4a3a', accentColor: '#8a7258' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 12, hp: 40 },
  },
  'steel_sword': {
    id: 'steel_sword',
    name: 'Steel Sword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 8,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_sword_steel',
    description: 'A well-forged steel blade. Noticeably sharper than bronze.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'steel_sword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 1, primaryColor: '#b8c2cc', accentColor: '#4f5963' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 35, strength: 5 },
  },
  'ice_staff': {
    id: 'ice_staff',
    name: 'Ice Staff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 8,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_staff_ice',
    description: 'A staff infused with frost energy.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'ice_staff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 1, primaryColor: '#7dd3fc', accentColor: '#e0f2fe' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 30, intelligence: 8 },
  },
  'hunter_bow': {
    id: 'hunter_bow',
    name: 'Hunter\'s Bow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 8,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_bow_hunter',
    description: 'A reliable bow used by experienced hunters.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'hunter_bow', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'bow', variant: 1, primaryColor: '#6b8e23', accentColor: '#3e4f1d' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 28, dexterity: 8 },
  },
  'iron_chainmail': {
    id: 'iron_chainmail',
    name: 'Iron Chainmail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 6,
    sellPrice: 350,
    tradeable: true,
    binding: 'none',
    icon: 'armor_chainmail_iron',
    description: 'Flexible iron chainmail that balances protection and mobility.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'iron_chainmail', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 1, primaryColor: '#737b84', accentColor: '#42484f' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 20, hp: 50 },
  },
  'copper_ring': {
    id: 'copper_ring',
    name: 'Copper Ring',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 45,
    tradeable: true,
    binding: 'none',
    icon: 'accessory_ring_copper',
    description: 'A simple copper ring with a faint glow.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'copper_ring', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'ring', variant: 0, primaryColor: '#b87333', accentColor: '#e0a06b' } as ItemEquipmentVisualDescriptor,
    stats: { luck: 5, hp: 20 },
  },
  'crystal_blade': {
    id: 'crystal_blade',
    name: 'Crystal Blade',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 2000,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_sword_crystal',
    description: 'A sword forged from crystallized mana. Hums with power.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'crystal_blade', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 2, primaryColor: '#9b7ede', accentColor: '#e6d5ff' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 55, strength: 10, dexterity: 5 },
  },
  'void_staff': {
    id: 'void_staff',
    name: 'Void Staff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 2000,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_staff_void',
    description: 'A staff that channels energy from the void.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'void_staff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 2, primaryColor: '#3b1f5f', accentColor: '#a855f7' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 50, intelligence: 15 },
  },
  'shadow_daggers': {
    id: 'shadow_daggers',
    name: 'Shadow Daggers',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 2000,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_dagger_shadow',
    description: 'Twin daggers that shimmer with shadow energy.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'shadow_daggers', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'dagger', variant: 2, primaryColor: '#2a183b', accentColor: '#9b5de5' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 45, dexterity: 12, luck: 8 },
  },
  'crystal_bow': {
    id: 'crystal_bow',
    name: 'Crystal Bow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 2000,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_bow_crystal',
    description: 'A bow carved from a single massive crystal.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'crystal_bow', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'bow', variant: 2, primaryColor: '#7c6ee6', accentColor: '#d8ccff' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 48, dexterity: 12 },
  },
  'holy_mace': {
    id: 'holy_mace',
    name: 'Holy Mace',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 2000,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_mace_holy',
    description: 'A mace blessed by ancient light. Radiates warmth.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'holy_mace', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'mace', variant: 1, primaryColor: '#f4d06f', accentColor: '#fff4c2' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 40, strength: 8, intelligence: 8 },
  },
  'crystal_helm': {
    id: 'crystal_helm',
    name: 'Crystal Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 1500,
    tradeable: true,
    binding: 'none',
    icon: 'armor_helm_crystal',
    description: 'A helm carved from purple crystal. Light yet protective.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'crystal_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 2, primaryColor: '#7c3aed', accentColor: '#d8b4fe' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 25, hp: 80 },
  },
  'void_robe': {
    id: 'void_robe',
    name: 'Void Robe',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 1500,
    tradeable: true,
    binding: 'none',
    icon: 'armor_robe_void',
    description: 'Woven from void threads. Enhances magical power.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'void_robe', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 2, primaryColor: '#24113d', accentColor: '#8b3fd1' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 15, magicAttack: 20, intelligence: 10, mp: 50 },
  },
  'volcanic_plate': {
    id: 'volcanic_plate',
    name: 'Volcanic Platemail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 12,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'armor_plate_volcanic',
    description: 'Forged in volcanic fire. Radiates intense heat.',
    equipmentSlot: 'top' as const,
    setName: 'volcanic_warrior',
    visual: { itemId: 'volcanic_plate', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 3, primaryColor: '#7f1d1d', accentColor: '#ff6b1a' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 35, hp: 120, strength: 10, attack: 10 },
  },
  'volcanic_helm': {
    id: 'volcanic_helm',
    name: 'Volcanic Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 12,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 4000,
    tradeable: true,
    binding: 'none',
    icon: 'armor_helm_volcanic',
    description: 'A helm tempered by magma. Nearly indestructible.',
    equipmentSlot: 'hat' as const,
    setName: 'volcanic_warrior',
    visual: { itemId: 'volcanic_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 3, primaryColor: '#7f1d1d', accentColor: '#ff6b1a' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 30, hp: 80, strength: 8 },
  },
  'frostweave_robe': {
    id: 'frostweave_robe',
    name: 'Frostweave Robe',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 12,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'armor_robe_frostweave',
    description: 'Woven from enchanted frost threads. Amplifies cold magic.',
    equipmentSlot: 'top' as const,
    setName: 'frostweave_mage',
    visual: { itemId: 'frostweave_robe', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 2, primaryColor: '#67c8f3', accentColor: '#e0f7ff' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 18, magicAttack: 35, intelligence: 15, mp: 80 },
  },
  'frostweave_hat': {
    id: 'frostweave_hat',
    name: 'Frostweave Hat',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 12,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 4000,
    tradeable: true,
    binding: 'none',
    icon: 'armor_hat_frostweave',
    description: 'A hat that crackles with frost energy.',
    equipmentSlot: 'hat' as const,
    setName: 'frostweave_mage',
    visual: { itemId: 'frostweave_hat', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hat', variant: 2, primaryColor: '#67c8f3', accentColor: '#e0f7ff' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 12, magicAttack: 25, intelligence: 12 },
  },
  'shadow_leather': {
    id: 'shadow_leather',
    name: 'Shadow Leather Vest',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 12,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'armor_leather_shadow',
    description: 'Leather infused with shadow essence. Silent and deadly.',
    equipmentSlot: 'top' as const,
    setName: 'shadow_assassin',
    visual: { itemId: 'shadow_leather', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 2, primaryColor: '#241b2f', accentColor: '#6d28a8' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 22, attack: 15, dexterity: 12, luck: 8 },
  },
  'hunters_mail': {
    id: 'hunters_mail',
    name: 'Hunter\'s Chainmail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 12,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'armor_mail_hunter',
    description: 'Light chainmail that doesn\'t impede movement. Perfect for hunters.',
    equipmentSlot: 'top' as const,
    setName: 'hunter_ranger',
    visual: { itemId: 'hunters_mail', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 2, primaryColor: '#5f6f3e', accentColor: '#b08a4a' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 25, attack: 10, dexterity: 15 },
  },
  'holy_plate': {
    id: 'holy_plate',
    name: 'Holy Plate Armor',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 12,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'armor_plate_holy',
    description: 'Blessed armor that shines with holy light.',
    equipmentSlot: 'top' as const,
    setName: 'holy_paladin',
    visual: { itemId: 'holy_plate', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 2, primaryColor: '#d4af37', accentColor: '#fff1a8' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 30, hp: 100, intelligence: 8, strength: 8 },
  },
  'infernal_greatsword': {
    id: 'infernal_greatsword',
    name: 'Infernal Greatsword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 10000,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_sword_infernal',
    description: 'A massive blade that burns with infernal fire. Dropped by the Infernal.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'infernal_greatsword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 3, primaryColor: '#c2410c', accentColor: '#ffb000' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 80, strength: 15, dexterity: 5 },
  },
  'ring_of_power': {
    id: 'ring_of_power',
    name: 'Ring of Power',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 3000,
    tradeable: true,
    binding: 'none',
    icon: 'accessory_ring_power',
    description: 'A ring that pulses with raw magical energy.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'ring_of_power', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'ring', variant: 3, primaryColor: '#6d28d9', accentColor: '#f5d76e' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 10, magicAttack: 10, hp: 50, mp: 30 },
  },
  'boots_of_swiftness': {
    id: 'boots_of_swiftness',
    name: 'Swiftwind Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 8,
    sellPrice: 1200,
    tradeable: true,
    binding: 'none',
    icon: 'trail_swiftwind',
    description: 'A streak of cool wind made visible. The motes lie flat behind you, as if they can barely keep up.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'boots_of_swiftness', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'boots', variant: 3, primaryColor: '#3b82f6', accentColor: '#a7f3d0' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#8fd3ff', glow: 0.8, size: 18, count: 5, rise: 0 } as ItemTrailStyle,
    stats: { defense: 10, dexterity: 10, hp: 30 },
  },
  'leather_vest': {
    id: 'leather_vest',
    name: 'Leather Vest',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 3,
    sellPrice: 80,
    tradeable: true,
    binding: 'none',
    icon: 'armor_vest_leather',
    description: 'A simple leather vest offering basic protection.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'leather_vest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 0, primaryColor: '#7a5230', accentColor: '#c49a6c' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 8, hp: 20 },
  },
  'iron_helm': {
    id: 'iron_helm',
    name: 'Iron Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 120,
    tradeable: true,
    binding: 'none',
    icon: 'armor_helm_iron',
    description: 'A sturdy iron helmet.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'iron_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 1, primaryColor: '#7f8790', accentColor: '#3d434a' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 12, hp: 30 },
  },
  'leather_boots': {
    id: 'leather_boots',
    name: 'Dusk Mote Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 3,
    sellPrice: 60,
    tradeable: true,
    binding: 'none',
    icon: 'trail_dusk',
    description: 'Soft motes the colour of the last hour of daylight. Blacksmith Erik swears they keep a traveller\'s spirits up on long roads.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'leather_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 0, primaryColor: '#7a5230', accentColor: '#c49a6c' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ffb38a', glow: 0.65, size: 16, count: 4, rise: 2 } as ItemTrailStyle,
    stats: { defense: 5, dexterity: 3 },
  },
  'silver_ring': {
    id: 'silver_ring',
    name: 'Silver Ring',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 200,
    tradeable: true,
    binding: 'none',
    icon: 'accessory_ring_silver',
    description: 'A polished silver ring with a faint magical glow.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'silver_ring', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'ring', variant: 1, primaryColor: '#b8c2cc', accentColor: '#e6eef5' } as ItemEquipmentVisualDescriptor,
    stats: { luck: 8, hp: 30, mp: 15 },
  },
  'traveler_gloves': {
    id: 'traveler_gloves',
    name: 'Traveler\'s Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 90,
    tradeable: true,
    binding: 'none',
    icon: 'armor_gloves_traveler',
    description: 'Well-worn gloves that have seen many journeys.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'traveler_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 1, primaryColor: '#8b6f47', accentColor: '#c2a477' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 6, dexterity: 4 },
  },
  'steel_longsword': {
    id: 'steel_longsword',
    name: 'Steel Longsword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_sword_steel',
    description: 'A well-forged steel blade. Reliable in battle.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'steel_longsword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 3, primaryColor: '#aeb8c2', accentColor: '#39434d' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 25, strength: 5 },
  },
  'oak_staff': {
    id: 'oak_staff',
    name: 'Oak Staff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_staff_oak',
    description: 'A gnarled oak staff humming with latent energy.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'oak_staff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 0, primaryColor: '#7a5230', accentColor: '#72a04a' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 25, intelligence: 5, mp: 30 },
  },
  'composite_bow': {
    id: 'composite_bow',
    name: 'Composite Bow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_bow_composite',
    description: 'A powerful bow crafted from layered materials.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'composite_bow', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'bow', variant: 3, primaryColor: '#8b5a2b', accentColor: '#d4a15d' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 22, dexterity: 8 },
  },
  'blessed_warhammer': {
    id: 'blessed_warhammer',
    name: 'Blessed Warhammer',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_hammer_blessed',
    description: 'A warhammer blessed by the light. Strikes with divine force.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'blessed_warhammer', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'mace', variant: 2, primaryColor: '#d6b34c', accentColor: '#fff0a8' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 20, strength: 4, intelligence: 3 },
  },
  'shadow_dagger': {
    id: 'shadow_dagger',
    name: 'Shadow Dagger',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_dagger_shadow',
    description: 'A dagger infused with shadow essence. Strikes from the dark.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'shadow_dagger', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'dagger', variant: 1, primaryColor: '#241735', accentColor: '#8b5cf6' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 18, dexterity: 6, luck: 4 },
  },
  'vesper_staff': {
    id: 'vesper_staff',
    name: 'Vesper Staff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['caster', 'staff'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_staff_vesper',
    description: 'A pale ashwood staff capped with a lamp of white glass. Its glow steadies a healer\'s hands.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'vesper_staff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 10, primaryColor: '#e8e0c8', accentColor: '#ffd86b' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 22, intelligence: 5, mp: 40 },
  },
  'stormroot_totem': {
    id: 'stormroot_totem',
    name: 'Stormroot Totem',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['caster', 'mace'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_totem_stormroot',
    description: 'A carved root-post bound in copper wire. It hums before every storm.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'stormroot_totem', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'totem', variant: 10, primaryColor: '#6b4a2f', accentColor: '#4fb3d9' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 21, intelligence: 4, strength: 3 },
  },
  'cinder_rod': {
    id: 'cinder_rod',
    name: 'Cinder Rod',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['caster', 'wand'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_wand_cinder',
    description: 'A blackened iron rod that stays warm long after the fire has gone out.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'cinder_rod', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 10, primaryColor: '#2b1d1d', accentColor: '#e0533a' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 25, intelligence: 6 },
  },
  'thornwood_crook': {
    id: 'thornwood_crook',
    name: 'Thornwood Crook',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['caster', 'staff'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_staff_thornwood',
    description: 'A shepherd\'s crook grown, not carved, from a living thorn hedge.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'thornwood_crook', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 11, primaryColor: '#8a6a3a', accentColor: '#6fbf4a' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 22, intelligence: 4, dexterity: 3, mp: 20 },
  },
  'mallows_rare_wick': {
    id: 'mallows_rare_wick',
    name: 'Mallow\'s Rare Wick',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'flame_mallows_wick',
    description: 'A little flame that floats just above your head, left in Mallow\'s chest for whoever found the way down. It won\'t light the whole road. Just the next little bit.',
    equipmentSlot: 'flame' as const,
    flame: { color: '#f2ae4f', glow: 0.9, size: 30, bob: 2, speed: 0.9 } as ItemLanternStyle,
    stats: { strength: 2, dexterity: 2, intelligence: 2, luck: 2 },
  },
  'pig_foot': {
    id: 'pig_foot',
    name: 'Pig Foot',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: '🐾',
    description: 'A pig\'s foot. Used in alchemy and cooking.',
    stats: {},
  },
  'pig_snout': {
    id: 'pig_snout',
    name: 'Pig Snout',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'material_pig_snout',
    description: 'A trophy from a defeated pig.',
    stats: {},
  },
  'slime_gel': {
    id: 'slime_gel',
    name: 'Slime Gel',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: '💧',
    description: 'Sticky gel from slimes. Useful for crafting.',
    stats: {},
  },
  'snail_shell': {
    id: 'snail_shell',
    name: 'Snail Shell',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'material_snail_shell',
    description: 'A spiral shell dropped by snails. Used in defense crafting.',
    stats: {},
  },
  'mushroom_spore': {
    id: 'mushroom_spore',
    name: 'Mushroom Spore',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: '🍄',
    description: 'Spores from a magical mushroom.',
    stats: {},
  },
  'veteran_badge': {
    id: 'veteran_badge',
    name: 'Veteran Badge',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: '🏅',
    description: 'Proof of your growth as an adventurer.',
    stats: {},
  },
  'stone_fragment': {
    id: 'stone_fragment',
    name: 'Stone Fragment',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 8,
    tradeable: true,
    binding: 'none',
    icon: 'material_stone_fragment',
    description: 'A fragment of enchanted stone from a golem.',
    stats: {},
  },
  'dark_essence': {
    id: 'dark_essence',
    name: 'Dark Essence',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'material_dark_essence',
    description: 'Shadow energy condensed into physical form.',
    stats: {},
  },
  'drake_scale': {
    id: 'drake_scale',
    name: 'Drake Scale',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 6,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'material_drake_scale',
    description: 'A glittering scale from an ice drake.',
    stats: {},
  },
  'cursed_plate': {
    id: 'cursed_plate',
    name: 'Cursed Plate',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 8,
    sellPrice: 40,
    tradeable: true,
    binding: 'none',
    icon: 'material_cursed_plate',
    description: 'A plate of cursed armor. Radiates dark energy.',
    stats: {},
  },
  'subi_throwing_star': {
    id: 'subi_throwing_star',
    name: 'Subi Throwing Star',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 10000,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: '✨',
    description: 'A basic throwing star used by assassins.',
    stats: { attack: 10 },
  },
  'fire_crystal': {
    id: 'fire_crystal',
    name: 'Fire Crystal',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'material_fire_crystal',
    description: 'A crystal infused with elemental fire energy.',
    stats: {},
  },
  'frost_shard': {
    id: 'frost_shard',
    name: 'Frost Shard',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 8,
    sellPrice: 35,
    tradeable: true,
    binding: 'none',
    icon: 'material_frost_shard',
    description: 'A frozen shard that never melts. Used in frost enchanting.',
    stats: {},
  },
  'void_fragment': {
    id: 'void_fragment',
    name: 'Void Fragment',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'material_void_fragment',
    description: 'A fragment of pure void energy. Extremely rare and valuable.',
    stats: {},
  },
  'enchanted_dust': {
    id: 'enchanted_dust',
    name: 'Enchanted Dust',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'material_enchanted_dust',
    description: 'Magical dust obtained from disenchanting equipment.',
    stats: {},
  },
  'phoenix_feather': {
    id: 'phoenix_feather',
    name: 'Phoenix Feather',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 10,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 18,
    sellPrice: 500,
    tradeable: true,
    binding: 'none',
    icon: 'material_phoenix_feather',
    description: 'A feather from the legendary Phoenix. Burns eternally.',
    stats: {},
  },
  'beach_trinket': {
    id: 'beach_trinket',
    name: 'Beach Trinket',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'material_beach_trinket',
    description: 'A small trinket washed up by the tide — a shard of old pottery, a bent spoon, a glass bead. The shore keeps its gifts for those who look.',
    stats: {},
  },
  'ancient_relic': {
    id: 'ancient_relic',
    name: 'Ancient Relic',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'material_ancient_relic',
    description: 'A small carved relic bearing the Coil\'s oldest mark. It hums faintly in your hand.',
    stats: {},
  },
  'fresh_fish': {
    id: 'fresh_fish',
    name: 'Fresh Fish',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'material_fresh_fish',
    description: 'A silver-scaled fish from the Tidewend dock. The village eats well when the catch is good.',
    stats: {},
  },
  'keepers_journal_page': {
    id: 'keepers_journal_page',
    name: 'Keeper\'s Journal Page',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 10,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: 'material_keepers_journal_page',
    description: 'A yellowed page from the lighthouse keeper\'s journal. The handwriting is careful, then hurried, then something else entirely.',
    stats: {},
  },
  'boar_tusk': {
    id: 'boar_tusk',
    name: 'Boar Tusk',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 4,
    tradeable: true,
    binding: 'none',
    icon: 'material_boar_tusk',
    description: 'A curved ivory tusk from a Brackenmoor boar. Sharp enough to have mattered.',
    stats: {},
  },
  'bee_stinger': {
    id: 'bee_stinger',
    name: 'Bee Stinger',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 4,
    tradeable: true,
    binding: 'none',
    icon: 'material_bee_stinger',
    description: 'A giant bee\'s stinger, long as a dagger. Handle by the blunt end.',
    stats: {},
  },
  'bat_wing': {
    id: 'bat_wing',
    name: 'Bat Wing',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'material_bat_wing',
    description: 'A leathery wing from a shadow bat. It still twitches when the fog rolls in.',
    stats: {},
  },
  'eye_shard': {
    id: 'eye_shard',
    name: 'Eye Shard',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 6,
    tradeable: true,
    binding: 'none',
    icon: 'material_eye_shard',
    description: 'A crystalline sliver from an evil eye. Hold it to the light and something looks back.',
    stats: {},
  },
  'dark_wood': {
    id: 'dark_wood',
    name: 'Dark Wood',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 6,
    sellPrice: 8,
    tradeable: true,
    binding: 'none',
    icon: 'material_dark_wood',
    description: 'Heartwood from a dark stump, stained through with the undercroft\'s corruption. It drinks lantern light.',
    stats: {},
  },
  'hollow_essence': {
    id: 'hollow_essence',
    name: 'Hollow Essence',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 10,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 7,
    sellPrice: 30,
    tradeable: true,
    binding: 'none',
    icon: 'material_hollow_essence',
    description: 'What was left where the Hollow Keeper\'s heart should have been. Cold, green, and quietly humming.',
    stats: {},
  },
  'lantern_oil': {
    id: 'lantern_oil',
    name: 'Lantern Oil',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 4,
    tradeable: true,
    binding: 'none',
    icon: 'material_lantern_oil',
    description: 'A stoppered flask of rendered slime-gel oil — Maren\'s own blend. Burns slow and clean, and the fog keeps its distance from a lit lamp. Every doorway in Tidewend hangs one after dark.',
    stats: {},
  },
  'simple_wand': {
    id: 'simple_wand',
    name: 'Simple Wand',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_simple_wand',
    description: 'A basic wand for novice casters.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'simple_wand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 0, primaryColor: '#8b6f47', accentColor: '#d6c29a' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'adepts_wand': {
    id: 'adepts_wand',
    name: 'Adept\'s Wand',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_adepts_wand',
    description: 'A well-crafted wand for experienced casters.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'adepts_wand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 1, primaryColor: '#4968a8', accentColor: '#b8ccff' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'fire_wand': {
    id: 'fire_wand',
    name: 'Fire Wand',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 30,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_fire_wand',
    description: 'A wand imbued with fire magic.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'fire_wand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 2, primaryColor: '#c2410c', accentColor: '#ffb347' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'frost_wand': {
    id: 'frost_wand',
    name: 'Frost Wand',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 30,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_frost_wand',
    description: 'A wand imbued with frost magic.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'frost_wand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 2, primaryColor: '#4aa3df', accentColor: '#d9f4ff' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'mystic_wand': {
    id: 'mystic_wand',
    name: 'Mystic Wand',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_mystic_wand',
    description: 'A powerful wand crackling with arcane energy.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'mystic_wand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 3, primaryColor: '#6d28d9', accentColor: '#e879f9' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'arcane_charges': {
    id: 'arcane_charges',
    name: 'Arcane Charges',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'material_arcane_charges',
    description: 'Condensed arcane energy used for powerful spells.',
    stats: {},
  },
  'reagent_fish_oil': {
    id: 'reagent_fish_oil',
    name: 'Fish Oil',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'material_reagent_fish_oil',
    description: 'Extracted from fish. Used in alchemy.',
    stats: {},
  },
  'reagent_ichor': {
    id: 'reagent_ichor',
    name: 'Ichor',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'material_reagent_ichor',
    description: 'A viscous fluid from creatures. Used in alchemy.',
    stats: {},
  },
  'reagent_light_feather': {
    id: 'reagent_light_feather',
    name: 'Light Feather',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'material_reagent_light_feather',
    description: 'A feather so light it nearly floats. Reagent for Slow Fall.',
    stats: {},
  },
  'reagent_naga_mucus': {
    id: 'reagent_naga_mucus',
    name: 'Naga Mucus',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'material_reagent_naga_mucus',
    description: 'Slippery mucus from the Naga. Used in alchemy.',
    stats: {},
  },
  'reagent_shiny_fish_scales': {
    id: 'reagent_shiny_fish_scales',
    name: 'Shiny Fish Scales',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'material_reagent_shiny_fish_scales',
    description: 'Iridescent scales from freshwater fish.',
    stats: {},
  },
  'reagent_spider_ichor': {
    id: 'reagent_spider_ichor',
    name: 'Spider Ichor',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'material_reagent_spider_ichor',
    description: 'Toxic fluid from spiders. Used in alchemy and poisons.',
    stats: {},
  },
  'health_potion': {
    id: 'health_potion',
    name: 'Health Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_health_potion',
    description: 'Restores a small amount of health.',
    stats: {},
  },
  'greater_health_potion': {
    id: 'greater_health_potion',
    name: 'Greater Health Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_greater_health_potion',
    description: 'Restores a moderate amount of health.',
    stats: {},
  },
  'potion_major_healing': {
    id: 'potion_major_healing',
    name: 'Major Healing Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_potion_major_healing',
    description: 'Restores a large amount of health.',
    stats: {},
  },
  'mana_potion': {
    id: 'mana_potion',
    name: 'Mana Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_mana_potion',
    description: 'Restores a small amount of mana.',
    stats: {},
  },
  'greater_mana_potion': {
    id: 'greater_mana_potion',
    name: 'Greater Mana Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_greater_mana_potion',
    description: 'Restores a moderate amount of mana.',
    stats: {},
  },
  'energy_potion': {
    id: 'energy_potion',
    name: 'Energy Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_energy_potion',
    description: 'Restores a small amount of energy.',
    stats: {},
  },
  'greater_energy_potion': {
    id: 'greater_energy_potion',
    name: 'Greater Energy Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_greater_energy_potion',
    description: 'Restores a moderate amount of energy.',
    stats: {},
  },
  'agility_potion': {
    id: 'agility_potion',
    name: 'Agility Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_agility_potion',
    description: 'Increases agility for a short duration.',
    stats: {},
  },
  'stamina_potion': {
    id: 'stamina_potion',
    name: 'Stamina Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_stamina_potion',
    description: 'Increases stamina for a short duration.',
    stats: {},
  },
  'strength_potion': {
    id: 'strength_potion',
    name: 'Strength Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_strength_potion',
    description: 'Increases strength for a short duration.',
    stats: {},
  },
  'swiftness_potion': {
    id: 'swiftness_potion',
    name: 'Swiftness Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_swiftness_potion',
    description: 'Increases movement speed for a short duration.',
    stats: {},
  },
  'unbinding_draught': {
    id: 'unbinding_draught',
    name: 'Unbinding Draught',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 20,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_unbinding_draught',
    description: 'Makes the user immune to stun and movement-impairing effects.',
    stats: {},
  },
  'invisibility_potion': {
    id: 'invisibility_potion',
    name: 'Invisibility Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_invisibility_potion',
    description: 'Grants invisibility for a short duration.',
    stats: {},
  },
  'flash_powder': {
    id: 'flash_powder',
    name: 'Flash Powder',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_flash_powder',
    description: 'Blinds nearby enemies briefly. Used by rogues.',
    stats: {},
  },
  'mending_stone': {
    id: 'mending_stone',
    name: 'Mending Stone',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_mending_stone',
    description: 'A smooth stone, warm with stored life. Restores health when used.',
    stats: {},
  },
  'copper_ore': {
    id: 'copper_ore',
    name: 'Copper Ore',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'material_copper_ore',
    description: 'A chunk of rough copper ore.',
    stats: {},
  },
  'iron_ore': {
    id: 'iron_ore',
    name: 'Iron Ore',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'material_iron_ore',
    description: 'A chunk of heavy iron ore.',
    stats: {},
  },
  'calmbloom': {
    id: 'calmbloom',
    name: 'Calmbloom',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'material_calmbloom',
    description: 'A delicate white flower.',
    stats: {},
  },
  'silverleaf': {
    id: 'silverleaf',
    name: 'Silverleaf',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'material_silverleaf',
    description: 'A silvery bush leaf.',
    stats: {},
  },
  'scroll_rain_cloud': {
    id: 'scroll_rain_cloud',
    name: 'Scroll: Summon Rain Cloud',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_scroll_rain_cloud',
    description: 'Summons a procedural rain cloud at the target location.',
    stats: {},
  },
  'scroll_fog_bank': {
    id: 'scroll_fog_bank',
    name: 'Scroll: Conjure Fog',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 75,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_scroll_fog_bank',
    description: 'Creates a mysterious bank of fog at the target location.',
    stats: {},
  },
  'scroll_small_tree': {
    id: 'scroll_small_tree',
    name: 'Scroll: Plant Small Tree',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_scroll_small_tree',
    description: 'Grows a small procedural tree at the target location.',
    stats: {},
  },
  'scroll_large_tree': {
    id: 'scroll_large_tree',
    name: 'Scroll: Plant Large Tree',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_scroll_large_tree',
    description: 'Grows a majestic large tree at the target location.',
    stats: {},
  },
  'scroll_boulder': {
    id: 'scroll_boulder',
    name: 'Scroll: Summon Boulder',
    type: 'consumable' as ItemType,
    category: 'consumable',
    quantity: 1,
    maxStack: 99,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'consumable_scroll_boulder',
    description: 'Conjures a rocky boulder at the target location.',
    stats: {},
  },
  'wand': {
    id: 'wand',
    name: 'Wand',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 8,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_wand',
    description: 'A common weapon.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'wand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'wand', variant: 0, primaryColor: '#7b5d3f', accentColor: '#c49a6c' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 35 },
  },
  'sword': {
    id: 'sword',
    name: 'Sword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 8,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_sword',
    description: 'A common weapon.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'sword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 0, primaryColor: '#a7adb4', accentColor: '#5a6068' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'staff': {
    id: 'staff',
    name: 'Staff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_staff',
    description: 'A common weapon.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'staff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 0, primaryColor: '#6b4a2d', accentColor: '#4f7d45' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 15 },
  },
  'mace': {
    id: 'mace',
    name: 'Mace',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 2,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'weapon_mace',
    description: 'A common weapon.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'mace', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'mace', variant: 0, primaryColor: '#80858c', accentColor: '#4b382a' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 20 },
  },
  'rogue_head': {
    id: 'rogue_head',
    name: 'Assassination Hood',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_head.png',
    description: 'Stitched from shadowsilk and lined with felt to muffle breathing. The hood\'s interior is darker than it should be — a minor enchantment that makes the wearer\'s face unreadable even in torchlight.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'rogue_head', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 1, primaryColor: '#241b2f', accentColor: '#6d28a8' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 25, hp: 20 },
  },
  'rogue_gloves': {
    id: 'rogue_gloves',
    name: 'Stormscale Gauntlets',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_gloves.png',
    description: 'Cut from the hide of a great storm-scaled drake and cured in volcanic springs. The scales still carry a predator\'s heat, and the fingertips are thin enough to pick a lock by touch alone.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'rogue_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 2, primaryColor: '#6f5236', accentColor: '#b87942' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 28, dexterity: 15 },
  },
  'rogue_chest': {
    id: 'rogue_chest',
    name: 'Duskweaver Chestpiece',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_chest.png',
    description: 'The Duskweaver set was designed for one purpose: to make the wearer disappear. The leather is dyed midnight-black and treated with a resin that absorbs light.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'rogue_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 1, primaryColor: '#241b2f', accentColor: '#6d28a8' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 30, dexterity: 30, defense: 150 },
  },
  'rogue_boots': {
    id: 'rogue_boots',
    name: 'Nightveil Wisp Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'trail_nightveil',
    description: 'Violet shadow wisps that sink instead of rise, pooling behind you like spilled dusk.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'rogue_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 2, primaryColor: '#241b2f', accentColor: '#6d28a8' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#9b6bff', glow: 0.55, size: 20, count: 5, rise: -1 } as ItemTrailStyle,
    stats: { hp: 20, dexterity: 20 },
  },
  'icemage_head': {
    id: 'icemage_head',
    name: 'Arcane Crown',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/icemage_head.png',
    description: 'A circlet of spellweave, silver wire, and crystallized mana that keeps the mind frost-clear even when battle turns frantic. Snowflake sigils gather over the brow as spells are prepared, making the wearer\'s thoughts look colder than they feel.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'icemage_head', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 2, primaryColor: '#6aa9ff', accentColor: '#d8f3ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 20, hp: 15, magicAttack: 15 },
  },
  'icemage_gloves': {
    id: 'icemage_gloves',
    name: 'Mage Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/icemage_gloves.png',
    description: 'Frost-resistant threading runs through these gloves in pale blue seams, channeling cold magic without numbing the hands inside. The surface blooms with tiny crystals that melt and return with each cast, like the gloves are quietly rehearsing a blizzard.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'icemage_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 2, primaryColor: '#6aa9ff', accentColor: '#d8f3ff' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 10 },
  },
  'icemage_chest': {
    id: 'icemage_chest',
    name: 'Frost Robe',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/icemage_chest.png',
    description: 'The Frostweave Robes hold a disciplined storm inside every fold. They trail a faint breath of winter through even the hottest battlefield, and the hem crackles when the wearer speaks the opening syllables of a killing spell.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'icemage_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 2, primaryColor: '#6aa9ff', accentColor: '#d8f3ff' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 25, intelligence: 25, defense: 40, magicAttack: 10 },
  },
  'icemage_boots': {
    id: 'icemage_boots',
    name: 'Rimefleck Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'trail_rimefleck',
    description: 'Frost flecks that fall behind you like the first snow of winter. Anything you pass feels a little colder.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'icemage_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 2, primaryColor: '#6aa9ff', accentColor: '#d8f3ff' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#bfeaff', glow: 0.85, size: 9, count: 5, rise: -2 } as ItemTrailStyle,
    stats: { hp: 15, intelligence: 10 },
  },
  'hunter_mainhand': {
    id: 'hunter_mainhand',
    name: 'Hunting Knife',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_mainhand.png',
    description: 'A field knife with a gut-hook spine and a grip darkened by sweat, resin, and old blood. Hunters use it to skin, cut snares, dress kills, and settle close-range arguments the bow was never meant to survive.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'hunter_mainhand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'dagger', variant: 0, primaryColor: '#6b4f2d', accentColor: '#3a2d1f' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 40, dexterity: 15 },
  },
  'hunter_legs': {
    id: 'hunter_legs',
    name: 'Beasttracker Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_legs.png',
    description: 'These leggings are reinforced at knee and thigh for crawling through bramble, scree, and half-frozen creek beds without tearing. The leather is rubbed with bitter herbs and ash that muddle a hunter\'s scent long enough to become someone else\'s problem.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'hunter_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 1, primaryColor: '#7a5a32', accentColor: '#9fbf62' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 25, hp: 20, defense: 40, attack: 25 },
  },
  'hunter_head': {
    id: 'hunter_head',
    name: 'Leather Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_head.png',
    description: 'A broad-brimmed leather hat built for glare, rain, and the patient misery of waiting motionless in a blind. The brim is stiff enough to turn thorns and light enough not to tug when the archer finally lifts their head to take the shot.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'hunter_head', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hat', variant: 1, primaryColor: '#7a5a32', accentColor: '#9fbf62' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 20, hp: 15, intelligence: 10 },
  },
  'hunter_hands': {
    id: 'hunter_hands',
    name: 'Tracker Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_hands.png',
    description: 'Tracker Gloves leave the string fingers bare for a clean release while armoring the opposite hand against trap-springs, teeth, and bad decisions. Every seam is placed for one rule only: never let clumsiness announce the hunt.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'hunter_hands', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 1, primaryColor: '#7a5a32', accentColor: '#9fbf62' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 10, attack: 10 },
  },
  'hunter_feet': {
    id: 'hunter_feet',
    name: 'Thicket Firefly Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'trail_firefly',
    description: 'A handful of fireflies that decided to follow you home. Small, quick and impossible to shoo away.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'hunter_feet', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 2, primaryColor: '#65704a', accentColor: '#9b7a4d' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#d8ff6b', glow: 0.8, size: 10, count: 5, rise: 2 } as ItemTrailStyle,
    stats: { dexterity: 12, hp: 8 },
  },
  'hunter_chest': {
    id: 'hunter_chest',
    name: 'Hunter\'s Tunic',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_chest.png',
    description: 'Layered raptor hide over spider-silk backing gives this tunic the flex of a stalking cat and the bite resistance of something that has survived being hunted in turn. It smells faintly of oil, pine pitch, and the last campfire before sunrise.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'hunter_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'tunic', variant: 1, primaryColor: '#7a5a32', accentColor: '#9fbf62' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 20, dexterity: 25, defense: 35, attack: 15 },
  },
  'frostborn_staff_epic': {
    id: 'frostborn_staff_epic',
    name: 'Frostborn Staff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/frostborn_staff_epic.png',
    description: 'Cut from the permafrost of the far north, older than several kingdoms, the Frostborn Staff refuses warmth with near-personal contempt. A frozen tear from a water elemental hangs at the crown, and every spell cast through it lands with the hush that falls just before a lake seals over.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'frostborn_staff_epic', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 3, primaryColor: '#6ecff6', accentColor: '#e7fbff' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 170, intelligence: 35 },
  },
  'dev_hero_admin_offhand': {
    id: 'dev_hero_admin_offhand',
    name: 'Admin\'s Tome',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/dev_hero_admin_offhand.png',
    description: 'A leather-bound tome containing the server commands. Page one: /kill. Page two: /teleport. Page three: /ban. There is no page four. There does not need to be a page four.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'dev_hero_admin_offhand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'tome', variant: 3, primaryColor: '#5b3a29', accentColor: '#ff5f57' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 80, mp: 2000, intelligence: 50 },
  },
  'dev_hero_admin_mainhand': {
    id: 'dev_hero_admin_mainhand',
    name: 'Admin\'s Blade',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/dev_hero_admin_mainhand.png',
    description: 'A blade that deals exactly enough damage to kill anything in one hit. It was forged by a developer who got tired of testing and decided the problem was the sword, not the boss. 「デバッグ完了」 — debug complete.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'dev_hero_admin_mainhand', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 3, primaryColor: '#d6d6d6', accentColor: '#ff3b30' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 250 },
  },
  'crimson_fang_epic': {
    id: 'crimson_fang_epic',
    name: 'Crimson Fang',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/crimson_fang_epic.png',
    description: 'Smelted with blood-iron from the Searing Gorge, Crimson Fang carries the matte red sheen of an old wound that never closed correctly. It bites shallow going in, then opens the cut on the way out, as if the blade resents leaving clean work behind.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'crimson_fang_epic', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'dagger', variant: 3, primaryColor: '#7f1d1d', accentColor: '#dc2626' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 45, dexterity: 25 },
  },
  'blood_dagger_epic': {
    id: 'blood_dagger_epic',
    name: 'Blood Dagger',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 650,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/blood_dagger_epic.png',
    description: 'The blade sweats a dark sheen that is not oil, not blood, and never welcome on the hand that grips it. Wounds struck with it bloom wider with every heartbeat, feeding the dagger a slow red satisfaction that borders on intelligence.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'blood_dagger_epic', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'dagger', variant: 3, primaryColor: '#4a0e18', accentColor: '#a1122b' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 55, dexterity: 30 },
  },
  'warrior_plate_helm': {
    id: 'warrior_plate_helm',
    name: 'Valor Plate Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_valor'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_plate_helm.png',
    description: 'A battle-scarred helm of the King\'s guard, dented by a raider\'s axe at the Battle of Blackcrag and repaired but never restored. The dent is a badge of honor. Removing it would insult the soldier who survived.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'warrior_plate_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 2, primaryColor: '#65707b', accentColor: '#8b2635' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 12, hp: 10 },
  },
  'warrior_plate_chest': {
    id: 'warrior_plate_chest',
    name: 'Valor Breastplate',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_valor'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_plate_chest.png',
    description: 'Heavy plate from the great forge under the mountain, still warm from the anvil. It bears the hammer-and-anvil hallmark of the old mountain smiths, a guarantee of quality that has held for three thousand years of war.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'warrior_plate_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 2, primaryColor: '#65707b', accentColor: '#8b2635' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 15, hp: 12 },
  },
  'warrior_plate_legs': {
    id: 'warrior_plate_legs',
    name: 'Valor Legguards',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_valor'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_plate_legs.png',
    description: 'Reinforced leg armor with enchanted rivets that tighten automatically under impact — dwarf engineering applied to plate smithing. The rivets glow faintly blue when magic is near, which in a dungeon is always.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'warrior_plate_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'plate', variant: 2, primaryColor: '#65707b', accentColor: '#8b2635' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 12, hp: 10 },
  },
  'warrior_plate_hands': {
    id: 'warrior_plate_hands',
    name: 'Valor Gauntlets',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_valor'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_plate_hands.png',
    description: 'Iron gauntlets etched with Dwarven battle runes that glow faintly during combat — each rune represents a virtue of war: courage, endurance, fury, and the one that translates roughly as \'hit them again, harder.\'',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'warrior_plate_hands', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 2, primaryColor: '#65707b', accentColor: '#8b2635' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 8, hp: 6 },
  },
  'warrior_plate_feet': {
    id: 'warrior_plate_feet',
    name: 'Banner Ember Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_valor'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'trail_banner_ember',
    description: 'Embers from a campfire where oaths were sworn. They rise behind you like a banner in the wind.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'warrior_plate_feet', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 2, primaryColor: '#65707b', accentColor: '#8b2635' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ff6b4a', glow: 0.8, size: 12, count: 5, rise: 5 } as ItemTrailStyle,
    stats: { strength: 8, hp: 8 },
  },
  'mage_cloth_helm': {
    id: 'mage_cloth_helm',
    name: 'Arcane Diadem',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_arcane'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_cloth_helm.png',
    description: 'A circlet of silver wire humming with barely contained arcane energy. The vibration is felt in the teeth rather than heard, and prolonged wear gives the wearer unusually vivid dreams about mathematics.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'mage_cloth_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'headband', variant: 1, primaryColor: '#5b4aa8', accentColor: '#c4b5fd' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 14, magicAttack: 10 },
  },
  'mage_cloth_chest': {
    id: 'mage_cloth_chest',
    name: 'Arcane Vestments',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_arcane'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_cloth_chest.png',
    description: 'Robes woven with threads of pure crystallized mana that shimmer blue-violet in certain light. The fabric crackles with static when the wearer gets excited, which in a mage\'s case is usually right before something explodes.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'mage_cloth_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 1, primaryColor: '#5b4aa8', accentColor: '#c4b5fd' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 16, magicAttack: 14, mp: 6 },
  },
  'mage_cloth_legs': {
    id: 'mage_cloth_legs',
    name: 'Arcane Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_arcane'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_cloth_legs.png',
    description: 'Leggings woven from manasilk that shimmer with contained arcane energy. The fabric shifts color with the wearer\'s mood — blue for calm, violet for concentration, red for the moment right before something explodes.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'mage_cloth_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 1, primaryColor: '#5b4aa8', accentColor: '#c4b5fd' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 12, magicAttack: 8, hp: 5 },
  },
  'mage_cloth_hands': {
    id: 'mage_cloth_hands',
    name: 'Arcane Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_arcane'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_cloth_hands.png',
    description: 'Spellweave gloves — so thin they\'re nearly transparent, designed to reduce the friction between thought and spell. Casting speed increases noticeably, though the tradeoff is that you can feel everything, including the temperature of your own fireballs.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'mage_cloth_hands', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 1, primaryColor: '#5b4aa8', accentColor: '#c4b5fd' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 8 },
  },
  'mage_cloth_feet': {
    id: 'mage_cloth_feet',
    name: 'Arcane Mote Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_arcane'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'trail_arcane',
    description: 'Lilac motes of loose spellwork, the leftovers of a thousand practice cantrips.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'mage_cloth_feet', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 1, primaryColor: '#5b4aa8', accentColor: '#c4b5fd' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#c4a8ff', glow: 0.75, size: 14, count: 5, rise: 3 } as ItemTrailStyle,
    stats: { intelligence: 8, mp: 5 },
  },
  'priest_cloth_helm': {
    id: 'priest_cloth_helm',
    name: 'Devout Crown',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_devout'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_cloth_helm.png',
    description: 'A crown of blessed silver that radiates warm golden light in a soft halo. It brightens when healing magic is channeled and dims in the presence of shadow — a practical indicator that also makes the healer the most visible target in any fight.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'priest_cloth_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 1, primaryColor: '#f2d27d', accentColor: '#fff7d6' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 14, mp: 12, magicAttack: 8 },
  },
  'priest_cloth_chest': {
    id: 'priest_cloth_chest',
    name: 'Devout Robe',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_devout'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_cloth_chest.png',
    description: 'White robes blessed by the High Priestess herself — the fabric never stains, even with blood. This is either a minor miracle or a very good enchantment. The priests don\'t clarify which, and the soldiers don\'t ask.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'priest_cloth_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 1, primaryColor: '#f2d27d', accentColor: '#fff7d6' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 16, mp: 14, magicAttack: 12 },
  },
  'priest_cloth_legs': {
    id: 'priest_cloth_legs',
    name: 'Devout Skirt',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_devout'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_cloth_legs.png',
    description: 'A flowing garment of mooncloth infused with healing magic that seeps into anyone standing nearby. Wounded soldiers gravitate toward priests wearing these without knowing why — the warmth is subtle but unmistakable, like standing in a sunbeam that follows you.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'priest_cloth_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'skirt', variant: 1, primaryColor: '#f2d27d', accentColor: '#fff7d6' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 12, mp: 10, hp: 5 },
  },
  'priest_cloth_hands': {
    id: 'priest_cloth_hands',
    name: 'Devout Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_devout'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_cloth_hands.png',
    description: 'Gossamer-thin gloves that channel the Light with zero resistance — the wearer\'s hands glow during healing as if the skin itself has become translucent. Effective. Also impossible to be subtle about.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'priest_cloth_hands', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 1, primaryColor: '#f2d27d', accentColor: '#fff7d6' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 8, mp: 8 },
  },
  'druid_leather_helm': {
    id: 'druid_leather_helm',
    name: 'Wildheart Cowl',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_wildheart'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_leather_helm.png',
    description: 'A cowl of living vine that sprouts small leaves and adapts its shape to the wearer\'s head. In sunlight it photosynthesizes, providing a pleasant tingle. In darkness, it tightens protectively. The druid\'s first lesson: everything alive has opinions.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'druid_leather_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 1, primaryColor: '#557a3e', accentColor: '#9fcf6b' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 10, dexterity: 8, mp: 6 },
  },
  'druid_leather_chest': {
    id: 'druid_leather_chest',
    name: 'Wildheart Vest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_wildheart'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_leather_chest.png',
    description: 'Bark-reinforced leather that grows tougher in response to damage — each hit hardens the surface, and given enough time between fights, the armor regenerates entirely. The first druid to discover this punched a tree until his vest was harder than plate.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'druid_leather_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 1, primaryColor: '#557a3e', accentColor: '#9fcf6b' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 12, dexterity: 10, hp: 8 },
  },
  'druid_leather_legs': {
    id: 'druid_leather_legs',
    name: 'Wildheart Kilt',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_wildheart'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_leather_legs.png',
    description: 'A kilt of cured hide blessed by nature spirits at a moonpool ceremony. It rustles in wind that isn\'t there and smells of wildflowers year-round. Plains druids consider it formal wear. Forest druids consider it casual. Both are correct.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'druid_leather_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'skirt', variant: 1, primaryColor: '#557a3e', accentColor: '#9fcf6b' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 10, dexterity: 8, mp: 5 },
  },
  'druid_leather_hands': {
    id: 'druid_leather_hands',
    name: 'Wildheart Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_wildheart'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_leather_hands.png',
    description: 'Living-leather gloves that let the druid feel the pulse of nature through any surface — soil composition, water depth, root networks, and the slow heartbeat of ancient trees. Wearing these in a dead zone feels like holding your hand over a grave.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'druid_leather_hands', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 1, primaryColor: '#557a3e', accentColor: '#9fcf6b' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 8, mp: 6 },
  },
  'iron_battleaxe': {
    id: 'iron_battleaxe',
    name: 'Iron Battleaxe',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'axe', 'melee'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 130,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/iron_battleaxe.png',
    description: 'A workman\'s axe repurposed for war — the blade has been reground to a fighting edge and the handle wrapped in leather for grip. It was made to fell trees. It fells other things just as well.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'iron_battleaxe', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'axe', variant: 0, primaryColor: '#7d858c', accentColor: '#4a3426' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 10 },
  },
  'steel_warhammer': {
    id: 'steel_warhammer',
    name: 'Steel Warhammer',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'mace', 'melee'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 12,
    sellPrice: 680,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/steel_warhammer.png',
    description: 'A blunt instrument in every sense. The hammer\'s head is solid iron, weighted to the striking face, and swung with the subtlety of an avalanche. Armor doesn\'t deflect it. Armor dents around it. The target inside dents too.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'steel_warhammer', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'mace', variant: 1, primaryColor: '#a7b0b8', accentColor: '#3f4650' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 22, strength: 4 },
  },
  'runed_staff': {
    id: 'runed_staff',
    name: 'Runed Staff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'staff', 'caster'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 830,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/runed_staff.png',
    description: 'An ironwood staff inscribed with Darnassian runes that glow green-gold during spellcasting. The wood is alive — it sprouts new buds each spring and drops them each autumn, regardless of what climate the wielder is standing in.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'runed_staff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 1, primaryColor: '#5b3d24', accentColor: '#8fcf5b' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 20, intelligence: 8, mp: 4 },
  },
  'crossbow_of_precision': {
    id: 'crossbow_of_precision',
    name: 'Crossbow of Precision',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'crossbow', 'ranged'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 18,
    sellPrice: 980,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/crossbow_of_precision.png',
    description: 'Engineered by a gnomish weaponsmith with an obsession for ballistic precision, this crossbow uses a counterweighted crank system that eliminates hand-tremor at full draw. The bolt flights are trimmed to a tolerance measured in hair-widths. It doesn\'t miss. If you miss with it, the problem is you.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'crossbow_of_precision', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'crossbow', variant: 2, primaryColor: '#7a5430', accentColor: '#d2a65a' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 24 },
  },
  'volcanic_greatsword': {
    id: 'volcanic_greatsword',
    name: 'Volcanic Greatsword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'sword', 'melee', 'fire'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 38,
    sellPrice: 11500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/volcanic_greatsword.png',
    description: 'The Volcanic Greatsword still radiates heat — not metaphorically, but literally. Forged in the caldera of a live volcano by a smith who didn\'t survive the process, the blade\'s edge glows dull orange and the air shimmers above it like a mirage. It cauterizes every wound it inflicts. Nothing it cuts ever bleeds.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'volcanic_greatsword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 3, primaryColor: '#6b1d12', accentColor: '#ff6b1a' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 78, strength: 16 },
  },
  'moonlight_bow': {
    id: 'moonlight_bow',
    name: 'Moonlight Bow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'bow', 'ranged', 'nature'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 30,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/moonlight_bow.png',
    description: 'Carved from a branch that fell from the great world-tree during a lunar eclipse, this bow holds the moon\'s light within its grain. Arrows loosed from it leave trails of silver light that linger in the dark for seconds after impact. Elven rangers call it a blessing from the moon. Their targets call it the last thing they see.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'moonlight_bow', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'bow', variant: 3, primaryColor: '#b8c8ff', accentColor: '#f4f7ff' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 42, dexterity: 12 },
  },
  'warlock_staff': {
    id: 'warlock_staff',
    name: 'Staff of the Void',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'staff', 'caster', 'shadow'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 30,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_staff.png',
    description: 'The Staff of the Void was carved from a tree that grew above a tear in reality — its wood grain spirals in directions that shouldn\'t exist. Shadow energy pulses through it like a dark heartbeat, and warlocks who channel through it report hearing whispers in a language they almost understand. The whispers get louder with each spell cast. Most warlocks find this encouraging.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'warlock_staff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 3, primaryColor: '#24113d', accentColor: '#8b3fd1' } as ItemEquipmentVisualDescriptor,
    stats: { magicAttack: 52, intelligence: 14 },
  },
  'tribal_spear': {
    id: 'tribal_spear',
    name: 'Tribal Spear',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'polearm', 'melee'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 14,
    sellPrice: 780,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/tribal_spear.png',
    description: 'A bone-tipped spear crafted by Darkspear warriors from raptor femur and ironwood shaft. The bone tip is harder than steel and self-sharpening — the marrow channels friction into the edge. Troll engineering: brutal, elegant, and slightly unsettling.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'tribal_spear', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'spear', variant: 1, primaryColor: '#8b6a3f', accentColor: '#d9d0b4' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 20, dexterity: 5 },
  },
  'buckler_shield': {
    id: 'buckler_shield',
    name: 'Iron Buckler',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['shield', 'off_hand'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 130,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/buckler_shield.png',
    description: 'A buckler small enough to strap to the forearm and forget about — until a sword comes at your face and twenty inches of riveted iron suddenly becomes the most important thing you own. Size isn\'t everything. Ask the shield.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'buckler_shield', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'shield', variant: 0, primaryColor: '#777f88', accentColor: '#41474d' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 4 },
  },
  'tower_shield': {
    id: 'tower_shield',
    name: 'Tower Shield',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['shield', 'off_hand', 'tank'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/tower_shield.png',
    description: 'A wall of riveted iron tall enough to hide behind completely. Tower shields were designed for siege warfare — arrow slits cut into the top edge let defenders fire while crouching, and the base is weighted to plant in soft ground. Carrying one into a dungeon is a statement of intent: nothing gets past.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'tower_shield', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'shield', variant: 2, primaryColor: '#5f6670', accentColor: '#2f343a' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 14, strength: 6 },
  },
  'mail_helm': {
    id: 'mail_helm',
    name: 'Beastmaster Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 830,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mail_helm.png',
    description: 'A helm crowned with the fangs of a dire wolf — each tooth earned through a kill, each kill remembered by the hide lining that still smells of the hunt. Hunters wear these to honor the predators they\'ve bested and to warn the ones they haven\'t.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'mail_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 2, primaryColor: '#6b5842', accentColor: '#c29a52' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 8, hp: 6 },
  },
  'mail_chest': {
    id: 'mail_chest',
    name: 'Beastmaster Hauberk',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 830,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mail_chest.png',
    description: 'Chainmail backed with boiled leather panels at the joints — the shaman\'s compromise between protection and mobility. The rings are etched with elemental wards that tingle during storms and glow during rituals. Neither effect is optional.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'mail_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 2, primaryColor: '#6b5842', accentColor: '#c29a52' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 10, hp: 8 },
  },
  'mail_legs': {
    id: 'mail_legs',
    name: 'Beastmaster Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 830,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mail_legs.png',
    description: 'Chainmail leggings with articulated knee joints designed for the shaman\'s fighting style — part caster, part brawler. The rings are etched with tiny lightning bolts that aren\'t decorative. In thunderstorms, they tingle. During combat, they sing.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'mail_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'mail', variant: 2, primaryColor: '#6b5842', accentColor: '#c29a52' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 8, hp: 6 },
  },
  'mail_boots': {
    id: 'mail_boots',
    name: 'Fieldfern Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 830,
    tradeable: true,
    binding: 'none',
    icon: 'trail_fieldfern',
    description: 'Fern-green motes that smell faintly of a meadow after rain. Favoured by those who track beasts through tall grass.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'mail_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'boots', variant: 2, primaryColor: '#6b5842', accentColor: '#c29a52' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#a8e06b', glow: 0.65, size: 14, count: 5, rise: 1 } as ItemTrailStyle,
    stats: { dexterity: 6, hp: 4 },
  },
  'cloth_shirt': {
    id: 'cloth_shirt',
    name: 'Cloth Shirt',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/cloth_shirt.png',
    description: 'A linen shirt that has survived more than it deserves. Every adventurer starts in one. Most upgrade within the hour. The ones who keep it are either sentimental or insane. Sometimes both.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'cloth_shirt', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'shirt', variant: 0, primaryColor: '#d6c7aa', accentColor: '#8a7964' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 1 },
  },
  'cloth_pants': {
    id: 'cloth_pants',
    name: 'Cloth Pants',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/cloth_pants.png',
    description: 'Standard-issue trousers from the quartermaster\'s bottom drawer. They fit poorly, fade quickly, and offer protection roughly equivalent to optimism. Replace immediately.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'cloth_pants', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'pants', variant: 0, primaryColor: '#9a8a72', accentColor: '#d6c7aa' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 1 },
  },
  'leather_gloves': {
    id: 'leather_gloves',
    name: 'Leather Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 3,
    sellPrice: 90,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/leather_gloves.png',
    description: 'Canvas gloves with reinforced palms. They won\'t stop a sword, but they\'ll keep your hands warm, blister-free, and capable of gripping a weapon when everything else has gone wrong. Which it will.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'leather_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 0, primaryColor: '#7a5230', accentColor: '#c49a6c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 1, hp: 1 },
  },
  'wooden_shield': {
    id: 'wooden_shield',
    name: 'Wooden Shield',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['shield', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 2,
    sellPrice: 70,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/wooden_shield.png',
    description: 'An oak buckler banded with iron — the trainee\'s first shield. It won\'t stop a battleaxe, but it\'ll block a kobold\'s pickaxe, and right now that\'s all you need. Everyone starts somewhere. Most people start behind a piece of wood.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'wooden_shield', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'shield', variant: 0, primaryColor: '#8a5a2f', accentColor: '#c49a62' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 2 },
  },
  'ring_of_fortitude': {
    id: 'ring_of_fortitude',
    name: 'Ring of Fortitude',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['ring', 'accessory'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 580,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/ring_of_fortitude.png',
    description: 'A plain iron ring forged from a single nail and blessed with a minor ward of endurance. The enchantment is small but honest — wearing it feels like being one drink into a good night: warmer, steadier, slightly harder to knock down.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'ring_of_fortitude', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'ring', variant: 1, primaryColor: '#7f8790', accentColor: '#3d434a' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 6 },
  },
  'amulet_of_the_bear': {
    id: 'amulet_of_the_bear',
    name: 'Amulet of the Bear',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['neck', 'accessory', 'tank'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/amulet_of_the_bear.png',
    description: 'Carved from the femur of an ancient dire bear by a plains shaman who asked the bear\'s spirit for permission first. The bone is warm to the touch and pulses with a slow, steady heartbeat that isn\'t yours. Wearing it feels like being a very small part of something very large and very patient.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'amulet_of_the_bear', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'amulet', variant: 2, primaryColor: '#d6c29e', accentColor: '#7a5a3a' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 12, strength: 6 },
  },
  'pendant_of_wisdom': {
    id: 'pendant_of_wisdom',
    name: 'Pendant of Wisdom',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['neck', 'accessory', 'caster'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 3500,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/pendant_of_wisdom.png',
    description: 'A clarity pendant cut from a single lake-clear crystal — the facets catch light and split it into thoughts. Wearing it suppresses mental fog, resists fear effects, and makes hangovers significantly more bearable. Mages buy these in bulk before exam season.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'pendant_of_wisdom', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'pendant', variant: 2, primaryColor: '#6aa6ff', accentColor: '#e7fbff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 10, mp: 8 },
  },
  'trinket_lucky_coin': {
    id: 'trinket_lucky_coin',
    name: 'Lucky Coin',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['trinket', 'accessory'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 580,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/trinket_lucky_coin.png',
    description: 'A trading company\'s doubloon so old the face has worn smooth. Nobody knows whose profile it once bore, but the coin has an uncanny habit of landing on the right side of every flip. It doesn\'t make you lucky. It just makes you slightly less unlucky, which in this world is practically the same thing.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'trinket_lucky_coin', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'trinket', variant: 1, primaryColor: '#c59b3c', accentColor: '#f4d76e' } as ItemEquipmentVisualDescriptor,
    stats: {},
  },
  'trinket_warriors_badge': {
    id: 'trinket_warriors_badge',
    name: 'Warrior\'s Badge',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['trinket', 'accessory', 'melee'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 4250,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/trinket_warriors_badge.png',
    description: 'A war-forged trinket — a clenched iron fist on a chain that grows warm during combat and cold during rest. The enchantment amplifies the wearer\'s aggression in a controlled, directed way. \'Controlled\' being relative.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'trinket_warriors_badge', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'trinket', variant: 2, primaryColor: '#65707b', accentColor: '#8b2635' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 20 },
  },
  'worn_shortsword': {
    id: 'worn_shortsword',
    name: 'Worn Shortsword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/worn_shortsword.png',
    description: 'The training sword — blunt-edged, balance-weighted, and as inspiring as a wet towel. Every warrior starts with one. Every warrior replaces it as soon as humanly possible. The sergeant says the blade doesn\'t matter. The sergeant is lying.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'worn_shortsword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 0, primaryColor: '#8f969c', accentColor: '#5b4635' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 1 },
  },
  'apprentice_robe': {
    id: 'apprentice_robe',
    name: 'Apprentice\'s Robe',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'starter', 'caster'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/apprentice_robe.png',
    description: 'An apprentice\'s robe — brown, baggy, and stained with the residue of every failed spell the previous owner cast. The pockets are surprisingly deep, there\'s a burn mark on the left sleeve from a fireball miscalculation, and the hem is exactly the right length to trip over during a hasty retreat.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'apprentice_robe', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 0, primaryColor: '#8b6f47', accentColor: '#7b68ee' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 2, mp: 1 },
  },
  'wooden_buckler': {
    id: 'wooden_buckler',
    name: 'Wooden Buckler',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['shield', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/wooden_buckler.png',
    description: 'A shield hewn from a single plank of valley oak and reinforced with iron nails. It\'s a plank. With nails. But it\'s YOUR plank with nails, and the difference between having one and not having one is measured in broken ribs.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'wooden_buckler', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'shield', variant: 1, primaryColor: '#7b512d', accentColor: '#b78b55' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 1 },
  },
  'hide_boots': {
    id: 'hide_boots',
    name: 'Hearthsmoke Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'trail_hearthsmoke',
    description: 'A curl of warm smoke caught from a cobbler\'s stove. It drifts up behind you the way a chimney sighs on a cold evening.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'hide_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'boots', variant: 0, primaryColor: '#6b4b32', accentColor: '#a97852' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#e0c9a6', glow: 0.5, size: 16, count: 4, rise: 4 } as ItemTrailStyle,
    stats: { dexterity: 1 },
  },
  'chain_mail_vest': {
    id: 'chain_mail_vest',
    name: 'Chain Mail Vest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'leveling'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 830,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/chain_mail_vest.png',
    description: 'A workhorse hauberk of interlocked rings, stamped out in frontier forges where \'good enough\' still has to survive an axe blow. The links are plain, heavy, and honest, favored by caravan guards who trust bruises more than elegance.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'chain_mail_vest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 0, primaryColor: '#737b84', accentColor: '#42484f' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 8, dexterity: 5 },
  },
  'rugged_leather_pants': {
    id: 'rugged_leather_pants',
    name: 'Rugged Leather Pants',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'leveling'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 1080,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rugged_leather_pants.png',
    description: 'Well-crafted leather leggings, double-stitched at the seams and supple enough for a full split. The leather is dyed dark for obvious professional reasons, and the inner thigh is reinforced for climbing. Rogues and druids both claim the design originated with their class. Both are wrong. A burglar invented them.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'rugged_leather_pants', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'pants', variant: 1, primaryColor: '#6b4b32', accentColor: '#a97852' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 8, hp: 5 },
  },
  'enchanted_gold_ring': {
    id: 'enchanted_gold_ring',
    name: 'Enchanted Gold Ring',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['ring', 'accessory'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 1330,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/enchanted_gold_ring.png',
    description: 'A copper ring imbued with a faint magical glow — the enchantment is so minor that it serves primarily as a nightlight. Hold it up in a dark cave and you can see about three feet ahead. Not useful, but oddly comforting. Sells surprisingly well.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'enchanted_gold_ring', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'ring', variant: 1, primaryColor: '#b87333', accentColor: '#f6d365' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 6, hp: 4 },
  },
  'thick_bronze_necklace': {
    id: 'thick_bronze_necklace',
    name: 'Thick Bronze Necklace',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['neck', 'accessory'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 830,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/thick_bronze_necklace.png',
    description: 'A bronze chain necklace with a clasp strong enough to survive a direct hit to the throat — and at some point in its history, it clearly has. The dent in the pendant saved someone\'s life. The scratch across the back says \'thank you\' in Dwarvish.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'thick_bronze_necklace', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'necklace', variant: 1, primaryColor: '#a96d34', accentColor: '#d8a15b' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 5, strength: 3 },
  },
  'silk_headband': {
    id: 'silk_headband',
    name: 'Silk Headband',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'head', 'crafted'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 1330,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/silk_headband.png',
    description: 'Tailors in the mage quarters stitch these headbands from treated silk so the fabric holds runic chalk and sweat with equal discipline. Apprentices wear them to keep hair, sparks, and ambition out of their eyes.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'silk_headband', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'headband', variant: 0, primaryColor: '#8b5e83', accentColor: '#e6b8d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 7, mp: 5 },
  },
  'green_iron_helm': {
    id: 'green_iron_helm',
    name: 'Green Iron Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'head', 'crafted'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 30,
    sellPrice: 1580,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/green_iron_helm.png',
    description: 'Forged from green iron quenched in pine ash, this blunt helm smells faintly of smoke long after the forge has cooled. Journeyman smiths sell it to sellswords and mine guards who need sturdy metal now, not heraldry later.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'green_iron_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 1, primaryColor: '#52705a', accentColor: '#9aa67d' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 10, strength: 8 },
  },
  'warrior_t1_helm': {
    id: 'warrior_t1_helm',
    name: 'Warrior T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t1_helm.png',
    description: 'The Forgeheart Helm was forged for the champions of the Cinder Deeps — beaten from black iron and quenched in the blood of fire elementals. The metal remembers the heat. Warriors who wear it say they can feel the Cinder King\'s rage pulsing behind their eyes, and in battle, that rage becomes theirs.',
    equipmentSlot: 'hat' as const,
    setName: 'warrior_t1_set',
    visual: { itemId: 'warrior_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 3, primaryColor: '#4b4b52', accentColor: '#c2410c' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 24, hp: 12 },
  },
  'warrior_t1_chest': {
    id: 'warrior_t1_chest',
    name: 'Warrior T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t1_chest.png',
    description: 'The Forgeheart Breastplate is scored with claw marks from the creatures that guarded it. Each scar in the metal is a kill confirmed — and the warriors who wear the full set say they can hear the heartbeat of the Cinder Deeps itself, deep and slow and angry.',
    equipmentSlot: 'top' as const,
    setName: 'warrior_t1_set',
    visual: { itemId: 'warrior_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 3, primaryColor: '#4b4b52', accentColor: '#c2410c' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 27, hp: 14 },
  },
  'warrior_t1_gloves': {
    id: 'warrior_t1_gloves',
    name: 'Warrior T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t1_gloves.png',
    description: 'Forged in the Cinder King\'s heat, these gauntlets keep their grip through blood, ash, and dragon bile alike. The knuckle plates are scarred by impacts no disciplined tactician would recommend and every veteran warrior quietly understands.',
    equipmentSlot: 'gloves' as const,
    setName: 'warrior_t1_set',
    visual: { itemId: 'warrior_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 3, primaryColor: '#4b4b52', accentColor: '#c2410c' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 18, hp: 8 },
  },
  'warrior_t1_legs': {
    id: 'warrior_t1_legs',
    name: 'Warrior T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t1_legs.png',
    description: 'Forgeheart Legplates are thick enough to mark stone and balanced for the brutal forward drive of a shield charge. Heat still sleeps in the knee joints, as though the forge never truly released them from the Cinder Deeps.',
    equipmentSlot: 'bottom' as const,
    setName: 'warrior_t1_set',
    visual: { itemId: 'warrior_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'plate', variant: 3, primaryColor: '#4b4b52', accentColor: '#c2410c' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 24, hp: 12 },
  },
  'warrior_t1_boots': {
    id: 'warrior_t1_boots',
    name: 'Warbrand Ember Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_warbrand',
    description: 'Hot orange embers that climb fast and burn bright, as restless as the one who wears them.',
    equipmentSlot: 'shoes' as const,
    setName: 'warrior_t1_set',
    visual: { itemId: 'warrior_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 3, primaryColor: '#4b4b52', accentColor: '#c2410c' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ff7a2e', glow: 0.85, size: 13, count: 5, rise: 6 } as ItemTrailStyle,
    stats: { strength: 18, hp: 10 },
  },
  'warrior_t2_helm': {
    id: 'warrior_t2_helm',
    name: 'Warrior T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t2_helm.png',
    description: 'The Wyrmbreaker Helm channels the fury of the Black Wyrm\'s roost — many-hued dragon scales line the interior, and the visor is shaped to resemble a snarling beast. Warriors who wear it report dreams of flight and fire. The helm doesn\'t just protect. It transforms.',
    equipmentSlot: 'hat' as const,
    setName: 'warrior_t2_set',
    visual: { itemId: 'warrior_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 4, primaryColor: '#5d2026', accentColor: '#d44b34' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 32, hp: 18 },
  },
  'warrior_t2_chest': {
    id: 'warrior_t2_chest',
    name: 'Warrior T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t2_chest.png',
    description: 'The Wyrmbreaker Breastplate is the Black Wyrm\'s roost\'s greatest prize: many-hued dragon scales layered over black iron plate, all but impenetrable. The metal shifts color in firelight, cycling through red, blue, green and bronze. It was made to withstand dragonfire. It has proven itself.',
    equipmentSlot: 'top' as const,
    setName: 'warrior_t2_set',
    visual: { itemId: 'warrior_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 4, primaryColor: '#5d2026', accentColor: '#d44b34' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 36, hp: 21 },
  },
  'warrior_t2_gloves': {
    id: 'warrior_t2_gloves',
    name: 'Warrior T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t2_gloves.png',
    description: 'Wyrmbreaker Gauntlets — many-hued scales flex across the knuckles like living armor. The grip tightens instinctively near dragons, as if the gauntlets remember who they were taken from.',
    equipmentSlot: 'gloves' as const,
    setName: 'warrior_t2_set',
    visual: { itemId: 'warrior_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 4, primaryColor: '#5d2026', accentColor: '#d44b34' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 24, hp: 12 },
  },
  'warrior_t2_legs': {
    id: 'warrior_t2_legs',
    name: 'Warrior T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t2_legs.png',
    description: 'Wyrmbreaker Legplates forged from the Black Wyrm\'s many-hued experiments. The metal shifts color with each step — red, blue, green, bronze — and is heavier than it looks. Much heavier.',
    equipmentSlot: 'bottom' as const,
    setName: 'warrior_t2_set',
    visual: { itemId: 'warrior_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'plate', variant: 4, primaryColor: '#5d2026', accentColor: '#d44b34' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 32, hp: 18 },
  },
  'warrior_t2_boots': {
    id: 'warrior_t2_boots',
    name: 'Bloodforge Ember Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_bloodforge',
    description: 'Deep red embers from a forge that has never gone cold. They roar upward behind every charge.',
    equipmentSlot: 'shoes' as const,
    setName: 'warrior_t2_set',
    visual: { itemId: 'warrior_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 4, primaryColor: '#5d2026', accentColor: '#d44b34' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ff4a3a', glow: 0.9, size: 15, count: 5, rise: 7 } as ItemTrailStyle,
    stats: { strength: 24, hp: 15 },
  },
  'paladin_t1_helm': {
    id: 'paladin_t1_helm',
    name: 'Paladin T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t1_helm.png',
    description: 'The Oathkeeper Helm radiates the Light\'s warmth in a soft golden corona visible even in darkness. Forged for the paladins who ventured into the Cinder Deeps, it protects against fire and shadow equally. The inscription along the brow reads: \'Justice burns brighter than any flame.\'',
    equipmentSlot: 'hat' as const,
    setName: 'paladin_t1_set',
    visual: { itemId: 'paladin_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 3, primaryColor: '#d4af37', accentColor: '#fff1a8' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 24, hp: 12 },
  },
  'paladin_t1_chest': {
    id: 'paladin_t1_chest',
    name: 'Paladin T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t1_chest.png',
    description: 'The Oathkeeper Chestguard was blessed by three Archbishops of the Light before it entered the Cinder Deeps — and came out with the blessings intact, which no one expected. The metal hums with holy resonance, and undead flinch from its proximity. Paladins consider wearing the full set a sacrament.',
    equipmentSlot: 'top' as const,
    setName: 'paladin_t1_set',
    visual: { itemId: 'paladin_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 3, primaryColor: '#d4af37', accentColor: '#fff1a8' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 27, hp: 14 },
  },
  'paladin_t1_gloves': {
    id: 'paladin_t1_gloves',
    name: 'Paladin T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t1_gloves.png',
    description: 'Gauntlets consecrated in the Cathedral of Light. The palms emit a soft warmth that amplifies healing touch — bandages applied with these hands seal faster and cleaner.',
    equipmentSlot: 'gloves' as const,
    setName: 'paladin_t1_set',
    visual: { itemId: 'paladin_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 3, primaryColor: '#d4af37', accentColor: '#fff1a8' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 18, hp: 8 },
  },
  'paladin_t1_legs': {
    id: 'paladin_t1_legs',
    name: 'Paladin T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t1_legs.png',
    description: 'Oathkeeper Legplates — solid gold trim over blessed steel. They ring like cathedral bells with every step, which is either inspiring or terrible for stealth, depending on your class.',
    equipmentSlot: 'bottom' as const,
    setName: 'paladin_t1_set',
    visual: { itemId: 'paladin_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'plate', variant: 3, primaryColor: '#d4af37', accentColor: '#fff1a8' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 24, hp: 12 },
  },
  'paladin_t1_boots': {
    id: 'paladin_t1_boots',
    name: 'Dawnward Glint Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_dawnward',
    description: 'Gold glints like sunrise on a polished shield. They lift gently, as if in prayer.',
    equipmentSlot: 'shoes' as const,
    setName: 'paladin_t1_set',
    visual: { itemId: 'paladin_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 3, primaryColor: '#d4af37', accentColor: '#fff1a8' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ffe27a', glow: 0.85, size: 11, count: 5, rise: 3 } as ItemTrailStyle,
    stats: { strength: 18, hp: 10 },
  },
  'paladin_t2_helm': {
    id: 'paladin_t2_helm',
    name: 'Paladin T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t2_helm.png',
    description: 'The Dawnwarden Crown blazes with righteous authority — a circlet of blessed steel crowned with the symbol of the Order of the Dawn. Paladins who wear the full Dawnwarden set radiate an aura visible to the faithful and terrifying to the damned. the Black Wyrm\'s many-hued experiments could not corrupt it. Nothing can.',
    equipmentSlot: 'hat' as const,
    setName: 'paladin_t2_set',
    visual: { itemId: 'paladin_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 4, primaryColor: '#4f3035', accentColor: '#ffb429' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 32, hp: 18 },
  },
  'paladin_t2_chest': {
    id: 'paladin_t2_chest',
    name: 'Paladin T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t2_chest.png',
    description: 'The Dawnwarden Breastplate was forged to withstand the shadowflame of the Black Wyrm himself — and it did. The metal bears permanent scorch patterns from dragonfire that only add to its terrible beauty. When a paladin wearing it calls upon the Light, the glow emanates from every seam and joint, turning the wearer into a walking beacon.',
    equipmentSlot: 'top' as const,
    setName: 'paladin_t2_set',
    visual: { itemId: 'paladin_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 4, primaryColor: '#4f3035', accentColor: '#ffb429' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 36, hp: 21 },
  },
  'paladin_t2_gloves': {
    id: 'paladin_t2_gloves',
    name: 'Paladin T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t2_gloves.png',
    description: 'Dawnwarden Gauntlets forged to channel righteous fury. The fist-plate bears the symbol of the Order of the Dawn, and the metal grows warm when lies are spoken nearby.',
    equipmentSlot: 'gloves' as const,
    setName: 'paladin_t2_set',
    visual: { itemId: 'paladin_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 4, primaryColor: '#4f3035', accentColor: '#ffb429' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 24, hp: 12 },
  },
  'paladin_t2_legs': {
    id: 'paladin_t2_legs',
    name: 'Paladin T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t2_legs.png',
    description: 'Dawnwarden Legplates — blessed steel reinforced with dragon bone. The joints are silent, which surprises everyone who sees plate armor this heavy move without sound.',
    equipmentSlot: 'bottom' as const,
    setName: 'paladin_t2_set',
    visual: { itemId: 'paladin_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'plate', variant: 4, primaryColor: '#4f3035', accentColor: '#ffb429' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 32, hp: 18 },
  },
  'paladin_t2_boots': {
    id: 'paladin_t2_boots',
    name: 'Sunvow Glint Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_sunvow',
    description: 'Rich gold light kept from a vow made at noon. It never dims, and it never hurries.',
    equipmentSlot: 'shoes' as const,
    setName: 'paladin_t2_set',
    visual: { itemId: 'paladin_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 4, primaryColor: '#4f3035', accentColor: '#ffb429' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ffc94a', glow: 0.95, size: 14, count: 5, rise: 4 } as ItemTrailStyle,
    stats: { strength: 24, hp: 15 },
  },
  'mage_t1_helm': {
    id: 'mage_t1_helm',
    name: 'Mage T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t1_helm.png',
    description: 'The Arcanist Crown channels raw arcane energy through a crystalline matrix embedded in the headband. Mages who wear it report heightened clarity — spells come faster, incantations sharpen, and the world takes on a faintly blue tint, as if reality itself is revealing its underlying structure.',
    equipmentSlot: 'hat' as const,
    setName: 'mage_t1_set',
    visual: { itemId: 'mage_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 3, primaryColor: '#315a9e', accentColor: '#70d6ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'mage_t1_chest': {
    id: 'mage_t1_chest',
    name: 'Mage T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t1_chest.png',
    description: 'The Arcanist Robes are woven from thread spun by the Arcanists\' finest enchantresses — each strand carries a micro-ward that absorbs ambient magic. Wearing the full set feels like standing in a library of infinite knowledge, where every spell you\'ve ever learned is one thought away.',
    equipmentSlot: 'top' as const,
    setName: 'mage_t1_set',
    visual: { itemId: 'mage_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 3, primaryColor: '#315a9e', accentColor: '#70d6ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 27, hp: 14 },
  },
  'mage_t1_gloves': {
    id: 'mage_t1_gloves',
    name: 'Mage T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t1_gloves.png',
    description: 'Arcanist Gloves woven with threads of crystallized mana. The fingertips spark when spellcasting, and the fabric repairs itself overnight — a convenience that never stops being unsettling.',
    equipmentSlot: 'gloves' as const,
    setName: 'mage_t1_set',
    visual: { itemId: 'mage_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 3, primaryColor: '#315a9e', accentColor: '#70d6ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 18, hp: 8 },
  },
  'mage_t1_legs': {
    id: 'mage_t1_legs',
    name: 'Mage T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t1_legs.png',
    description: 'Arcanist Leggings — enchanted cloth that maintains a comfortable temperature regardless of environment. Lava fields, glaciers, or the back of a drake — the same pleasant coolness persists.',
    equipmentSlot: 'bottom' as const,
    setName: 'mage_t1_set',
    visual: { itemId: 'mage_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 3, primaryColor: '#315a9e', accentColor: '#70d6ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'mage_t1_boots': {
    id: 'mage_t1_boots',
    name: 'Starwell Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_starwell',
    description: 'Blue starlight drawn up from a deep, still well. Each mote is a tiny, steady star.',
    equipmentSlot: 'shoes' as const,
    setName: 'mage_t1_set',
    visual: { itemId: 'mage_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 3, primaryColor: '#315a9e', accentColor: '#70d6ff' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#7cc8ff', glow: 0.85, size: 12, count: 5, rise: 3 } as ItemTrailStyle,
    stats: { intelligence: 18, hp: 10 },
  },
  'mage_t2_helm': {
    id: 'mage_t2_helm',
    name: 'Mage T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t2_helm.png',
    description: 'The Starwake Crown crackles with barely contained arcane energy — lightning arcs between the crystalline points on its brow, and the air around it smells of ozone and old parchment. Mages who wear it describe a sensation of expanded consciousness, as if they can feel the ley lines beneath their feet.',
    equipmentSlot: 'hat' as const,
    setName: 'mage_t2_set',
    visual: { itemId: 'mage_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 4, primaryColor: '#4d4fb3', accentColor: '#9be7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'mage_t2_chest': {
    id: 'mage_t2_chest',
    name: 'Mage T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t2_chest.png',
    description: 'Starwake Robes are cut from thread spun between dimensions, the fabric flickering through colors that do not behave properly under mortal light. When the ley lines stir, the robe answers like a second pulse beneath the wearer\'s own.',
    equipmentSlot: 'top' as const,
    setName: 'mage_t2_set',
    visual: { itemId: 'mage_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 4, primaryColor: '#4d4fb3', accentColor: '#9be7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 36, hp: 21 },
  },
  'mage_t2_gloves': {
    id: 'mage_t2_gloves',
    name: 'Mage T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t2_gloves.png',
    description: 'Each finger of the Starwake Gloves is inscribed for a different school of magic, turning the hands into a deliberate little cosmology. Sparks change hue with intent alone, which is useful in duels and intolerable for anyone trying to ignore the mage.',
    equipmentSlot: 'gloves' as const,
    setName: 'mage_t2_set',
    visual: { itemId: 'mage_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 4, primaryColor: '#4d4fb3', accentColor: '#9be7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'mage_t2_legs': {
    id: 'mage_t2_legs',
    name: 'Mage T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t2_legs.png',
    description: 'Starwake Leggings are warded against portal tears, dimensional shear, and the sort of temporal accidents only archmages call manageable. The cuffs never wrinkle because somewhere, somehow, they are always a moment ahead of being worn.',
    equipmentSlot: 'bottom' as const,
    setName: 'mage_t2_set',
    visual: { itemId: 'mage_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 4, primaryColor: '#4d4fb3', accentColor: '#9be7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'mage_t2_boots': {
    id: 'mage_t2_boots',
    name: 'Deepstar Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_deepstar',
    description: 'Pale starlight from far past the night sky. It glows brighter the farther you travel.',
    equipmentSlot: 'shoes' as const,
    setName: 'mage_t2_set',
    visual: { itemId: 'mage_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 4, primaryColor: '#4d4fb3', accentColor: '#9be7ff' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#9be7ff', glow: 0.95, size: 15, count: 5, rise: 4 } as ItemTrailStyle,
    stats: { intelligence: 24, hp: 15 },
  },
  'warlock_t1_helm': {
    id: 'warlock_t1_helm',
    name: 'Warlock T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t1_helm.png',
    description: 'The Cinderheart Horns curve upward from the brow like a demon\'s crown. The metal is warm to the touch and whispers temptations that grow louder with each soul shard consumed. Most warlocks find this motivating.',
    equipmentSlot: 'hat' as const,
    setName: 'warlock_t1_set',
    visual: { itemId: 'warlock_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'antlers', variant: 3, primaryColor: '#3b1f4f', accentColor: '#7cff4f' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'warlock_t1_chest': {
    id: 'warlock_t1_chest',
    name: 'Warlock T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t1_chest.png',
    description: 'Cinderheart Robes stitched from shadow-silk and lined with demonic leather. The fabric absorbs ambient light, creating a pocket of darkness around the wearer. Standing next to a warlock in full Cinderheart feels like standing next to a hole in the world.',
    equipmentSlot: 'top' as const,
    setName: 'warlock_t1_set',
    visual: { itemId: 'warlock_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 3, primaryColor: '#3b1f4f', accentColor: '#7cff4f' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 27, hp: 14 },
  },
  'warlock_t1_gloves': {
    id: 'warlock_t1_gloves',
    name: 'Warlock T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t1_gloves.png',
    description: 'Cinderheart Gloves — the palms are inscribed with binding circles that flare green when summoning demons. The leather grows warm during soul harvesting, as if the gloves themselves are feeding.',
    equipmentSlot: 'gloves' as const,
    setName: 'warlock_t1_set',
    visual: { itemId: 'warlock_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 3, primaryColor: '#3b1f4f', accentColor: '#7cff4f' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 18, hp: 8 },
  },
  'warlock_t1_legs': {
    id: 'warlock_t1_legs',
    name: 'Warlock T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t1_legs.png',
    description: 'Cinderheart Pants — enchanted to resist fire, shadow, and the occasional demonic tantrum. The stitching incorporates runes of binding that prevent the wearer\'s own summons from turning on them. Usually.',
    equipmentSlot: 'bottom' as const,
    setName: 'warlock_t1_set',
    visual: { itemId: 'warlock_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'pants', variant: 3, primaryColor: '#3b1f4f', accentColor: '#7cff4f' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'warlock_t1_boots': {
    id: 'warlock_t1_boots',
    name: 'Hexfire Wisp Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_hexfire',
    description: 'Sickly green wisps that sink behind you and linger a moment too long.',
    equipmentSlot: 'shoes' as const,
    setName: 'warlock_t1_set',
    visual: { itemId: 'warlock_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 3, primaryColor: '#3b1f4f', accentColor: '#7cff4f' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#8cff5a', glow: 0.7, size: 16, count: 5, rise: -1 } as ItemTrailStyle,
    stats: { intelligence: 18, hp: 10 },
  },
  'warlock_t2_helm': {
    id: 'warlock_t2_helm',
    name: 'Warlock T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t2_helm.png',
    description: 'The Gravecall Skullcap is forged from a metal that drinks light. Shadowy tendrils curl from its surface, and the wearer\'s eyes glow green with suppressed infernal energy. It was made to intimidate. It succeeds.',
    equipmentSlot: 'hat' as const,
    setName: 'warlock_t2_set',
    visual: { itemId: 'warlock_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 4, primaryColor: '#14131f', accentColor: '#6ee7b7' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'warlock_t2_chest': {
    id: 'warlock_t2_chest',
    name: 'Warlock T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t2_chest.png',
    description: 'Gravecall Robes woven from the distilled essence of defeated demons. The fabric writhes subtly, as if something is trying to escape from within. Soul Drain spells channel through it like water through a sieve — efficient, merciless, and deeply unsettling to watch.',
    equipmentSlot: 'top' as const,
    setName: 'warlock_t2_set',
    visual: { itemId: 'warlock_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 4, primaryColor: '#14131f', accentColor: '#6ee7b7' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 36, hp: 21 },
  },
  'warlock_t2_gloves': {
    id: 'warlock_t2_gloves',
    name: 'Warlock T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t2_gloves.png',
    description: 'Gravecall Gloves — the fingers end in clawed tips that are decorative. Probably. The shadow energy they channel leaves afterimages of every gesture, so the warlock\'s hands appear to multiply during spellcasting.',
    equipmentSlot: 'gloves' as const,
    setName: 'warlock_t2_set',
    visual: { itemId: 'warlock_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 4, primaryColor: '#14131f', accentColor: '#6ee7b7' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'warlock_t2_legs': {
    id: 'warlock_t2_legs',
    name: 'Warlock T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t2_legs.png',
    description: 'Gravecall Leggings inscribed with the true names of seven lesser demons — a binding that reinforces the wearer\'s dominance over their summons. The demons in question are reportedly unhappy about this arrangement.',
    equipmentSlot: 'bottom' as const,
    setName: 'warlock_t2_set',
    visual: { itemId: 'warlock_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 4, primaryColor: '#14131f', accentColor: '#6ee7b7' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'warlock_t2_boots': {
    id: 'warlock_t2_boots',
    name: 'Gloomhex Wisp Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_gloomhex',
    description: 'Cold jade wisps that settle low and slow. Small animals give your path a wide berth.',
    equipmentSlot: 'shoes' as const,
    setName: 'warlock_t2_set',
    visual: { itemId: 'warlock_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 4, primaryColor: '#14131f', accentColor: '#6ee7b7' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#6ee7b7', glow: 0.75, size: 19, count: 5, rise: -2 } as ItemTrailStyle,
    stats: { intelligence: 24, hp: 15 },
  },
  'priest_t1_helm': {
    id: 'priest_t1_helm',
    name: 'Priest T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t1_helm.png',
    description: 'The Dawnsong Circlet gleams with inner light that brightens in the presence of suffering — a beacon for healers and a warning for enemies. The crystal centerpiece channels prayer into power.',
    equipmentSlot: 'hat' as const,
    setName: 'priest_t1_set',
    visual: { itemId: 'priest_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 3, primaryColor: '#d8c8a0', accentColor: '#f8e7a1' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, mp: 12 },
  },
  'priest_t1_chest': {
    id: 'priest_t1_chest',
    name: 'Priest T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t1_chest.png',
    description: 'The Dawnsong Vestments are woven from mooncloth blessed under three full moons. The fabric mends small tears on its own and radiates a warmth that eases pain in anyone standing nearby. Wearing the full set feels like being wrapped in a prayer.',
    equipmentSlot: 'top' as const,
    setName: 'priest_t1_set',
    visual: { itemId: 'priest_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 3, primaryColor: '#d8c8a0', accentColor: '#f8e7a1' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 27, mp: 14 },
  },
  'priest_t1_gloves': {
    id: 'priest_t1_gloves',
    name: 'Priest T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t1_gloves.png',
    description: 'Dawnsong Gloves — the fingertips glow when touching the wounded, channeling the Light directly through skin contact. Field medics who wear these can stabilize a dying soldier with a touch.',
    equipmentSlot: 'gloves' as const,
    setName: 'priest_t1_set',
    visual: { itemId: 'priest_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 3, primaryColor: '#d8c8a0', accentColor: '#f8e7a1' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 18, mp: 8 },
  },
  'priest_t1_legs': {
    id: 'priest_t1_legs',
    name: 'Priest T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t1_legs.png',
    description: 'Dawnsong Pants — consecrated cloth reinforced at the knees for kneeling in prayer on stone, mud, or blood-soaked battlefields. The fabric never stains. A small mercy in a graceless profession.',
    equipmentSlot: 'bottom' as const,
    setName: 'priest_t1_set',
    visual: { itemId: 'priest_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'pants', variant: 3, primaryColor: '#d8c8a0', accentColor: '#f8e7a1' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, mp: 12 },
  },
  'priest_t1_boots': {
    id: 'priest_t1_boots',
    name: 'Vesper Glint Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_vesper',
    description: 'Soft candle-gold motes from an evening service. They rise like a quiet hymn.',
    equipmentSlot: 'shoes' as const,
    setName: 'priest_t1_set',
    visual: { itemId: 'priest_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 3, primaryColor: '#d8c8a0', accentColor: '#f8e7a1' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#fff1b8', glow: 0.8, size: 12, count: 5, rise: 4 } as ItemTrailStyle,
    stats: { intelligence: 18, mp: 10 },
  },
  'priest_t2_helm': {
    id: 'priest_t2_helm',
    name: 'Priest T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t2_helm.png',
    description: 'The Halo of the Lightward hovers above the wearer\'s head, held by faith alone. Its light cuts through shadow magic like a blade through smoke. Undead recoil at its mere approach.',
    equipmentSlot: 'hat' as const,
    setName: 'priest_t2_set',
    visual: { itemId: 'priest_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 4, primaryColor: '#f4e7c4', accentColor: '#ffffff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, mp: 18 },
  },
  'priest_t2_chest': {
    id: 'priest_t2_chest',
    name: 'Priest T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t2_chest.png',
    description: 'Robes of the Lightward, woven from thread blessed by the first light. The fabric phases slightly between material and ethereal, making the wearer hard to hit with physical strikes. Shadow priests report the robes whisper to them. Holy priests report hymns.',
    equipmentSlot: 'top' as const,
    setName: 'priest_t2_set',
    visual: { itemId: 'priest_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 4, primaryColor: '#f4e7c4', accentColor: '#ffffff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 36, mp: 21 },
  },
  'priest_t2_gloves': {
    id: 'priest_t2_gloves',
    name: 'Priest T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t2_gloves.png',
    description: 'Handguards of the Lightward channel healing with surgical precision — the wearer can direct the Light to specific wounds, mending bone before flesh, vein before skin. Battlefield triage becomes an art form.',
    equipmentSlot: 'gloves' as const,
    setName: 'priest_t2_set',
    visual: { itemId: 'priest_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 4, primaryColor: '#f4e7c4', accentColor: '#ffffff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, mp: 12 },
  },
  'priest_t2_legs': {
    id: 'priest_t2_legs',
    name: 'Priest T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t2_legs.png',
    description: 'Leggings of the Lightward — the cloth shimmers between white and gold depending on the angle, an effect of the dual-blessing woven into every thread. Shadow and Light coexist in the fabric, as they do in every priest.',
    equipmentSlot: 'bottom' as const,
    setName: 'priest_t2_set',
    visual: { itemId: 'priest_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 4, primaryColor: '#f4e7c4', accentColor: '#ffffff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, mp: 18 },
  },
  'priest_t2_boots': {
    id: 'priest_t2_boots',
    name: 'Lumen Choir Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_lumen',
    description: 'Pure white motes that rise in a steady chorus behind you, lighting the way for whoever follows.',
    equipmentSlot: 'shoes' as const,
    setName: 'priest_t2_set',
    visual: { itemId: 'priest_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'slippers', variant: 4, primaryColor: '#f4e7c4', accentColor: '#ffffff' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ffffff', glow: 0.9, size: 14, count: 5, rise: 5 } as ItemTrailStyle,
    stats: { intelligence: 24, mp: 15 },
  },
  'rogue_t1_helm': {
    id: 'rogue_t1_helm',
    name: 'Rogue T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t1_helm.png',
    description: 'The Duskweaver Cover wraps the face in enchanted shadow — even in broad daylight, the wearer\'s features are blurred and unreadable. Witnesses describe seeing someone, but can never recall what they looked like.',
    equipmentSlot: 'hat' as const,
    setName: 'rogue_t1_set',
    visual: { itemId: 'rogue_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 3, primaryColor: '#231a2b', accentColor: '#7e365c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 24, hp: 12 },
  },
  'rogue_t1_chest': {
    id: 'rogue_t1_chest',
    name: 'Rogue T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t1_chest.png',
    description: 'Duskweaver Chestpiece — form-fitting leather that moves like a second skin. The interior is lined with silk for silence, and the exterior is treated with oil that makes blades glance off at angles. Getting stabbed while wearing Duskweaver is genuinely difficult.',
    equipmentSlot: 'top' as const,
    setName: 'rogue_t1_set',
    visual: { itemId: 'rogue_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 3, primaryColor: '#231a2b', accentColor: '#7e365c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 27, hp: 14 },
  },
  'rogue_t1_gloves': {
    id: 'rogue_t1_gloves',
    name: 'Rogue T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t1_gloves.png',
    description: 'Duskweaver Gloves — fingerless for lockpicking, padded at the palm for climbing, and reinforced at the knuckles for the inevitable fistfight. The leather is thin enough to feel a pulse through a throat.',
    equipmentSlot: 'gloves' as const,
    setName: 'rogue_t1_set',
    visual: { itemId: 'rogue_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 3, primaryColor: '#231a2b', accentColor: '#7e365c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 18, hp: 8 },
  },
  'rogue_t1_legs': {
    id: 'rogue_t1_legs',
    name: 'Rogue T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t1_legs.png',
    description: 'Duskweaver Pants — articulated at every joint for maximum range of motion. The pockets are hidden, numerous, and deep enough to conceal objects that definitely aren\'t there, officer.',
    equipmentSlot: 'bottom' as const,
    setName: 'rogue_t1_set',
    visual: { itemId: 'rogue_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'pants', variant: 3, primaryColor: '#231a2b', accentColor: '#7e365c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 24, hp: 12 },
  },
  'rogue_t1_boots': {
    id: 'rogue_t1_boots',
    name: 'Duskcut Shadow Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_duskcut',
    description: 'Plum-dark wisps that slip down and away. Easy to miss, which is the point.',
    equipmentSlot: 'shoes' as const,
    setName: 'rogue_t1_set',
    visual: { itemId: 'rogue_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 3, primaryColor: '#231a2b', accentColor: '#7e365c' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#b06bd6', glow: 0.55, size: 18, count: 5, rise: -1 } as ItemTrailStyle,
    stats: { dexterity: 18, hp: 10 },
  },
  'rogue_t2_helm': {
    id: 'rogue_t2_helm',
    name: 'Rogue T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t2_helm.png',
    description: 'The Redthorn Hood is dyed with actual blood — specifically, the blood of the many-hued dragonkin it was taken from. The leather absorbs light and sound equally, and the visor slits are narrowed to predatory focus. You wear this to be feared.',
    equipmentSlot: 'hat' as const,
    setName: 'rogue_t2_set',
    visual: { itemId: 'rogue_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 4, primaryColor: '#4a111b', accentColor: '#a72e3c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 32, hp: 18 },
  },
  'rogue_t2_chest': {
    id: 'rogue_t2_chest',
    name: 'Rogue T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t2_chest.png',
    description: 'Redthorn Chestpiece — many-hued dragon leather that shifts between dark red and black depending on the light. The armor is self-repairing: small cuts seal within hours, fed by residual draconic vitality in the hide.',
    equipmentSlot: 'top' as const,
    setName: 'rogue_t2_set',
    visual: { itemId: 'rogue_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 4, primaryColor: '#4a111b', accentColor: '#a72e3c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 36, hp: 21 },
  },
  'rogue_t2_gloves': {
    id: 'rogue_t2_gloves',
    name: 'Rogue T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t2_gloves.png',
    description: 'Redthorn Gloves — the fingertips are coated in a toxin-resistant resin that lets the wearer handle poisons bare-handed. The leather is so supple that safecracking in these gloves is actually easier than without them.',
    equipmentSlot: 'gloves' as const,
    setName: 'rogue_t2_set',
    visual: { itemId: 'rogue_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 4, primaryColor: '#4a111b', accentColor: '#a72e3c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 24, hp: 12 },
  },
  'rogue_t2_legs': {
    id: 'rogue_t2_legs',
    name: 'Rogue T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t2_legs.png',
    description: 'Redthorn Pants — reinforced at the inner thigh for climbing ropes and the outer for sliding across stone. Hidden sheaths hold three knives in each leg. Nobody who wears these has ever had only two weapons on them.',
    equipmentSlot: 'bottom' as const,
    setName: 'rogue_t2_set',
    visual: { itemId: 'rogue_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'pants', variant: 4, primaryColor: '#4a111b', accentColor: '#a72e3c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 32, hp: 18 },
  },
  'rogue_t2_boots': {
    id: 'rogue_t2_boots',
    name: 'Crimson Hush Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_crimson_hush',
    description: 'Red wisps that sink without a sound. Whoever sees them has usually already lost.',
    equipmentSlot: 'shoes' as const,
    setName: 'rogue_t2_set',
    visual: { itemId: 'rogue_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 4, primaryColor: '#4a111b', accentColor: '#a72e3c' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#e0506a', glow: 0.6, size: 18, count: 5, rise: -2 } as ItemTrailStyle,
    stats: { dexterity: 24, hp: 15 },
  },
  'druid_t1_helm': {
    id: 'druid_t1_helm',
    name: 'Druid T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t1_helm.png',
    description: 'The Greenwarden Helm is carved from living wood that continues to grow — vines twist from the crown, leaves sprout at the temples, and the grain shifts with the seasons. In autumn, it turns gold. In winter, it sleeps.',
    equipmentSlot: 'hat' as const,
    setName: 'druid_t1_set',
    visual: { itemId: 'druid_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'antlers', variant: 3, primaryColor: '#55733b', accentColor: '#b78b4b' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'druid_t1_chest': {
    id: 'druid_t1_chest',
    name: 'Druid T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t1_chest.png',
    description: 'Greenwarden Vestments woven from bark-fiber and moonpool-blessed linen. The vest breathes with the wearer — expanding in bear form, tightening in cat form, and flowing like water in caster form. The craft is ten thousand years old and still unmatched.',
    equipmentSlot: 'top' as const,
    setName: 'druid_t1_set',
    visual: { itemId: 'druid_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 3, primaryColor: '#55733b', accentColor: '#b78b4b' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 27, hp: 14 },
  },
  'druid_t1_gloves': {
    id: 'druid_t1_gloves',
    name: 'Druid T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t1_gloves.png',
    description: 'Greenwarden Gloves — living leather that photosynthesizes. The fingertips sprout small roots when touching soil, drawing nutrients and information from the earth. Druids wearing these can feel corruption underground from fifty yards.',
    equipmentSlot: 'gloves' as const,
    setName: 'druid_t1_set',
    visual: { itemId: 'druid_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 3, primaryColor: '#55733b', accentColor: '#b78b4b' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 18, hp: 8 },
  },
  'druid_t1_legs': {
    id: 'druid_t1_legs',
    name: 'Druid T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t1_legs.png',
    description: 'Greenwarden Leggings reinforced with woven vine that tightens under stress and relaxes at rest. The leather adapts to every druid form seamlessly — the secret is in the moonpool curing process, which no non-druid has ever observed.',
    equipmentSlot: 'bottom' as const,
    setName: 'druid_t1_set',
    visual: { itemId: 'druid_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 3, primaryColor: '#55733b', accentColor: '#b78b4b' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'druid_t1_boots': {
    id: 'druid_t1_boots',
    name: 'Seedwisp Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_seedwisp',
    description: 'Green seed-lights carried on a gentle breeze. Some say flowers bloom where they land.',
    equipmentSlot: 'shoes' as const,
    setName: 'druid_t1_set',
    visual: { itemId: 'druid_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 3, primaryColor: '#55733b', accentColor: '#b78b4b' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#9be06b', glow: 0.75, size: 12, count: 5, rise: 2 } as ItemTrailStyle,
    stats: { intelligence: 18, hp: 10 },
  },
  'druid_t2_helm': {
    id: 'druid_t2_helm',
    name: 'Druid T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t2_helm.png',
    description: 'The Thundergrove Cover channels the fury of nature through antlers of crystallized moonpool water. In moonlight, the antlers glow silver. In sunlight, amber. During an eclipse, they sing.',
    equipmentSlot: 'hat' as const,
    setName: 'druid_t2_set',
    visual: { itemId: 'druid_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'antlers', variant: 4, primaryColor: '#4f6f61', accentColor: '#c0e9ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'druid_t2_chest': {
    id: 'druid_t2_chest',
    name: 'Druid T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t2_chest.png',
    description: 'Thundergrove Chestguard — living bark armor that heals itself between battles. Deep gashes seal overnight, claw marks smooth over in hours, and the whole vest smells of fresh rain and ancient forest. It is as much companion as armor.',
    equipmentSlot: 'top' as const,
    setName: 'druid_t2_set',
    visual: { itemId: 'druid_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 4, primaryColor: '#4f6f61', accentColor: '#c0e9ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 36, hp: 21 },
  },
  'druid_t2_gloves': {
    id: 'druid_t2_gloves',
    name: 'Druid T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t2_gloves.png',
    description: 'Thundergrove Handguards — the bark-leather flexes with impossible articulation, letting the druid shift between claw and hand mid-swing. Nature magic channels through them like sap through a trunk: slow, powerful, inevitable.',
    equipmentSlot: 'gloves' as const,
    setName: 'druid_t2_set',
    visual: { itemId: 'druid_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 4, primaryColor: '#4f6f61', accentColor: '#c0e9ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'druid_t2_legs': {
    id: 'druid_t2_legs',
    name: 'Druid T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t2_legs.png',
    description: 'Thundergrove Legguards woven from thunderwood bark and elven silk. The bark hardens under impact and flexes under movement — natural plate armor that weighs nothing and grows back from damage.',
    equipmentSlot: 'bottom' as const,
    setName: 'druid_t2_set',
    visual: { itemId: 'druid_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 4, primaryColor: '#4f6f61', accentColor: '#c0e9ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'druid_t2_boots': {
    id: 'druid_t2_boots',
    name: 'Mossmoon Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_mossmoon',
    description: 'Silvery moon-motes over damp moss, the calm glow of an old forest at midnight.',
    equipmentSlot: 'shoes' as const,
    setName: 'druid_t2_set',
    visual: { itemId: 'druid_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 4, primaryColor: '#4f6f61', accentColor: '#c0e9ff' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#c0e9ff', glow: 0.8, size: 14, count: 5, rise: 1 } as ItemTrailStyle,
    stats: { intelligence: 24, hp: 15 },
  },
  'hunter_t1_helm': {
    id: 'hunter_t1_helm',
    name: 'Hunter T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t1_helm.png',
    description: 'The Ridgetracker\'s Helm is designed for patience — a wide brim blocks glare, the interior is lined with sound-dampening moss, and the visor narrows the field of vision to a hunter\'s focus. You see less. What you see, you hit.',
    equipmentSlot: 'hat' as const,
    setName: 'hunter_t1_set',
    visual: { itemId: 'hunter_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hat', variant: 3, primaryColor: '#5f5136', accentColor: '#9f8b4f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 24, hp: 12 },
  },
  'hunter_t1_chest': {
    id: 'hunter_t1_chest',
    name: 'Hunter T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t1_chest.png',
    description: 'Ridgetracker\'s Breastplate — layered leather and scale that flexes for a full bow draw. The armor is treated with giant-blood oil that hardens on impact, absorbing blows that would crack lesser leather. Smells faintly of pine and old kills.',
    equipmentSlot: 'top' as const,
    setName: 'hunter_t1_set',
    visual: { itemId: 'hunter_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 3, primaryColor: '#5f5136', accentColor: '#9f8b4f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 27, hp: 14 },
  },
  'hunter_t1_gloves': {
    id: 'hunter_t1_gloves',
    name: 'Hunter T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t1_gloves.png',
    description: 'Ridgetracker\'s Gloves — the trigger finger is reinforced with wire threading for rapid-fire shots. The palm is textured for trap-setting, and the wrist guard has saved more hands from bowstring snap than any enchantment.',
    equipmentSlot: 'gloves' as const,
    setName: 'hunter_t1_set',
    visual: { itemId: 'hunter_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 3, primaryColor: '#5f5136', accentColor: '#9f8b4f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 18, hp: 8 },
  },
  'hunter_t1_legs': {
    id: 'hunter_t1_legs',
    name: 'Hunter T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t1_legs.png',
    description: 'Ridgetracker\'s Leggings — double-layered at the thighs for kneeling in brush and reinforced at the seat for long hours in a hunting blind. Practical. Unglamorous. Perfect.',
    equipmentSlot: 'bottom' as const,
    setName: 'hunter_t1_set',
    visual: { itemId: 'hunter_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 3, primaryColor: '#5f5136', accentColor: '#9f8b4f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 24, hp: 12 },
  },
  'hunter_t1_boots': {
    id: 'hunter_t1_boots',
    name: 'Pathfinder Spark Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_pathfinder',
    description: 'Amber sparks that mark the way you came, so you can always find your way back.',
    equipmentSlot: 'shoes' as const,
    setName: 'hunter_t1_set',
    visual: { itemId: 'hunter_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 3, primaryColor: '#5f5136', accentColor: '#9f8b4f' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#e8c26b', glow: 0.75, size: 11, count: 5, rise: 2 } as ItemTrailStyle,
    stats: { dexterity: 18, hp: 10 },
  },
  'hunter_t2_helm': {
    id: 'hunter_t2_helm',
    name: 'Hunter T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t2_helm.png',
    description: 'The Wyrmtracker\'s Helm is faced with many-hued scales that shift color to match the environment — forest green, desert tan, snow white. The ultimate camouflage, taken from the ultimate prey.',
    equipmentSlot: 'hat' as const,
    setName: 'hunter_t2_set',
    visual: { itemId: 'hunter_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 4, primaryColor: '#4f6745', accentColor: '#b0744f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 32, hp: 18 },
  },
  'hunter_t2_chest': {
    id: 'hunter_t2_chest',
    name: 'Hunter T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t2_chest.png',
    description: 'Wyrmtracker\'s Breastplate — wyvern-scale over hardened leather, light enough for a full sprint and tough enough to turn dragonkin claws. The chestpiece has a built-in quiver slot that keeps twenty arrows within arm\'s reach at all times.',
    equipmentSlot: 'top' as const,
    setName: 'hunter_t2_set',
    visual: { itemId: 'hunter_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 4, primaryColor: '#4f6745', accentColor: '#b0744f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 36, hp: 21 },
  },
  'hunter_t2_gloves': {
    id: 'hunter_t2_gloves',
    name: 'Hunter T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t2_gloves.png',
    description: 'Wyrmtracker\'s Gauntlets — the fingertips are coated in drake-blood resin that improves grip in any weather. Rain, blood, dragon slobber — nothing makes these hands slip.',
    equipmentSlot: 'gloves' as const,
    setName: 'hunter_t2_set',
    visual: { itemId: 'hunter_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 4, primaryColor: '#4f6745', accentColor: '#b0744f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 24, hp: 12 },
  },
  'hunter_t2_legs': {
    id: 'hunter_t2_legs',
    name: 'Hunter T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t2_legs.png',
    description: 'Wyrmtracker\'s Legguards — reinforced with woven dragon sinew that tightens under stress. A charging dragonkin hit feels like a firm shove rather than a death sentence. The engineers who designed them still aren\'t sure how the sinew works.',
    equipmentSlot: 'bottom' as const,
    setName: 'hunter_t2_set',
    visual: { itemId: 'hunter_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'mail', variant: 4, primaryColor: '#4f6745', accentColor: '#b0744f' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 32, hp: 18 },
  },
  'hunter_t2_boots': {
    id: 'hunter_t2_boots',
    name: 'Wildmark Spark Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_wildmark',
    description: 'Bright sparks like the eyes of a pack watching from the treeline. They keep pace with you.',
    equipmentSlot: 'shoes' as const,
    setName: 'hunter_t2_set',
    visual: { itemId: 'hunter_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 4, primaryColor: '#4f6745', accentColor: '#b0744f' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#ffb070', glow: 0.85, size: 13, count: 5, rise: 3 } as ItemTrailStyle,
    stats: { dexterity: 24, hp: 15 },
  },
  'shaman_t1_helm': {
    id: 'shaman_t1_helm',
    name: 'Shaman T1 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t1_helm.png',
    description: 'The Stoneroar Helm is carved from thunderstone — a mineral that resonates with elemental frequencies. Lightning arcs between the totemic horns during storms, and the wearer can feel earthquakes before they happen.',
    equipmentSlot: 'hat' as const,
    setName: 'shaman_t1_set',
    visual: { itemId: 'shaman_t1_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'antlers', variant: 3, primaryColor: '#704a2b', accentColor: '#53b7d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'shaman_t1_chest': {
    id: 'shaman_t1_chest',
    name: 'Shaman T1 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t1_chest.png',
    description: 'Stoneroar Vestments — chainmail woven with elemental runes that shift between fire-red, earth-brown, water-blue, and air-white. The mail rings hum different notes when different elements are channeled, creating an involuntary war-chant.',
    equipmentSlot: 'top' as const,
    setName: 'shaman_t1_set',
    visual: { itemId: 'shaman_t1_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 3, primaryColor: '#704a2b', accentColor: '#53b7d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 27, hp: 14 },
  },
  'shaman_t1_gloves': {
    id: 'shaman_t1_gloves',
    name: 'Shaman T1 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t1_gloves.png',
    description: 'Stoneroar Gauntlets — the knuckles are inlaid with elemental focus stones: red for fire, blue for water, green for earth, grey for air. Punching something while channeling lightning is discouraged but undeniably effective.',
    equipmentSlot: 'gloves' as const,
    setName: 'shaman_t1_set',
    visual: { itemId: 'shaman_t1_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 3, primaryColor: '#704a2b', accentColor: '#53b7d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 18, hp: 8 },
  },
  'shaman_t1_legs': {
    id: 'shaman_t1_legs',
    name: 'Shaman T1 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t1_legs.png',
    description: 'Stoneroar Legguards — mail reinforced with petrified wood from the first forests. The wearer feels the earth\'s pulse through the metal — tremors, underground rivers, sleeping elementals. Everything the ground remembers.',
    equipmentSlot: 'bottom' as const,
    setName: 'shaman_t1_set',
    visual: { itemId: 'shaman_t1_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'mail', variant: 3, primaryColor: '#704a2b', accentColor: '#53b7d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'shaman_t1_boots': {
    id: 'shaman_t1_boots',
    name: 'Tidecall Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t1_set', 'tier1'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 15,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 5750,
    tradeable: true,
    binding: 'none',
    icon: 'trail_tidecall',
    description: 'Sea-blue motes that sway behind you like a slow tide coming in.',
    equipmentSlot: 'shoes' as const,
    setName: 'shaman_t1_set',
    visual: { itemId: 'shaman_t1_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'moccasins', variant: 3, primaryColor: '#704a2b', accentColor: '#53b7d8' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#53b7d8', glow: 0.8, size: 13, count: 5, rise: 1 } as ItemTrailStyle,
    stats: { intelligence: 18, hp: 10 },
  },
  'shaman_t2_helm': {
    id: 'shaman_t2_helm',
    name: 'Shaman T2 Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t2_helm.png',
    description: 'The Stormchain Crown crackles with chained lightning that never fully discharges. Each of the ten storm-crystals represents a different elemental bond, and together they create a harmony that makes the very air vibrate.',
    equipmentSlot: 'hat' as const,
    setName: 'shaman_t2_set',
    visual: { itemId: 'shaman_t2_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 4, primaryColor: '#285f78', accentColor: '#8ee7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'shaman_t2_chest': {
    id: 'shaman_t2_chest',
    name: 'Shaman T2 Chest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t2_chest.png',
    description: 'Stormchain Breastplate — mail forged in a storm that lasted ten days. Each ring was struck by lightning during the quenching process, embedding elemental fury into the metal itself. It hums during thunderstorms. It screams during combat.',
    equipmentSlot: 'top' as const,
    setName: 'shaman_t2_set',
    visual: { itemId: 'shaman_t2_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 4, primaryColor: '#285f78', accentColor: '#8ee7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 36, hp: 21 },
  },
  'shaman_t2_gloves': {
    id: 'shaman_t2_gloves',
    name: 'Shaman T2 Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t2_gloves.png',
    description: 'Stormchain Gauntlets — lightning arcs between the fingers when the shaman casts, creating a web of crackling energy. Handshakes are inadvisable. Punches are legendary.',
    equipmentSlot: 'gloves' as const,
    setName: 'shaman_t2_set',
    visual: { itemId: 'shaman_t2_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gauntlets', variant: 4, primaryColor: '#285f78', accentColor: '#8ee7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 24, hp: 12 },
  },
  'shaman_t2_legs': {
    id: 'shaman_t2_legs',
    name: 'Shaman T2 Legs',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t2_legs.png',
    description: 'Stormchain Kilt — yes, it\'s a kilt. The chainmail skirt channels grounding energy that makes the wearer immovable during casting. Knockback effects fail. Charge attacks bounce. The kilt holds.',
    equipmentSlot: 'bottom' as const,
    setName: 'shaman_t2_set',
    visual: { itemId: 'shaman_t2_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'skirt', variant: 4, primaryColor: '#285f78', accentColor: '#8ee7ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 32, hp: 18 },
  },
  'shaman_t2_boots': {
    id: 'shaman_t2_boots',
    name: 'Stormtide Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t2_set', 'tier2'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'trail_stormtide',
    description: 'Crackling blue-white motes from where the storm meets the sea. They hum when lightning is near.',
    equipmentSlot: 'shoes' as const,
    setName: 'shaman_t2_set',
    visual: { itemId: 'shaman_t2_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 4, primaryColor: '#285f78', accentColor: '#8ee7ff' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#8ee7ff', glow: 0.9, size: 15, count: 5, rise: 2 } as ItemTrailStyle,
    stats: { intelligence: 24, hp: 15 },
  },
  'warrior_t3_helm': {
    id: 'warrior_t3_helm',
    name: 'Gravesiege Greathelm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t3_helm.png',
    description: 'Forged from coffin-dark plate and rimmed with iron thick enough to turn a ghoul\'s bite. The visor narrows the world to the next breach, teaching the wearer the lesson of the Hollow Necropolis every warrior eventually learns: hold the line, or be buried beneath it.',
    equipmentSlot: 'hat' as const,
    setName: 'warrior_t3_set',
    visual: { itemId: 'warrior_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 5, primaryColor: '#28252a', accentColor: '#8f98a3' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 40, hp: 30 },
  },
  'warrior_t3_chest': {
    id: 'warrior_t3_chest',
    name: 'Gravesiege Cuirass',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t3_chest.png',
    description: 'This cuirass carries the weight of a portcullis and the stubbornness of a fortress gate. Each overlapping plate is pinned with grave-iron rivets that ring like distant bells when struck, as if warning the dead that the wall still stands.',
    equipmentSlot: 'top' as const,
    setName: 'warrior_t3_set',
    visual: { itemId: 'warrior_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 5, primaryColor: '#28252a', accentColor: '#8f98a3' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 45, hp: 35 },
  },
  'warrior_t3_legs': {
    id: 'warrior_t3_legs',
    name: 'Gravesiege Legplates',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'warrior', 'set_warrior_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARRIOR,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warrior_t3_legs.png',
    description: 'Built for the long advance through plague-slick stone, these legplates turn every stride into a siege engine\'s promise. The hinges are brutally simple, the balance perfect, and the greaves heavy enough to leave bootprints like burial markers.',
    equipmentSlot: 'bottom' as const,
    setName: 'warrior_t3_set',
    visual: { itemId: 'warrior_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'plate', variant: 5, primaryColor: '#28252a', accentColor: '#8f98a3' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 40, hp: 30 },
  },
  'paladin_t3_helm': {
    id: 'paladin_t3_helm',
    name: 'Helm of the Last Vigil',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t3_helm.png',
    description: 'A reliquary crown set with pale crystals that store the afterglow of prayer. In the dark halls of the Hollow Necropolis it burns with tomb-bright conviction, a quiet reminder that holiness is most useful when everything around it is trying to rot.',
    equipmentSlot: 'hat' as const,
    setName: 'paladin_t3_set',
    visual: { itemId: 'paladin_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 5, primaryColor: '#d8c9a3', accentColor: '#e8f0ff' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 40, hp: 30 },
  },
  'paladin_t3_chest': {
    id: 'paladin_t3_chest',
    name: 'Cuirass of the Last Vigil',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t3_chest.png',
    description: 'This breastplate seals relic parchment and saint-bone slivers between consecrated layers of gold and steel. Blows land on it like accusations and are answered with the same unshaken oath: the Light does not retreat just because the battlefield smells of death.',
    equipmentSlot: 'top' as const,
    setName: 'paladin_t3_set',
    visual: { itemId: 'paladin_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'plate', variant: 5, primaryColor: '#d8c9a3', accentColor: '#e8f0ff' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 45, hp: 35 },
  },
  'paladin_t3_legs': {
    id: 'paladin_t3_legs',
    name: 'Greaves of the Last Vigil',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['plate', 'paladin', 'set_paladin_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PALADIN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/paladin_t3_legs.png',
    description: 'Greaves etched with litany lines from ankle to knee, meant for the weary march from chapel to charnel house. They were made for paladins who keep walking after everyone else has knelt, either in prayer or defeat.',
    equipmentSlot: 'bottom' as const,
    setName: 'paladin_t3_set',
    visual: { itemId: 'paladin_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'plate', variant: 5, primaryColor: '#d8c9a3', accentColor: '#e8f0ff' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 40, hp: 30 },
  },
  'mage_t3_helm': {
    id: 'mage_t3_helm',
    name: 'Rimecandle Circlet',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t3_helm.png',
    description: 'A circlet of silver filigree and iceglass that keeps six tiny witch-lights burning around the brow. The flames never warm the skin; they exist to illuminate sigils, margins, and the final half-second before something in the dark reaches you.',
    equipmentSlot: 'hat' as const,
    setName: 'mage_t3_set',
    visual: { itemId: 'mage_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 5, primaryColor: '#a8c8e8', accentColor: '#f7fbff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'mage_t3_chest': {
    id: 'mage_t3_chest',
    name: 'Rimecandle Raiment',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t3_chest.png',
    description: 'Layered with geometric wards stitched in frost-blue thread, this raiment turns the body into a walking theorem. Cold gathers along the hems, pages stay dry in blizzards, and hostile magic hesitates as though forced to solve for permission.',
    equipmentSlot: 'top' as const,
    setName: 'mage_t3_set',
    visual: { itemId: 'mage_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 5, primaryColor: '#a8c8e8', accentColor: '#f7fbff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 45, hp: 35 },
  },
  'mage_t3_legs': {
    id: 'mage_t3_legs',
    name: 'Rimecandle Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'mage', 'set_mage_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.MAGE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/mage_t3_legs.png',
    description: 'These leggings are cut for long nights in frozen libraries and longer duels on cracked stone. Their lining holds a patient chill that steadies the pulse, narrows the mind, and makes panic feel embarrassingly imprecise.',
    equipmentSlot: 'bottom' as const,
    setName: 'mage_t3_set',
    visual: { itemId: 'mage_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 5, primaryColor: '#a8c8e8', accentColor: '#f7fbff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'warlock_t3_helm': {
    id: 'warlock_t3_helm',
    name: 'Ashwake Veil',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t3_helm.png',
    description: 'A funeral veil sewn with void-silk and ember thread, light enough to drift when there is no wind. It softens the edges of the wearer\'s face until only the eyes remain clear, two coals studying where the soul will split first.',
    equipmentSlot: 'hat' as const,
    setName: 'warlock_t3_set',
    visual: { itemId: 'warlock_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'mask', variant: 5, primaryColor: '#241b2b', accentColor: '#d95c4f' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'warlock_t3_chest': {
    id: 'warlock_t3_chest',
    name: 'Ashwake Vestment',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t3_chest.png',
    description: 'This vestment is lined with seals written on skin-thin parchment and stitched shut before the ink can dry. The garment drinks in stray whispers, stores them near the heart, and lets them out only when the caster speaks a name that should not be known.',
    equipmentSlot: 'top' as const,
    setName: 'warlock_t3_set',
    visual: { itemId: 'warlock_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 5, primaryColor: '#241b2b', accentColor: '#d95c4f' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 45, hp: 35 },
  },
  'warlock_t3_legs': {
    id: 'warlock_t3_legs',
    name: 'Ashwake Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'warlock', 'set_warlock_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.WARLOCK,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/warlock_t3_legs.png',
    description: 'Ashwake leggings trail a faint scent of smoke and graveflowers with every step. They are tailored for ritual circles and patient ruin, allowing a warlock to move like a mourner while binding things that have never once mourned anything.',
    equipmentSlot: 'bottom' as const,
    setName: 'warlock_t3_set',
    visual: { itemId: 'warlock_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 5, primaryColor: '#241b2b', accentColor: '#d95c4f' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'priest_t3_helm': {
    id: 'priest_t3_helm',
    name: 'Vespercrypt Halo',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t3_helm.png',
    description: 'A narrow halo of silver prayer-rings that chime only in catacombs and plague wards. Its light is soft, almost apologetic, but the dead recoil from it all the same.',
    equipmentSlot: 'hat' as const,
    setName: 'priest_t3_set',
    visual: { itemId: 'priest_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 5, primaryColor: '#d9d9e8', accentColor: '#fff8d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, mp: 30 },
  },
  'priest_t3_chest': {
    id: 'priest_t3_chest',
    name: 'Vespercrypt Robe',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t3_chest.png',
    description: 'These robes were woven for healers who keep vigil where incense cannot drown out the smell of opened graves. Layer after pale layer catches stray fear and turns it into measured breath, letting the priest stand calm in the exact place panic would do the most harm.',
    equipmentSlot: 'top' as const,
    setName: 'priest_t3_set',
    visual: { itemId: 'priest_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'robe', variant: 5, primaryColor: '#d9d9e8', accentColor: '#fff8d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 45, mp: 35 },
  },
  'priest_t3_legs': {
    id: 'priest_t3_legs',
    name: 'Vespercrypt Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['cloth', 'priest', 'set_priest_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.PRIEST,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/priest_t3_legs.png',
    description: 'The hem is weighted with tiny ward-discs so the cloth never tangles during a midnight procession or a desperate retreat. It moves with the hush of liturgy, carrying the wearer through plague-dark corridors as though escorted by unseen acolytes.',
    equipmentSlot: 'bottom' as const,
    setName: 'priest_t3_set',
    visual: { itemId: 'priest_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 5, primaryColor: '#d9d9e8', accentColor: '#fff8d8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, mp: 30 },
  },
  'rogue_t3_helm': {
    id: 'rogue_t3_helm',
    name: 'Gravewhisper Hood',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t3_helm.png',
    description: 'A hood of burial-black leather with a mask thin as second skin beneath it. Sound slips off the cowl, torchglow dies in its folds, and the eyes behind it learn to measure a throat from across a room.',
    equipmentSlot: 'hat' as const,
    setName: 'rogue_t3_set',
    visual: { itemId: 'rogue_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 5, primaryColor: '#18181b', accentColor: '#6b7280' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 40, hp: 30 },
  },
  'rogue_t3_chest': {
    id: 'rogue_t3_chest',
    name: 'Gravewhisper Jerkin',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t3_chest.png',
    description: 'The jerkin is cut close and reinforced with narrow bands of flexible grave-steel that never catch on window ledges or rib bones. It was built for assassins who treat silence as a weapon and patience as the final form of speed.',
    equipmentSlot: 'top' as const,
    setName: 'rogue_t3_set',
    visual: { itemId: 'rogue_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 5, primaryColor: '#18181b', accentColor: '#6b7280' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 45, hp: 35 },
  },
  'rogue_t3_legs': {
    id: 'rogue_t3_legs',
    name: 'Gravewhisper Legwraps',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'rogue', 'set_rogue_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.ROGUE,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/rogue_t3_legs.png',
    description: 'Tight-wrapped leathers with hidden blades sewn along the seams for emergencies that refuse to stay discreet. They favor low stances, sudden lunges, and the patient stalk of a predator that already chose its meal hours ago.',
    equipmentSlot: 'bottom' as const,
    setName: 'rogue_t3_set',
    visual: { itemId: 'rogue_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'leggings', variant: 5, primaryColor: '#18181b', accentColor: '#6b7280' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 40, hp: 30 },
  },
  'druid_t3_helm': {
    id: 'druid_t3_helm',
    name: 'Winterbloom Antlers',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t3_helm.png',
    description: 'A headdress of pale antler, moon-bark, and lichen that never quite freezes over. Even inside the Hollow Necropolis it smells of thawing groves, as if some stubborn pocket of spring followed the druid in and refused to die.',
    equipmentSlot: 'hat' as const,
    setName: 'druid_t3_set',
    visual: { itemId: 'druid_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'antlers', variant: 5, primaryColor: '#d9e8d2', accentColor: '#9cc8b0' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'druid_t3_chest': {
    id: 'druid_t3_chest',
    name: 'Winterbloom Vest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t3_chest.png',
    description: 'This vest binds supple hide to living bark panels that harden when shadow magic presses too near. Thin roots on the inner lining curl gently against the skin, taking the wearer\'s pulse like a forest checking whether one of its own is still standing.',
    equipmentSlot: 'top' as const,
    setName: 'druid_t3_set',
    visual: { itemId: 'druid_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 5, primaryColor: '#d9e8d2', accentColor: '#9cc8b0' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 45, hp: 35 },
  },
  'druid_t3_legs': {
    id: 'druid_t3_legs',
    name: 'Winterbloom Kilt',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['leather', 'druid', 'set_druid_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.DRUID,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/druid_t3_legs.png',
    description: 'Layered skirts of fur, moss-cloth, and rune-braided leather made for stalking through dead snow without surrendering to it. Every stride carries the memory of moonpools and winter branches, life advancing where undeath insists nothing should grow.',
    equipmentSlot: 'bottom' as const,
    setName: 'druid_t3_set',
    visual: { itemId: 'druid_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'skirt', variant: 5, primaryColor: '#d9e8d2', accentColor: '#9cc8b0' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'hunter_t3_helm': {
    id: 'hunter_t3_helm',
    name: 'Carrionstalker Mask',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t3_helm.png',
    description: 'Bone toggles and plague-cured leather frame the face without narrowing the sightline. The mask teaches the hunter\'s oldest discipline: watch the corpse pile, not the doorway, because the thing that feeds there is never far behind.',
    equipmentSlot: 'hat' as const,
    setName: 'hunter_t3_set',
    visual: { itemId: 'hunter_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'mask', variant: 5, primaryColor: '#5a4a39', accentColor: '#b8aa8a' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 40, hp: 30 },
  },
  'hunter_t3_chest': {
    id: 'hunter_t3_chest',
    name: 'Carrionstalker Harness',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t3_chest.png',
    description: 'A harness built from layered hide, tendon cord, and mail links dulled to a graveyard sheen. It spreads the recoil of heavy shots across the ribs and carries the calm practical smell of oil, feathers, and old blood.',
    equipmentSlot: 'top' as const,
    setName: 'hunter_t3_set',
    visual: { itemId: 'hunter_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 5, primaryColor: '#5a4a39', accentColor: '#b8aa8a' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 45, hp: 35 },
  },
  'hunter_t3_legs': {
    id: 'hunter_t3_legs',
    name: 'Carrionstalker Legguards',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'hunter', 'set_hunter_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.HUNTER,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hunter_t3_legs.png',
    description: 'These legguards are cut for kneeling in wet grass, climbing broken masonry, and tracking through places where even wolves hesitate. Mud does not cling to them long; plague dust does, a reminder that the quarry in the Hollow Necropolis rarely bleeds only once.',
    equipmentSlot: 'bottom' as const,
    setName: 'hunter_t3_set',
    visual: { itemId: 'hunter_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'mail', variant: 5, primaryColor: '#5a4a39', accentColor: '#b8aa8a' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 40, hp: 30 },
  },
  'shaman_t3_helm': {
    id: 'shaman_t3_helm',
    name: 'Stormwake Coif',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t3_helm.png',
    description: 'Mail rings woven around carved stormbone fetishes, each one humming when necromancy thickens the air. The coif keeps the ears open to harder things than thunder: ancestor warning, spirit anger, and the crackle before a curse lands.',
    equipmentSlot: 'hat' as const,
    setName: 'shaman_t3_set',
    visual: { itemId: 'shaman_t3_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 5, primaryColor: '#58616b', accentColor: '#6bc5e8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'shaman_t3_chest': {
    id: 'shaman_t3_chest',
    name: 'Stormwake Hauberk',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t3_chest.png',
    description: 'This hauberk hangs with totem-plates etched in the old elemental bargains, bronze gone dark with rain and ash. When the shaman calls lightning inside the Hollow Necropolis, the metal answers first, rattling like distant drums before the sky obeys.',
    equipmentSlot: 'top' as const,
    setName: 'shaman_t3_set',
    visual: { itemId: 'shaman_t3_chest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'mail', variant: 5, primaryColor: '#58616b', accentColor: '#6bc5e8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 45, hp: 35 },
  },
  'shaman_t3_legs': {
    id: 'shaman_t3_legs',
    name: 'Stormwake Kilt',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['mail', 'shaman', 'set_shaman_t3_set', 'tier3', 'hollow_necropolis'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    requiredClass: CharacterClass.SHAMAN,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/shaman_t3_legs.png',
    description: 'Stormwake\'s lower mail is split for dancing footwork rather than marching drill, built for shamans who cast as they advance. Tassels of wolf hair and bead-strung charms snap at the knees, keeping pace with every stomp that tells the dead they are no longer welcome.',
    equipmentSlot: 'bottom' as const,
    setName: 'shaman_t3_set',
    visual: { itemId: 'shaman_t3_legs', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'skirt', variant: 5, primaryColor: '#58616b', accentColor: '#6bc5e8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 30 },
  },
  'benediction': {
    id: 'benediction',
    name: 'Benediction',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'epic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/benediction.png',
    description: 'Born of a trial in fire and faith, Benediction answers only to a priest who can hold compassion and judgment in the same steady hand. Its shaft glows with dawn-pale grace until shadow is invited in, at which point the staff remembers Anathema and shows how thin the line between miracle and condemnation has always been.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'benediction', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 3, primaryColor: '#f2d27d', accentColor: '#fff7d6' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 22, mp: 18 },
  },
  'elderwood_longbow': {
    id: 'elderwood_longbow',
    name: 'Elderwood, Longbow of the Old Grove',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'epic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/elderwood_longbow.png',
    description: 'The old grove\'s wardens shaped Elderwood for hunters willing to face monsters alone and return without applause. Its limbs draw with the patient strength of old forests, and every arrow loosed from its string carries the judgment of something older than kingdoms.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'elderwood_longbow', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'bow', variant: 3, primaryColor: '#557a3e', accentColor: '#d9c77a' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 20, hp: 10 },
  },
  'magmaheart_maul': {
    id: 'magmaheart_maul',
    name: 'Magmaheart Maul',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'epic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/magmaheart_maul.png',
    description: 'A fragment of a volcano\'s wrath hammered into mortal scale, the Magmaheart Maul swings like a piece of a burning throne torn loose and set ablaze. When it lands, the air bursts open in sparks and molten breath, reminding everyone nearby that some weapons were never meant to cool.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'magmaheart_maul', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'mace', variant: 3, primaryColor: '#8f250f', accentColor: '#ff8a1f' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 15, hp: 12 },
  },
  'last_breath': {
    id: 'last_breath',
    name: 'Last Breath',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'epic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/last_breath.png',
    description: 'Last Breath is all appetite: a narrow edge, a perfect balance, and a point that seems to arrive before the hand has finished the thought. Rogues prize it because the dagger does not merely kill cleanly; it makes murder feel inevitable.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'last_breath', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'dagger', variant: 3, primaryColor: '#52206d', accentColor: '#ff7043' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 18 },
  },
  'frostgrief': {
    id: 'frostgrief',
    name: 'Frostgrief',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'epic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 17000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/frostgrief.png',
    description: 'The blade weeps frost that never melts. A smith who failed a cold master was frozen into its edge, and those who wield it hear his whispers in a language older than any kingdom. The wounds it leaves never quite stop aching.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'frostgrief', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 3, primaryColor: '#8aa7c4', accentColor: '#d9f1ff' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 22, hp: 15 },
  },
  'wardens_greatstaff': {
    id: 'wardens_greatstaff',
    name: 'Greatstaff of the Last Warden',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'legendary'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 35000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/wardens_greatstaff.png',
    description: 'The staff of the last Warden, shattered when she fell and scattered as trophies. Reassembled from forty splinters by a mage of extraordinary will, it hums with what every Warden before her learned. The owl perched atop it is not carved. It watches.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'wardens_greatstaff', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'staff', variant: 3, primaryColor: '#6b5034', accentColor: '#b99cff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 35, hp: 20 },
  },
  'hellbound_glaive_mh': {
    id: 'hellbound_glaive_mh',
    name: 'Hellbound Glaive (Main Hand)',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'legendary'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 70,
    sellPrice: 40000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hellbound_glaive_mh.png',
    description: 'Torn from a demon lord\'s fists, this glaive burns with trapped hellfire that sears through armor like parchment. The blade shifts and reforms with each swing, never quite the same shape twice.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'hellbound_glaive_mh', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'glaive', variant: 3, primaryColor: '#38a34a', accentColor: '#b8ff5a' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 30, hp: 20 },
  },
  'hellbound_glaive_oh': {
    id: 'hellbound_glaive_oh',
    name: 'Hellbound Glaive (Off Hand)',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'legendary'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 70,
    sellPrice: 40000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/hellbound_glaive_oh.png',
    description: 'The twin to its counterpart — lighter, faster, and somehow hungrier. When both glaives are wielded together, their fires synchronize into a resonance that makes demons flinch.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'hellbound_glaive_oh', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'glaive', variant: 4, primaryColor: '#38a34a', accentColor: '#b8ff5a' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 28, hp: 18 },
  },
  'skyshard': {
    id: 'skyshard',
    name: 'Skyshard, the Bow of Falling Light',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'legendary'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 70,
    sellPrice: 40000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/skyshard.png',
    description: 'Skyshard needs no arrows — it creates its own from compressed starlight, each one bright enough to leave afterimages in the dark. The string hums with a note that sounds like the last star before dawn.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'skyshard', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'bow', variant: 4, primaryColor: '#6aa6ff', accentColor: '#f7e98e' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 25, hp: 15 },
  },
  'kingsmend': {
    id: 'kingsmend',
    name: 'Kingsmend, Hammer of the First Crowns',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'legendary'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 80,
    sellPrice: 45000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/kingsmend.png',
    description: 'Forged for the first crowned kings and broken in the war that ended their line, this hammer was mended by mortal hands. Each heal channeled through it creates a barrier of golden light around the wounded.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'kingsmend', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'mace', variant: 4, primaryColor: '#d6a93b', accentColor: '#fff2a8' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 40, hp: 25 },
  },
  'gloomharrow': {
    id: 'gloomharrow',
    name: 'Gloomharrow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'legendary'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 80,
    sellPrice: 45000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/equipment/gloomharrow.png',
    description: 'Forged to end a cursed blade, not to match it. Gloomharrow was quenched in the grief of a thousand souls who gave themselves willingly. It was made to kill a king. It remembers its purpose.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'gloomharrow', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'axe', variant: 4, primaryColor: '#202737', accentColor: '#9b6bd3' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 50, hp: 30 },
  },
  'enchanted_sword_mage': {
    id: 'enchanted_sword_mage',
    name: 'EnchantedSword_Mage',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 75,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/enchanted_sword_mage.png',
    description: 'A blade inscribed with arcane runes that glow faintly blue. Mages carry these not because they plan to melee, but because the enchantment channels spillover mana into the weapon. (durability value uncertain)',
    stats: {},
  },
  'dagger': {
    id: 'dagger',
    name: 'Dagger',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 7,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/dagger.png',
    description: 'Short, sharp, and meant for close work. The dagger is the weapon of last resort and first choice, depending on your class. (durability value uncertain)',
    stats: {},
  },
  'bow': {
    id: 'bow',
    name: 'Bow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/bow.png',
    description: 'A recurve bow of yew and sinew. It kills at distance, which is the polite way to fight. (durability value uncertain)',
    stats: {},
  },
  'axe': {
    id: 'axe',
    name: 'Axe',
    type: 'weapon' as ItemType,
    category: 'weapon',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 3,
    sellPrice: 14,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/axe.png',
    description: 'A weapon that makes no pretense of elegance. The axe chops through armor, shields, and excuses with equal efficiency. (durability value uncertain)',
    stats: {},
  },
  'iron_longsword': {
    id: 'iron_longsword',
    name: 'Iron Longsword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'sword'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/iron_longsword.png',
    description: 'A sturdy iron blade.',
    stats: { attack: 8 },
  },
  'bastard_sword_rare': {
    id: 'bastard_sword_rare',
    name: 'Bastard Sword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'sword'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 300,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/bastard_sword_rare.png',
    description: 'A versatile blade for one or two hands.',
    stats: { attack: 42, strength: 8, hp: 5 },
  },
  'zweihander_epic': {
    id: 'zweihander_epic',
    name: 'Zweihander',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'sword', 'two_hand'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 45,
    sellPrice: 2000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/zweihander_epic.png',
    description: 'A massive two-handed sword.',
    stats: { attack: 85, strength: 18 },
  },
  'iron_tomahawk': {
    id: 'iron_tomahawk',
    name: 'Tomahawk',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'axe'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/iron_tomahawk.png',
    description: 'A light throwing axe.',
    stats: { attack: 7 },
  },
  'iron_mace': {
    id: 'iron_mace',
    name: 'Iron Mace',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'mace'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 18,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/iron_mace.png',
    description: 'A heavy iron mace.',
    stats: { attack: 7 },
  },
  'hunting_shortbow': {
    id: 'hunting_shortbow',
    name: 'Shortbow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['ranged', 'bow'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 20,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/hunting_shortbow.png',
    description: 'A compact bow for quick shots.',
    stats: { attack: 6 },
  },
  'hunters_longbow': {
    id: 'hunters_longbow',
    name: 'Longbow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['ranged', 'bow'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/hunters_longbow.png',
    description: 'A tall bow with excellent range.',
    stats: { attack: 22, dexterity: 5 },
  },
  'composite_bow_rare': {
    id: 'composite_bow_rare',
    name: 'Composite Bow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['ranged', 'bow'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 24,
    sellPrice: 280,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/composite_bow_rare.png',
    description: 'A powerful layered bow of horn and sinew.',
    stats: { attack: 38, dexterity: 10 },
  },
  'steel_dagger': {
    id: 'steel_dagger',
    name: 'Steel Dagger',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'dagger'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/steel_dagger.png',
    description: 'A sharp dagger for quick strikes.',
    stats: { attack: 6 },
  },
  'iron_spear': {
    id: 'iron_spear',
    name: 'Iron Spear',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'polearm'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 6,
    sellPrice: 22,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/iron_spear.png',
    description: 'A basic spear with sharp iron head.',
    stats: { attack: 9 },
  },
  'bardiche_rare': {
    id: 'bardiche_rare',
    name: 'Bardiche',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'polearm'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 28,
    sellPrice: 350,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/bardiche_rare.png',
    description: 'A heavy polearm with a long cutting blade.',
    stats: { attack: 48, strength: 10, hp: 6 },
  },
  'steel_glaive': {
    id: 'steel_glaive',
    name: 'Glaive',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'polearm'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 90,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/steel_glaive.png',
    description: 'A curved blade on a long shaft.',
    stats: { attack: 20, dexterity: 4 },
  },
  'oak_quarterstaff': {
    id: 'oak_quarterstaff',
    name: 'Quarterstaff',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['caster', 'staff'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/oak_quarterstaff.png',
    description: 'A simple wooden fighting staff.',
    stats: { magicAttack: 4, hp: 2 },
  },
  'repeating_crossbow_epic': {
    id: 'repeating_crossbow_epic',
    name: 'Repeating Crossbow',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['ranged', 'crossbow'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 38,
    sellPrice: 1600,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/repeating_crossbow_epic.png',
    description: 'An ingenious mechanism for rapid fire.',
    stats: { attack: 58, dexterity: 12 },
  },
  'leather_whip': {
    id: 'leather_whip',
    name: 'Whip',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['melee', 'exotic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 22,
    sellPrice: 200,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/leather_whip.png',
    description: 'A coiled leather whip with reach.',
    stats: { attack: 30, dexterity: 7 },
  },
  'returning_boomerang': {
    id: 'returning_boomerang',
    name: 'Boomerang',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['ranged', 'exotic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 14,
    sellPrice: 70,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/returning_boomerang.png',
    description: 'A curved throwing weapon that returns.',
    stats: { attack: 16, dexterity: 3 },
  },
  'scales_of_libra': {
    id: 'scales_of_libra',
    name: 'Scales of Libra',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['caster', 'scepter', 'legendary'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/scales_of_libra.png',
    description: 'A mystical scepter balancing life and death.',
    stats: { magicAttack: 50, intelligence: 25, mp: 25 },
  },
  'leather_helm': {
    id: 'leather_helm',
    name: 'Leather Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['head', 'leather'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/leather_helm.png',
    description: 'A simple helm of hardened leather.',
    stats: { hp: 3, dexterity: 2 },
  },
  'plate_helm': {
    id: 'plate_helm',
    name: 'Plate Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['head', 'plate'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 120,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/plate_helm.png',
    description: 'A heavy helm of forged steel.',
    stats: { hp: 10, strength: 6 },
  },
  'plate_chestpiece': {
    id: 'plate_chestpiece',
    name: 'Plate Chestpiece',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['chest', 'plate'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 150,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/plate_chestpiece.png',
    description: 'Full plate chest deflecting blows.',
    stats: { hp: 12, strength: 8 },
  },
  'plate_leggings': {
    id: 'plate_leggings',
    name: 'Plate Leggings',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['legs', 'plate'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 130,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/plate_leggings.png',
    description: 'Heavy leggings of polished steel.',
    stats: { hp: 10, strength: 6 },
  },
  'enchanted_silk_robes': {
    id: 'enchanted_silk_robes',
    name: 'Silk Robes',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['chest', 'cloth'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 80,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/enchanted_silk_robes.png',
    description: 'Fine silk humming with arcane energy.',
    stats: { intelligence: 8, mp: 5, magicAttack: 6 },
  },
  'arcane_crown': {
    id: 'arcane_crown',
    name: 'Arcane Crown',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['head', 'cloth'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 30,
    sellPrice: 400,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/arcane_crown.png',
    description: 'A crown amplifying magical prowess.',
    stats: { intelligence: 14, magicAttack: 18 },
  },
  'tough_leather_vest': {
    id: 'tough_leather_vest',
    name: 'Leather Vest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['chest', 'leather'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/tough_leather_vest.png',
    description: 'A tough leather vest.',
    stats: { dexterity: 3, hp: 2 },
  },
  'shadow_cloak': {
    id: 'shadow_cloak',
    name: 'Shadow Cloak',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['back', 'cloth'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 300,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/shadow_cloak.png',
    description: 'A cloak woven from shadow threads.',
    stats: { dexterity: 10, hp: 6 },
  },
  'minor_healing_potion': {
    id: 'minor_healing_potion',
    name: 'Minor Health Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['potion', 'health'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/minor_healing_potion.png',
    description: 'Restores 150 health.',
    stats: {},
  },
  'major_healing_potion': {
    id: 'major_healing_potion',
    name: 'Major Health Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['potion', 'health'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/major_healing_potion.png',
    description: 'Restores 1200 health.',
    stats: {},
  },
  'minor_mana_potion': {
    id: 'minor_mana_potion',
    name: 'Minor Mana Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['potion', 'mana'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/minor_mana_potion.png',
    description: 'Restores 100 mana.',
    stats: {},
  },
  'major_mana_potion': {
    id: 'major_mana_potion',
    name: 'Major Mana Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['potion', 'mana'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 25,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/major_mana_potion.png',
    description: 'Restores 900 mana.',
    stats: {},
  },
  'elixir_of_the_lynx': {
    id: 'elixir_of_the_lynx',
    name: 'Elixir of the Lynx',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['elixir', 'buff'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 30,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/elixir_of_the_lynx.png',
    description: 'Quickens hand and eye: more agility and critical strikes for 30 minutes.',
    stats: {},
  },
  'fire_protection_potion': {
    id: 'fire_protection_potion',
    name: 'Fire Protection Potion',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['potion', 'protection'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 20,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/fire_protection_potion.png',
    description: 'Absorbs 500 fire damage.',
    stats: {},
  },
  'cooked_wolf_meat': {
    id: 'cooked_wolf_meat',
    name: 'Cooked Wolf Meat',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['food', 'meat'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/cooked_wolf_meat.png',
    description: 'Restores 200 health while eating.',
    stats: {},
  },
  'spiced_bear_stew': {
    id: 'spiced_bear_stew',
    name: 'Spiced Bear Stew',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['food', 'cooked', 'buff'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/spiced_bear_stew.png',
    description: 'Restores health and grants stamina buff.',
    stats: {},
  },
  'linen_bandage': {
    id: 'linen_bandage',
    name: 'Linen Bandage',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['bandage', 'first_aid'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/linen_bandage.png',
    description: 'Heals 200 damage over 8 seconds.',
    stats: {},
  },
  'silk_bandage': {
    id: 'silk_bandage',
    name: 'Silk Bandage',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['bandage', 'first_aid'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 15,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/silk_bandage.png',
    description: 'Heals 600 damage over 8 seconds.',
    stats: {},
  },
  'wolf_pelt': {
    id: 'wolf_pelt',
    name: 'Wolf Pelt',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'leather'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/wolf_pelt.png',
    description: 'A rough pelt for leatherworking.',
    stats: {},
  },
  'spider_silk_thread': {
    id: 'spider_silk_thread',
    name: 'Spider Silk',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'cloth'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/spider_silk_thread.png',
    description: 'Strong silk from giant spiders.',
    stats: {},
  },
  'raw_iron_ore': {
    id: 'raw_iron_ore',
    name: 'Iron Ore',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'mining'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/raw_iron_ore.png',
    description: 'Raw iron ore for smelting.',
    stats: {},
  },
  'mithril_ore': {
    id: 'mithril_ore',
    name: 'Mithril Ore',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'mining'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 20,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/mithril_ore.png',
    description: 'A rare silvery ore.',
    stats: {},
  },
  'calmbloom_herb': {
    id: 'calmbloom_herb',
    name: 'Calmbloom',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'herb'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/calmbloom_herb.png',
    description: 'A common white-petalled herb for basic alchemy.',
    stats: {},
  },
  'sungold_root': {
    id: 'sungold_root',
    name: 'Sungold Root',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'herb'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/sungold_root.png',
    description: 'A rare golden root that grows where sunlight pools longest.',
    stats: {},
  },
  'glyphcloth_bolt': {
    id: 'glyphcloth_bolt',
    name: 'Glyphcloth Bolt',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'cloth'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/glyphcloth_bolt.png',
    description: 'A bolt of cloth woven with faint glyphs that hold an enchantment. Used in tailoring.',
    stats: {},
  },
  'gravecleaver': {
    id: 'gravecleaver',
    name: 'Gravecleaver',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'two_hand', 'axe', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 29,
    sellPrice: 350,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/gravecleaver.png',
    description: 'A heavy two-handed axe with a notched, patient edge. It has never needed a second swing.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'gravecleaver', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'axe', variant: 1, primaryColor: '#67605a', accentColor: '#3b241c' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 10, hp: 8 },
  },
  'sword_of_omen': {
    id: 'sword_of_omen',
    name: 'Sword of Omen',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'one_hand', 'sword', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 36,
    sellPrice: 400,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/sword_of_omen.png',
    description: 'A blade humming with dark energy.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'sword_of_omen', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 2, primaryColor: '#34204f', accentColor: '#9d63d6' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 8, dexterity: 5 },
  },
  'emberglass_reaper': {
    id: 'emberglass_reaper',
    name: 'Emberglass Reaper',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'two_hand', 'axe', 'crafted', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 56,
    sellPrice: 1200,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/emberglass_reaper.png',
    description: 'A crafted two-handed axe of folded emberglass. Few smiths ever finish one.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'emberglass_reaper', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'axe', variant: 3, primaryColor: '#77a2b8', accentColor: '#d9f4ff' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 20, hp: 15 },
  },
  'stormbinder': {
    id: 'stormbinder',
    name: 'Stormbinder, Blade of the Open Sky',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'one_hand', 'sword', 'legendary', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/stormbinder.png',
    description: 'A legendary sword that hums before a storm breaks. Lightning leaps from it to whatever it strikes.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'stormbinder', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 4, primaryColor: '#3b82f6', accentColor: '#f5d76e' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 25, hp: 12, intelligence: 8 },
  },
  'cindermaul': {
    id: 'cindermaul',
    name: 'Cindermaul, the Burning Anvil',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'two_hand', 'mace', 'legendary', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 5000,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/cindermaul.png',
    description: 'A legendary hammer that has never cooled. Every blow leaves the ground smouldering.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'cindermaul', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'mace', variant: 4, primaryColor: '#a62910', accentColor: '#ffb12b' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 30, hp: 20 },
  },
  'greenwarden_helm': {
    id: 'greenwarden_helm',
    name: 'Greenwarden Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['armor', 'tier_1', 'druid', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 800,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/greenwarden_helm.png',
    description: 'Druid Tier 1 headpiece of the Greenwarden raiment, taken in the Cinder Deeps.',
    equipmentSlot: 'hat' as const,
    setName: 'greenwarden_raiment',
    visual: { itemId: 'greenwarden_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'antlers', variant: 3, primaryColor: '#55733b', accentColor: '#b78b4b' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 22, intelligence: 18, mp: 15 },
  },
  'duskweaver_cover': {
    id: 'duskweaver_cover',
    name: 'Duskweaver Cover',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['armor', 'tier_1', 'rogue', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 800,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/duskweaver_cover.png',
    description: 'Rogue Tier 1 headpiece of the Duskweaver armor, taken in the Cinder Deeps.',
    equipmentSlot: 'hat' as const,
    setName: 'duskweaver_armor',
    visual: { itemId: 'duskweaver_cover', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'hood', variant: 3, primaryColor: '#231a2b', accentColor: '#7e365c' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 25, hp: 18 },
  },
  'arcanist_crown': {
    id: 'arcanist_crown',
    name: 'Arcanist Crown',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['armor', 'tier_1', 'mage', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 800,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/arcanist_crown.png',
    description: 'Mage Tier 1 headpiece of the Arcanist regalia, taken in the Cinder Deeps.',
    equipmentSlot: 'hat' as const,
    setName: 'arcanist_regalia',
    visual: { itemId: 'arcanist_crown', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 3, primaryColor: '#315a9e', accentColor: '#70d6ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 28, hp: 15, mp: 10 },
  },
  'wyrmbreaker_helm': {
    id: 'wyrmbreaker_helm',
    name: 'Wyrmbreaker Helm',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['armor', 'tier_2', 'warrior', 'classic'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 1200,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/wyrmbreaker_helm.png',
    description: 'Warrior Tier 2 headpiece of the Wyrmbreaker battlegear, taken in the Black Wyrm\'s roost.',
    equipmentSlot: 'hat' as const,
    setName: 'wyrmbreaker_battlegear',
    visual: { itemId: 'wyrmbreaker_helm', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'helm', variant: 4, primaryColor: '#5d2026', accentColor: '#d44b34' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 30, hp: 25, dexterity: 10 },
  },
  'rough_stone': {
    id: 'rough_stone',
    name: 'Rough Stone',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent', 'mining'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/rough_stone.png',
    description: 'A rough stone, useful for crafting grinding stones.',
    stats: {},
  },
  'light_feather': {
    id: 'light_feather',
    name: 'Light Feather',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/light_feather.png',
    description: 'Reagent for Slow Fall and Levitate spells.',
    stats: {},
  },
  'vial_empty': {
    id: 'vial_empty',
    name: 'Empty Vial',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent', 'alchemy'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/vial_empty.png',
    description: 'Used by Alchemists to create potions.',
    stats: {},
  },
  'crystal_vial': {
    id: 'crystal_vial',
    name: 'Crystal Vial',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent', 'alchemy'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/crystal_vial.png',
    description: 'Required for high-level Alchemy recipes.',
    stats: {},
  },
  'moorwatch_chausses': {
    id: 'moorwatch_chausses',
    name: 'Moorwatch Chausses',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['quest_reward', 'moor_road'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 16,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/moorwatch_chausses.png',
    description: 'Reward for breaking the bandit gang on the moor road.',
    equipmentSlot: 'bottom' as const,
    visual: { itemId: 'moorwatch_chausses', itemSlot: 'bottom', schemaSlot: 'legs', layer: 'legs', family: 'mail', variant: 0, primaryColor: '#0070dd', accentColor: '#4da6ff' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 5, hp: 4 },
  },
  'firewyrm_amulet': {
    id: 'firewyrm_amulet',
    name: 'Firewyrm Amulet',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['quest_reward', 'attunement'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 55,
    sellPrice: 0,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/firewyrm_amulet.png',
    description: 'Proof that you saw through the dragon\'s disguise. It opens the way into her lair.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'firewyrm_amulet', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'amulet', variant: 3, primaryColor: '#8f250f', accentColor: '#ff8a1f' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 10 },
  },
  'tiger_meat': {
    id: 'tiger_meat',
    name: 'Tiger Meat',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cooking', 'meat'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/tiger_meat.png',
    description: 'Raw meat from jungle tigers. Used in cooking recipes.',
    stats: {},
  },
  'sandswarm_carapace': {
    id: 'sandswarm_carapace',
    name: 'Sandswarm Carapace',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['crafting', 'desert'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/sandswarm_carapace.png',
    description: 'Chitinous shell from a desert swarm-bug. Used in crafting.',
    stats: {},
  },
  'blighted_dawnblade': {
    id: 'blighted_dawnblade',
    name: 'Blighted Dawnblade',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['weapon', 'legendary', 'raid'],
    quantity: 1,
    maxStack: 1,
    rarity: 'legendary' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 0,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/blighted_dawnblade.png',
    description: 'A holy blade carried into the dark that came back changed. Its old light still flickers under the rot.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'blighted_dawnblade', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 4, primaryColor: '#3f342e', accentColor: '#9fcf52' } as ItemEquipmentVisualDescriptor,
    stats: { strength: 30, hp: 20 },
  },
  'fishing_pole_basic': {
    id: 'fishing_pole_basic',
    name: 'Fishing Pole',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['tool', 'fishing'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/fishing_pole_basic.png',
    description: 'A basic fishing pole. Equip it to fish in bodies of water.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'fishing_pole_basic', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'fishing_pole', variant: 0, primaryColor: '#7a5331', accentColor: '#c6a15b' } as ItemEquipmentVisualDescriptor,
    stats: {},
  },
  'sandstinger_scale': {
    id: 'sandstinger_scale',
    name: 'Sandstinger Scale',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material', 'mob_drop'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/sandstinger_scale.png',
    description: 'A scale from a sandstinger. Used in leatherworking.',
    stats: {},
  },
  'dragon_scale': {
    id: 'dragon_scale',
    name: 'Dragon Scale',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material', 'mob_drop'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/dragon_scale.png',
    description: 'A scale from a dragonkin. Used in high-level crafting.',
    stats: {},
  },
  'dragon_fang': {
    id: 'dragon_fang',
    name: 'Dragon Fang',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material', 'mob_drop'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/dragon_fang.png',
    description: 'A fang from a dragonkin. Rare crafting material.',
    stats: {},
  },
  'fire_essence': {
    id: 'fire_essence',
    name: 'Fire Essence',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material', 'mob_drop'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/fire_essence.png',
    description: 'Concentrated fire elemental energy.',
    stats: {},
  },
  'strange_dust': {
    id: 'strange_dust',
    name: 'Strange Dust',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['enchanting', 'material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/strange_dust.png',
    description: 'Enchanting material from disenchanting level 1-20 uncommon items.',
    stats: {},
  },
  'soul_dust': {
    id: 'soul_dust',
    name: 'Soul Dust',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['enchanting', 'material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/soul_dust.png',
    description: 'Enchanting material from disenchanting level 21-30 uncommon items.',
    stats: {},
  },
  'vision_dust': {
    id: 'vision_dust',
    name: 'Vision Dust',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['enchanting', 'material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/vision_dust.png',
    description: 'Enchanting material from disenchanting level 31-40 uncommon items.',
    stats: {},
  },
  'dream_dust': {
    id: 'dream_dust',
    name: 'Dream Dust',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['enchanting', 'material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 8,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/dream_dust.png',
    description: 'Enchanting material from disenchanting level 41-50 uncommon items.',
    stats: {},
  },
  'illusion_dust': {
    id: 'illusion_dust',
    name: 'Illusion Dust',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['enchanting', 'material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/illusion_dust.png',
    description: 'Enchanting material from disenchanting level 51-60 uncommon items.',
    stats: {},
  },
  'pristine_dragon_scale': {
    id: 'pristine_dragon_scale',
    name: 'Pristine Dragon Scale',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material', 'dragonkin'],
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/pristine_dragon_scale.png',
    description: 'A flawless scale from a powerful dragon. Used in Leatherworking.',
    stats: {},
  },
  'inferno_core': {
    id: 'inferno_core',
    name: 'Inferno Core',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material', 'raid', 'cinder_deeps'],
    quantity: 1,
    maxStack: 10,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/inferno_core.png',
    description: 'A smoldering core from the deepest fire caverns. A crafting reagent.',
    stats: {},
  },
  'wolf_meat': {
    id: 'wolf_meat',
    name: 'Wolf Meat',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cooking', 'meat'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/wolf_meat.png',
    description: 'Raw wolf meat. Used in Cooking recipes.',
    stats: {},
  },
  'starsteel_bar': {
    id: 'starsteel_bar',
    name: 'Starsteel Bar',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/starsteel_bar.png',
    description: 'An epic crafting bar. It takes eight hours to smelt.',
    stats: {},
  },
  'frozen_orb': {
    id: 'frozen_orb',
    name: 'Frozen Orb',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/frozen_orb.png',
    description: 'A shard of cold that never thaws, carried by the ice wraiths of Frostspire Peaks. Used in crafting.',
    stats: {},
  },
  'champions_orb': {
    id: 'champions_orb',
    name: 'Champion\'s Orb',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/champions_orb.png',
    description: 'Won in the great tournament\'s trials. Used in epic crafting.',
    stats: {},
  },
  'primordial_gloomsteel': {
    id: 'primordial_gloomsteel',
    name: 'Primordial Gloomsteel',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/primordial_gloomsteel.png',
    description: 'Drawn from the deepest frozen citadel. Used to forge Gloomharrow and other epic gear.',
    stats: {},
  },
  'nightweave': {
    id: 'nightweave',
    name: 'Nightweave',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['material'],
    quantity: 1,
    maxStack: 20,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/nightweave.png',
    description: 'A specialty cloth only tailors can make. Four-day cooldown.',
    stats: {},
  },
  'darkstar_card_greatness': {
    id: 'darkstar_card_greatness',
    name: 'Scribe\'s Omen',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['accessory'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/darkstar_card_greatness.png',
    description: 'A scribe\'s finest trinket. When it triggers, your highest stat rises by 90.',
    stats: {},
  },
  'linen_cloth': {
    id: 'linen_cloth',
    name: 'Linen Cloth',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cloth', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/linen_cloth.png',
    description: 'A rough cloth often found on humanoids.',
    stats: {},
  },
  'wool_cloth': {
    id: 'wool_cloth',
    name: 'Wool Cloth',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cloth', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/wool_cloth.png',
    description: 'A soft cloth used in tailoring.',
    stats: {},
  },
  'silk_cloth': {
    id: 'silk_cloth',
    name: 'Silk Cloth',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cloth', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 6,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/silk_cloth.png',
    description: 'A smooth cloth from higher-level humanoids.',
    stats: {},
  },
  'spellweave_cloth': {
    id: 'spellweave_cloth',
    name: 'Spellweave Cloth',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cloth', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 10,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/spellweave_cloth.png',
    description: 'An enchanted cloth taken from powerful foes.',
    stats: {},
  },
  'glyphcloth': {
    id: 'glyphcloth',
    name: 'Glyphcloth',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cloth', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 15,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/glyphcloth.png',
    description: 'A fine cloth humming with arcane energy.',
    stats: {},
  },
  'frostweave_cloth': {
    id: 'frostweave_cloth',
    name: 'Frostweave Cloth',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['cloth', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 20,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/frostweave_cloth.png',
    description: 'A cold-infused cloth from the far north.',
    stats: {},
  },
  'light_leather': {
    id: 'light_leather',
    name: 'Light Leather',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['leather', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 1,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/light_leather.png',
    description: 'A thin leather from low-level beasts.',
    stats: {},
  },
  'medium_leather': {
    id: 'medium_leather',
    name: 'Medium Leather',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['leather', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/medium_leather.png',
    description: 'A standard leather from mid-level beasts.',
    stats: {},
  },
  'thick_leather': {
    id: 'thick_leather',
    name: 'Thick Leather',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['leather', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 8,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/thick_leather.png',
    description: 'A dense leather from higher-level beasts.',
    stats: {},
  },
  'rugged_leather': {
    id: 'rugged_leather',
    name: 'Rugged Leather',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['leather', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 12,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/rugged_leather.png',
    description: 'A tough leather from high-level beasts.',
    stats: {},
  },
  'tundra_leather': {
    id: 'tundra_leather',
    name: 'Tundra Leather',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['leather', 'material', 'crafting'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 18,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/tundra_leather.png',
    description: 'A cold-weather leather from the beasts of the far north.',
    stats: {},
  },
  'essence_of_fire': {
    id: 'essence_of_fire',
    name: 'Essence of Fire',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent', 'elemental', 'crafting'],
    quantity: 1,
    maxStack: 10,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/essence_of_fire.png',
    description: 'A concentrated elemental essence, hot to the touch.',
    stats: {},
  },
  'essence_of_earth': {
    id: 'essence_of_earth',
    name: 'Essence of Earth',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent', 'elemental', 'crafting'],
    quantity: 1,
    maxStack: 10,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/essence_of_earth.png',
    description: 'A concentrated elemental essence, dense and heavy.',
    stats: {},
  },
  'void_crystal': {
    id: 'void_crystal',
    name: 'Void Crystal',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent', 'enchanting', 'crafting'],
    quantity: 1,
    maxStack: 10,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/void_crystal.png',
    description: 'A shimmering crystal used in high-level enchanting.',
    stats: {},
  },
  'abyss_crystal': {
    id: 'abyss_crystal',
    name: 'Abyss Crystal',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['reagent', 'enchanting', 'crafting'],
    quantity: 1,
    maxStack: 10,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/abyss_crystal.png',
    description: 'A rare crystal from the deepest enchantments.',
    stats: {},
  },
  'gold_ore': {
    id: 'gold_ore',
    name: 'Gold Ore',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 20,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/gold_ore.png',
    description: 'A chunk of heavy gold ore.',
    stats: {},
  },
  'verdance_coalition_armband': {
    id: 'verdance_coalition_armband',
    name: 'Verdance Coalition Armband',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['quest_reward', 'mount_verdance', 'verdance_original', 'phase_2', 'boss_kill_reward', 'rare'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 56,
    sellPrice: 120,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/spider_silk_thread.png',
    description: 'Grove-silk reinforced with coalition chainlinks. Given to those who brought down the demon reaver that threatened the coalition\'s gathering on Mount Verdance. A warden of the old watch sewed the frost-ward thread herself before the march.',
    stats: { hp: 14, dexterity: 10, mp: 8 },
  },
  'verdance_frost_ward_ring': {
    id: 'verdance_frost_ward_ring',
    name: 'Verdance Frost-Ward Ring',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['quest_reward', 'mount_verdance', 'verdance_original', 'phase_3', 'boss_kill_reward', 'rare', 'frost_resist'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 57,
    sellPrice: 135,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/firewyrm_amulet.png',
    description: 'Forged from frost-hardened ember-glass recovered from a lich lieutenant\'s shattered phylactery. Given for carrying his battle plans back to the Elder Tree\'s grove command. The cold never truly leaves the metal.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'verdance_frost_ward_ring', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'ring', variant: 3, primaryColor: '#8aa7c4', accentColor: '#d9f1ff' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 12, hp: 12, mp: 8 },
  },
  'verdance_hornlord_trophy_totem': {
    id: 'verdance_hornlord_trophy_totem',
    name: 'Hornlord Trophy Totem',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['quest_reward', 'mount_verdance', 'verdance_original', 'phase_3', 'boss_kill_reward', 'rare', 'off_hand'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 57,
    sellPrice: 140,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/staff.png',
    description: 'A totem carved from a shard of the demon hornlord\'s horn, etched with the grove wardens\' binding rune. Shamans and druids channel nature\'s rebuke through it; the hornlord\'s corruption stays bound inside.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'verdance_hornlord_trophy_totem', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'totem', variant: 3, primaryColor: '#5f3a22', accentColor: '#79a857' } as ItemEquipmentVisualDescriptor,
    stats: { mp: 15, intelligence: 10, hp: 12 },
  },
  'verdance_worldtree_pendant': {
    id: 'verdance_worldtree_pendant',
    name: 'Worldtree Pendant',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['quest_reward', 'mount_verdance', 'verdance_original', 'phase_4', 'boss_kill_reward', 'rare', 'arc_capstone'],
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 58,
    sellPrice: 160,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/firewyrm_amulet.png',
    description: 'A pendant holding a splinter of the Elder Tree\'s bark, blessed by the archdruids of Mount Verdance after the demon lord fell. The tree\'s breath still pulses faintly inside the wood.',
    equipmentSlot: 'accessory' as const,
    visual: { itemId: 'verdance_worldtree_pendant', itemSlot: 'accessory', schemaSlot: 'neck', layer: 'neck', family: 'pendant', variant: 3, primaryColor: '#6b5034', accentColor: '#9fcf52' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 16, dexterity: 10, intelligence: 10, mp: 10 },
  },
  'worldtree_blessing_crown': {
    id: 'worldtree_blessing_crown',
    name: 'Worldtree Blessing Crown',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['quest_reward', 'mount_verdance', 'verdance_original', 'phase_4', 'arc_capstone', 'epic', 'elder_tree_eulogy'],
    quantity: 1,
    maxStack: 1,
    rarity: 'epic' as ItemRarity,
    requiredLevel: 60,
    sellPrice: 350,
    tradeable: true,
    binding: 'none',
    icon: 'sprites/items/arcane_crown.png',
    description: 'Woven from the Elder Tree\'s outermost boughs, the ones that carried the last prayer of the siege. Its guardian gives it to those who saw the tree spend its blessing so the demons could never return.',
    equipmentSlot: 'hat' as const,
    visual: { itemId: 'worldtree_blessing_crown', itemSlot: 'hat', schemaSlot: 'head', layer: 'head', family: 'crown', variant: 5, primaryColor: '#6f8f52', accentColor: '#d8f2a3' } as ItemEquipmentVisualDescriptor,
    stats: { hp: 22, intelligence: 18, mp: 14, dexterity: 12 },
  },
  'rough_grinding_stone': {
    id: 'rough_grinding_stone',
    name: 'Rough Grinding Stone',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['classic', 'material', 'blacksmithing', 'tradeskill'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 6,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Stone_Grindingstone_01.png',
    description: 'A crude grinding stone used in the creation of blacksmithing weapons and armor. Made from rough stone at a forge.',
    stats: {},
  },
  'weak_flux': {
    id: 'weak_flux',
    name: 'Weak Flux',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['classic', 'reagent', 'blacksmithing', 'tradeskill'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Misc_Powder_Black.png',
    description: 'A cheap flux compound sold by blacksmiths. Required for basic blacksmithing recipes.',
    stats: {},
  },
  'copper_chain_belt': {
    id: 'copper_chain_belt',
    name: 'Copper Chain Belt',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['classic', 'armor', 'mail', 'belt', 'blacksmithing'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 25,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Belt_03.png',
    description: 'A sturdy belt fashioned from copper chain links. Light protection for beginning adventurers.',
    stats: { defense: 35 },
  },
  'copper_chain_boots': {
    id: 'copper_chain_boots',
    name: 'Verdigris Glint Trail',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['classic', 'armor', 'mail', 'trail', 'blacksmithing'],
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 10,
    sellPrice: 20,
    tradeable: true,
    binding: 'none',
    icon: 'trail_verdigris',
    description: 'Green-blue glints, like old copper catching the sun. They hang low and steady behind you.',
    equipmentSlot: 'shoes' as const,
    visual: { itemId: 'copper_chain_boots', itemSlot: 'shoes', schemaSlot: 'feet', layer: 'feet', family: 'sabatons', variant: 0, primaryColor: '#b87333', accentColor: '#e0a06b' } as ItemEquipmentVisualDescriptor,
    trail: { color: '#6fe0b8', glow: 0.6, size: 12, count: 4, rise: 1 } as ItemTrailStyle,
    stats: { defense: 48 },
  },
  'drink_hearthside_mild': {
    id: 'drink_hearthside_mild',
    name: 'Hearthside Mild',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['classic', 'consumable', 'drink', 'inn', 'hearthside'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Drink_05.png',
    description: 'A mild ale served at roadside inns. Restores mana over 30 seconds while sitting. \'Mild\' is a relative term.',
    stats: {},
  },
  'drink_cellar_milk': {
    id: 'drink_cellar_milk',
    name: 'Cellar-Cold Milk',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['classic', 'consumable', 'drink', 'inn', 'hearthside'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Drink_14.png',
    description: 'Fresh milk kept cold by the innkeeper\'s cellar. Restores mana over 30 seconds while sitting.',
    stats: {},
  },
  'food_rye_bread': {
    id: 'food_rye_bread',
    name: 'Rye Bread',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['classic', 'consumable', 'food', 'inn', 'hearthside'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 2,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Misc_Food_09.png',
    description: 'Dense dark bread sold at roadside inns. Restores health over 30 seconds while sitting.',
    stats: {},
  },
  'food_smoked_bear_meat': {
    id: 'food_smoked_bear_meat',
    name: 'Smoked Bear Meat',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['classic', 'consumable', 'food', 'inn', 'hearthside'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Misc_Food_15.png',
    description: 'Thick cuts of bear meat slow-smoked by the innkeeper\'s cook. Restores health over 30 seconds while sitting.',
    stats: {},
  },
  'food_hearthside_pie': {
    id: 'food_hearthside_pie',
    name: 'Hearthside Pie',
    type: 'consumable' as ItemType,
    category: 'consumable',
    tags: ['classic', 'consumable', 'food', 'inn', 'hearthside'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 3,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Misc_Food_11.png',
    description: 'A warm pie baked fresh at a village inn. Restores health over 30 seconds while sitting.',
    stats: {},
  },
  'bronze_bar': {
    id: 'bronze_bar',
    name: 'Bronze Bar',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['metal', 'bar', 'smithing', 'material', 'crafting', 'blacksmithing'],
    quantity: 1,
    maxStack: 20,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 6,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Ingot_Bronze.png',
    description: 'An alloy of copper and tin, smelted to a warm golden hue. Essential material for Blacksmiths and Engineers training through the Journeyman tier.',
    stats: {},
  },
  'recruit_linen_vest': {
    id: 'recruit_linen_vest',
    name: 'Recruit\'s Linen Vest',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['classic', 'armor', 'cloth', 'chest', 'abbey_valley', 'quest_reward', 'starter', 'caster'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 35,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Chest_Cloth_04.png',
    description: 'A simple linen vest stitched by the abbey seamstress for the kingdom\'s newest conscripts. The weave carries a faint blessing from the resident clerics, a small comfort against the chill of the northern valley.',
    equipmentSlot: 'top' as const,
    visual: { itemId: 'recruit_linen_vest', itemSlot: 'top', schemaSlot: 'chest', layer: 'chest', family: 'vest', variant: 0, primaryColor: '#d8c7a5', accentColor: '#355b8c' } as ItemEquipmentVisualDescriptor,
    stats: { intelligence: 3, mp: 3 },
  },
  'scouts_leather_spaulders': {
    id: 'scouts_leather_spaulders',
    name: 'Scout\'s Leather Spaulders',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['classic', 'armor', 'leather', 'shoulder', 'abbey_valley', 'quest_reward', 'starter', 'physical'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 2,
    sellPrice: 45,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Shoulder_06.png',
    description: 'Stitched from boar hide by the abbey\'s skinner, these light pauldrons have seen a dozen scouts before you. The lacing is tight and the leather still supple — better protection than it looks.',
    stats: { dexterity: 3, hp: 2 },
  },
  'conscripts_shortsword': {
    id: 'conscripts_shortsword',
    name: 'Conscript\'s Shortsword',
    type: 'weapon' as ItemType,
    category: 'weapon',
    tags: ['classic', 'weapon', 'one_hand', 'sword', 'abbey_valley', 'quest_reward', 'starter'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 40,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Sword_04.png',
    description: 'A standard-issue garrison blade, handed out with a blessing and a sharp word from the drill marshal. The edge holds well. The rest is up to you.',
    equipmentSlot: 'weapon' as const,
    visual: { itemId: 'conscripts_shortsword', itemSlot: 'weapon', schemaSlot: 'mainHand', layer: 'mainHand', family: 'sword', variant: 0, primaryColor: '#a9adb2', accentColor: '#355b8c' } as ItemEquipmentVisualDescriptor,
    stats: { attack: 6 },
  },
  'kobold_cloth_scrap': {
    id: 'kobold_cloth_scrap',
    name: 'Kobold Cloth Scrap',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['classic', 'material', 'cloth', 'mob_drop', 'abbey_valley', 'kobold'],
    quantity: 1,
    maxStack: 5,
    rarity: 'common' as ItemRarity,
    requiredLevel: 0,
    sellPrice: 5,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Fabric_Linen_01.png',
    description: 'A grimy scrap of linen torn from a kobold worker\'s wrapping. Not much to look at, but a tailor might find a use for it.',
    stats: {},
  },
  'brigand_leather_gloves': {
    id: 'brigand_leather_gloves',
    name: 'Brigand Leather Gloves',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['classic', 'armor', 'leather', 'hands', 'mob_drop', 'abbey_valley', 'brigand', 'quest_reward'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 2,
    sellPrice: 30,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Gauntlets_04.png',
    description: 'Cut from stiffened boar leather and marked inside with a bandit gang\'s crossed-knives sign. Whoever wore these last was decent with a knife; the fingers are worn smooth at the grip.',
    equipmentSlot: 'gloves' as const,
    visual: { itemId: 'brigand_leather_gloves', itemSlot: 'gloves', schemaSlot: 'hands', layer: 'hands', family: 'gloves', variant: 1, primaryColor: '#6b382c', accentColor: '#b44435' } as ItemEquipmentVisualDescriptor,
    stats: { dexterity: 2 },
  },
  'gnoll_chieftain_belt': {
    id: 'gnoll_chieftain_belt',
    name: 'Gnoll Chieftain\'s Belt',
    type: 'armor' as ItemType,
    category: 'armor',
    tags: ['classic', 'armor', 'leather', 'waist', 'mob_drop', 'abbey_valley', 'river_clans', 'gnoll_chieftain', 'quest_reward'],
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 50,
    tradeable: true,
    binding: 'none',
    icon: 'Interface/Icons/INV_Belt_04.png',
    description: 'Roughly stitched from thick gnoll hide and knotted with stolen rope. The old chieftain wore it when he ruled the river clans. It smells of campfire and something worse, but it is surprisingly sturdy.',
    stats: { strength: 3, hp: 2 },
  },
  'hearth_lantern': {
    id: 'hearth_lantern',
    name: 'Hearthglow Lantern',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 30,
    tradeable: true,
    binding: 'none',
    icon: 'lantern_hearth',
    description: 'A tin lantern with an ember from the First Hearth inside. It sways at your shoulder and keeps the dark a step back.',
    equipmentSlot: 'lantern' as const,
    lantern: { color: '#ffc46b', glow: 0.75, size: 34, bob: 2, speed: 0.6 } as ItemLanternStyle,
    stats: { luck: 1 },
  },
  'tidelight_lantern': {
    id: 'tidelight_lantern',
    name: 'Tidelight Lantern',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 150,
    tradeable: true,
    binding: 'none',
    icon: 'lantern_tidelight',
    description: 'Sea-glass panes around a light the Tidewend fishers swear was caught at low tide. It rocks like a buoy.',
    equipmentSlot: 'lantern' as const,
    lantern: { color: '#6fe0e8', glow: 0.8, size: 38, bob: 3, speed: 0.8 } as ItemLanternStyle,
    stats: { mp: 10 },
  },
  'gloamwisp_lantern': {
    id: 'gloamwisp_lantern',
    name: 'Gloamwisp Lantern',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 5,
    sellPrice: 400,
    tradeable: true,
    binding: 'on_equip',
    icon: 'lantern_gloamwisp',
    description: 'A wisp of dusk that followed a shadow bat home and would not leave. Carry it and it will not leave you either.',
    equipmentSlot: 'lantern' as const,
    lantern: { color: '#b48cff', glow: 0.9, size: 42, bob: 4, speed: 1.1 } as ItemLanternStyle,
    stats: { intelligence: 2, luck: 2 },
  },
  'glowspore_field_notes': {
    id: 'glowspore_field_notes',
    name: 'Orla\'s Canopy Notes',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 1,
    rarity: 'common' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: 'material_keepers_journal_page',
    description: 'Orla\'s water-spotted sketches of the western canopy. A note in the margin warns that the Keeper can throw loose stone.',
    stats: {},
  },
  'glowspore_heartseed': {
    id: 'glowspore_heartseed',
    name: 'Living Heartseed',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: '🍄',
    description: 'A pale pulse moves through this heartseed from the high crown, even when the surrounding caps go dark.',
    stats: {},
  },
  'glowspore_lantern': {
    id: 'glowspore_lantern',
    name: 'Sporeglass Lantern',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 4,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: 'lantern_tidelight',
    description: 'A living glow from the grotto, sealed in a field researcher\'s glass. It sways beside your shoulder and refuses to go dark.',
    equipmentSlot: 'lantern' as const,
    lantern: { color: '#8edec5', glow: 0.75, size: 36, bob: 2, speed: 0.7 } as ItemLanternStyle,
    stats: { mp: 5 },
  },
  'moonfen_reedwater_tally': {
    id: 'moonfen_reedwater_tally',
    name: 'Reedwater Tally',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: 'material_keepers_journal_page',
    description: 'A careful tally beside the old stilt path: every pig counted through the reedwater, with a second mark beside the ones that needed carrying. The herd did not scatter blindly.',
    stats: {},
  },
  'moonfen_grange_ledger': {
    id: 'moonfen_grange_ledger',
    name: 'Herdkeeper\'s Ledger',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: 'material_keepers_journal_page',
    description: 'The grange ledger follows the families as the lower paddock flooded. Its last clear line reads: Take the dry boards east. Keep one lamp for the watchtower.',
    stats: {},
  },
  'moonfen_last_watch_letter': {
    id: 'moonfen_last_watch_letter',
    name: 'Last Watch Letter',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: 'material_keepers_journal_page',
    description: 'A letter sheltered in the beacon\'s crow nest: The last family has gone on. Orren will keep the watch below. Leave the light where a tired traveller can see it.',
    stats: {},
  },
  'fenwatch_lantern': {
    id: 'fenwatch_lantern',
    name: 'Fenwatch Lantern',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 2,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'lantern_hearth',
    description: 'Orren\'s gift for carrying the pasture\'s story to its last watchtower. A green-gold light moves beside your shoulder, warm as a window seen across dark water.',
    equipmentSlot: 'lantern' as const,
    lantern: { color: '#c6d487', glow: 0.78, size: 36, bob: 2.5, speed: 0.55 } as ItemLanternStyle,
    stats: { luck: 1 },
  },
  'forest_tool_roll': {
    id: 'forest_tool_roll',
    name: 'Hetta\'s Tool Roll',
    type: 'material' as ItemType,
    category: 'material',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 0,
    tradeable: false,
    binding: 'quest',
    icon: 'material_forest_tool_roll',
    description: 'A waxed canvas roll containing a pruning saw, a grafting knife and a whetstone. Hetta\'s initials are stitched into the strap. These tools were made to keep a grove growing, not simply to fell it.',
    stats: {},
  },
  'foresters_mantle': {
    id: 'foresters_mantle',
    name: 'Forester\'s Mantle',
    type: 'armor' as ItemType,
    category: 'armor',
    quantity: 1,
    maxStack: 1,
    rarity: 'uncommon' as ItemRarity,
    requiredLevel: 8,
    sellPrice: 0,
    tradeable: false,
    binding: 'on_pickup',
    icon: 'armor_cape_travelers',
    description: 'A moss-green mantle with a warm, weathered lining. Senn gives it to the traveller who answered the old grove\'s keepers. Its brass clasp bears a leaf wrapped around a pruning blade.',
    equipmentSlot: 'cape' as const,
    visual: { itemId: 'foresters_mantle', itemSlot: 'cape', schemaSlot: 'back', layer: 'back', family: 'cloak', variant: 0, primaryColor: '#49654d', accentColor: '#c6a46b' } as ItemEquipmentVisualDescriptor,
    stats: { defense: 8, hp: 20 },
  },
  'grundas_gilded_tusk': {
    id: 'grundas_gilded_tusk',
    name: 'Grunda\'s Gilded Tusk',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'goldengrass_savanna'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 45,
    tradeable: true,
    binding: 'none',
    icon: 'material_grundas_gilded_tusk',
    description: 'A boar tusk wrapped in hammered gold leaf, the badge of the matron who counts the Tuskmire stores. The raiders swear she knows when one bead is missing.',
    stats: {},
  },
  'glimmergills_lucky_shard': {
    id: 'glimmergills_lucky_shard',
    name: 'Glimmergill\'s Lucky Shard',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'murkwater_swamp'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 75,
    tradeable: true,
    binding: 'none',
    icon: 'material_glimmergills_lucky_shard',
    description: 'A shard of green bottle glass polished to a shine by years in a Gilling\'s fist. Of everything in his hoard, this was the piece he would not trade.',
    stats: {},
  },
  'sunbacks_bleached_carapace': {
    id: 'sunbacks_bleached_carapace',
    name: 'Sunback\'s Bleached Carapace',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'sunkissed_shore'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 100,
    tradeable: true,
    binding: 'none',
    icon: 'material_sunbacks_bleached_carapace',
    description: 'A plate of sandstinger shell baked white by a hundred summers. Shore folk set them over doorways to keep the tide polite.',
    stats: {},
  },
  'galeclaws_storm_feather': {
    id: 'galeclaws_storm_feather',
    name: 'Galeclaw\'s Storm Feather',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'stormbreak_cliffs'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 85,
    tradeable: true,
    binding: 'none',
    icon: 'material_galeclaws_storm_feather',
    description: 'A wyvern\'s flight feather, blue-black and stiff as a blade, that still crackles faintly when the wind turns.',
    stats: {},
  },
  'brakkas_rime_tusk': {
    id: 'brakkas_rime_tusk',
    name: 'Brakka\'s Rime Tusk',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'frostspire_peaks'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 115,
    tradeable: true,
    binding: 'none',
    icon: 'material_brakkas_rime_tusk',
    description: 'A troll\'s tusk furred with frost that never melts, carved with the tally of every climber Brakka turned back.',
    stats: {},
  },
  'tobins_silvered_claw': {
    id: 'tobins_silvered_claw',
    name: 'Tobin\'s Silvered Claw',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'duskfall_vale'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 80,
    tradeable: true,
    binding: 'none',
    icon: 'material_tobins_silvered_claw',
    description: 'A claw gone silver at the root, the way the curse turns hair. Lampwardens keep them as proof that the miller is not coming home.',
    stats: {},
  },
  'censors_vigil_badge': {
    id: 'censors_vigil_badge',
    name: 'Censor\'s Vigil Badge',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'bloodmoon_reach'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 105,
    tradeable: true,
    binding: 'none',
    icon: 'material_censors_vigil_badge',
    description: 'A brass flame on a scorched ribbon, worn by the Vigil\'s censors. The pin is sharp enough to draw blood, which is said to be the point.',
    stats: {},
  },
  'mossclaws_heartwood_totem': {
    id: 'mossclaws_heartwood_totem',
    name: 'Mossclaw\'s Heartwood Totem',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'verdant_canopy'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 95,
    tradeable: true,
    binding: 'none',
    icon: 'material_mossclaws_heartwood_totem',
    description: 'A palm-sized bear carved from living heartwood, still sprouting moss. The Thornpaw give one to each cub that climbs the stair alone.',
    stats: {},
  },
  'chertjaws_seam_crystal': {
    id: 'chertjaws_seam_crystal',
    name: 'Chertjaw\'s Seam Crystal',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'sunless_deep'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 85,
    tradeable: true,
    binding: 'none',
    icon: 'material_chertjaws_seam_crystal',
    description: 'A violet crystal with tooth marks along one edge. Chertjaw saved the best of every seam; this one he was saving for later.',
    stats: {},
  },
  'ashkettles_smoldering_scale': {
    id: 'ashkettles_smoldering_scale',
    name: 'Ashkettle\'s Smoldering Scale',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'smoldering_wastes'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 95,
    tradeable: true,
    binding: 'none',
    icon: 'material_ashkettles_smoldering_scale',
    description: 'A drake scale that has not cooled since the day it fell. Wrapped in a damp cloth, it keeps a kettle warm all night.',
    stats: {},
  },
  'sorrels_copper_spur': {
    id: 'sorrels_copper_spur',
    name: 'Sorrel\'s Copper Spur',
    type: 'material' as ItemType,
    category: 'material',
    tags: ['mob_drop', 'trophy', 'scorched_dunes'],
    quantity: 1,
    maxStack: 20,
    rarity: 'rare' as ItemRarity,
    requiredLevel: 1,
    sellPrice: 130,
    tradeable: true,
    binding: 'none',
    icon: 'material_sorrels_copper_spur',
    description: 'A centaur\'s copper heel-spur, green with age. The scouts say Sorrel never needed it; he wore it to remind the young ones who led.',
    stats: {},
  },
};

export const ITEM_CATEGORIES = {
  consumables: Object.values(ITEM_DATABASE).filter(item => item.type === 'consumable'),
  equipment: Object.values(ITEM_DATABASE).filter(item => item.type === 'armor' || item.type === 'weapon'),
  materials: Object.values(ITEM_DATABASE).filter(item => item.type === 'material'),
};

export const EQUIPMENT_SETS: Record<string, string[]> = {
  beginner_warrior: ['training_sword', 'bronze_helm', 'iron_platemail'],
  beginner_mage: ['apprentice_wand', 'apprentice_hat', 'mage_robe'],
  beginner_ranger: ['training_bow', 'archer_tunic'],
};

export function getItem(itemId: string): ItemDefinition | null {
  return ITEM_DATABASE[itemId] || null;
}

export function getAllItems(): ItemDefinition[] {
  return Object.values(ITEM_DATABASE);
}

export function getItemsByType(type: ItemType): ItemDefinition[] {
  return Object.values(ITEM_DATABASE).filter(item => item.type === type);
}

export function getItemsByClass(className: string): ItemDefinition[] {
  return Object.values(ITEM_DATABASE).filter(item =>
    !item.requiredClass || item.requiredClass === className
  );
}

// MONSTER_DROPS moved to loot.gen.ts (generated from content/loot/drop_tables.toml)

export const RESTORATION_ITEMS: Record<string, { hp: number; mp: number }> = {
  red_potion: { hp: 50, mp: 0 },
  orange_potion: { hp: 150, mp: 0 },
  blue_potion: { hp: 0, mp: 50 },
  power_elixir: { hp: 9999, mp: 9999 },
};

export const EQUIPMENT_SLOTS = ['weapon', 'hat', 'top', 'bottom', 'shoes', 'gloves', 'cape', 'accessory', 'lantern', 'flame'];
